import type {
  Brand,
  BulkPriceResponse,
  Category,
  Item,
  Party,
  PortalStatement,
  PosCollection,
  PosCustomerInsight,
  PosCustomerLastPrice,
  PosSale,
  PosSaleWithDetails,
  PosSession,
  PosTenderKind,
  PosTerminal,
  PosZReport,
  PriceList,
} from '@erp/shared';
import Decimal from 'decimal.js';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  Banknote,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coins,
  CreditCard,
  Eraser,
  FolderTree,
  Gift,
  History,
  Inbox,
  Info,
  Keyboard,
  LogOut,
  Maximize,
  Minimize,
  Minus,
  Monitor,
  Package,
  PackagePlus,
  Pause,
  Percent,
  Plus,
  Printer,
  Receipt,
  ScanLine,
  Search,
  ShoppingCart,
  Star,
  Tag,
  StickyNote,
  Ticket,
  Check,
  Timer,
  Trash2,
  TrendingUp,
  Tv,
  User,
  UserPlus,
  Volume2,
  VolumeX,
  Wallet,
  Warehouse as WarehouseIcon,
  Wrench,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge,
  Button as ShButton,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FitImage, FormField, NumberInput } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { fetchAttachmentObjectUrl, isImageMime, listAttachments } from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { type CashbackConfig, getCashbackBalance, getCashbackConfig } from '../../lib/cashback.js';
import { cn } from '../../lib/cn.js';
import { type DisplaySnapshot, publishDisplay } from '../../lib/customerDisplay.js';
import { capRaw } from '../../lib/groupedNumber.js';
import { formatDateTime, formatNumber, formatTime } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney as formatMoneyLib, getCurrencyDecimals, trimMoney } from '../../lib/money.js';
import { normalizeAmountTyping, safeDecimal } from '../../lib/numerals.js';
import { listPriceLists, resolvePrice, resolvePricesBulk } from '../../lib/pricing.js';
import { isSoundMuted, playSound, setSoundMuted } from '../../lib/sounds.js';
import { loadViewPrefs, saveViewPrefs, viewPrefsKey } from '../../lib/viewPrefs.js';
import { WhatsAppButton } from '../common/WhatsAppButton.js';
import { ItemDetailDialog, ItemThumb } from '../items/ItemDetailDialog.js';
import { categoryDescendantIds, categorySubtreeCounts, registerCategoryIds } from '../items/itemHelpers.js';
import { itemGradient, useItemImage } from '../items/itemImage.js';
import { CustomerStatementCard } from '../parties/CustomerStatementCard.js';
import { PartyForm } from '../parties/PartiesPage.js';

import { CollectDebtDialog } from './CollectDebtDialog.js';
import { PaneDivider } from './PaneDivider.js';
import { PosSaleDetailBody, PosSaleDetailSkeleton, usePosSaleDetail } from './PosSaleDetail.js';
import { printSessionCloseout } from './shiftPrint.js';
import { useShiftMoney } from './ShiftView.js';
import { CART_STACKED_PX, CART_TIGHT_PX, useCartWidth } from './useCartWidth.js';

/**
 * Cashier register, redesigned to mirror real POS systems (Shopify / Square /
 * Toast). Layout:
 *   • Left pane: product tile grid + search/scan. Tapping a tile adds a line
 *     by item code, which the backend resolves via the same barcode lookup
 *     used by the scanner.
 *   • Right pane: live cart with qty steppers, running totals, and a single
 *     primary "Charge" CTA.
 *   • Charge opens a Dialog with method tabs, a numpad, quick-amount chips,
 *     and a tender list. When the running tender total covers the order,
 *     Finalize lights up and the receipt screen takes over the dialog.
 *
 * The API surface is unchanged — every action still posts to the same
 * /pos/sessions and /pos/sales endpoints the previous shell used.
 */

type OpenSessionResponse = PosSession;
interface SaleResponse {
  sale: PosSale;
  lines: PosSaleWithDetails['lines'];
  tenders: PosSaleWithDetails['tenders'];
}

/**
 * One product's group prices for the attached customer — the base unit plus each
 * packaging unit — already filtered to the SALE currency, so what the picker
 * shows is exactly what the cart will charge. `null` for a unit means the group
 * has no price for it.
 */
interface ItemGroupPricing {
  itemId: string;
  base: string | null;
  units: Record<string, string | null>;
  groupName: string | null;
}

/** A refused customer switch: the cart can't be re-priced into their currency. */
interface SwitchBlock {
  partyId: string;
  partyName: string;
  from: string;
  to: string;
  /** Line descriptions the target group has no price for (may be empty). */
  items: string[];
}

/**
 * RFC-7807 body as the API sends it: a stable top-level `code` plus the
 * thrower's machine-readable `cause` (NOT flattened — see errorHandler).
 */
interface ProblemBody {
  code?: string;
  detail?: string;
  cause?: {
    code?: string;
    expected?: string;
    groupCurrency?: string;
    currency?: string;
    itemId?: string;
    itemName?: string;
    itemCurrency?: string;
    currentQuantity?: string;
    requestedQuantity?: string;
    lines?: Array<{ lineNo: number; itemId: string }>;
    permission?: string;
    ref?: string;
    docNumber?: string;
    payments?: string[];
  };
}
const problemOf = (e: unknown): ProblemBody =>
  (e as { response?: { data?: ProblemBody } }).response?.data ?? {};

/**
 * A name as a person reads it. Some customers were imported with their text
 * HTML-escaped ("B&amp;B center"); React prints it literally, so the entity
 * shows. Decoded for display only — the stored name is left alone.
 */
function plainText(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'");
}

/** The cart's share of the register when nobody has dragged the seam. */
const CART_PCT_DEFAULT = 75;
/** A stored value is user input that survived a reload — treat it as untrusted.
 *  An undefined or corrupt one resolves to the default, never to `NaN fr`, which
 *  is an unparseable grid template and would collapse the whole register. */
function clampCartPct(p: unknown): number {
  const n = Number(p);
  if (!Number.isFinite(n)) return CART_PCT_DEFAULT;
  return Math.min(80, Math.max(20, Math.round(n * 10) / 10));
}

const TENDER_KINDS: PosTenderKind[] = ['cash', 'card', 'wallet', 'other'];

/**
 * May this person open a shift on the till? A till may name the cashiers allowed
 * on it (pos_terminal_users); none named = anyone with the permission — the
 * server's own rule (PosSessionsService.open), asked here so the register lands
 * on a till you can use and says so instead of failing at Open.
 */
function mayOpenTill(tt: PosTerminal, userId: string | null): boolean {
  return !tt.cashierUserIds?.length || (!!userId && tt.cashierUserIds.includes(userId));
}

/** A sale still waiting in a shift — what holds its close (named in the dialog). */
interface WaitingSale { saleNumber: string; partyName?: string | null; total: string; currencyCode: string }

export function PosRegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();

  /**
   * A server refusal in words a cashier can act on. Known cause codes get a
   * specific sentence — WHICH item, how many are left — and anything else
   * falls back to the problem's own detail, EXCEPT the server's raw i18n keys
   * ("errors.unprocessable.detail"): those read as machine code to a human,
   * so they translate through the errors catalog instead.
   */
  const friendlyError = useCallback((e: unknown): string => {
    const p = problemOf(e);
    const cause = p.cause;
    // The ledger names a sealed month by a bare string, not a coded object.
    if ((cause as unknown) === 'period_closed') {
      return t('sale.date.periodClosed', {
        defaultValue: 'That day falls in a closed accounting period, so a sale cannot be booked on it. Pick a later date, or ask for the period to be re-opened.',
      });
    }
    if (cause?.code === 'session_not_yours') {
      return t('shift.notYours', {
        defaultValue: 'This shift belongs to another cashier. Reload the page and open your own shift to sell.',
      });
    }
    if (cause?.code === 'shift_already_open') {
      return t('shift.alreadyOpen', { defaultValue: 'You already have a shift open on this register. Reload the page to continue it.' });
    }
    if (cause?.code === 'pos.sale_date_in_future') {
      return t('sale.date.future', { defaultValue: 'A sale cannot be dated in the future.' });
    }
    if (cause?.code === 'pos.sale_date_invalid') {
      return t('sale.date.invalid', { defaultValue: 'That is not a valid date.' });
    }
    if (cause?.code === 'permission.required' && cause.permission === 'pos.sale.date') {
      return t('sale.date.notAllowed', { defaultValue: 'Your account cannot change the date of a sale. Ask whoever manages your users for the "pos.sale.date" permission.' });
    }
    if (cause?.code === 'insufficient_stock') {
      // Whole numbers, not "3.0000" — the server sends DECIMAL(19,4) strings.
      const qty = (v?: string): string => {
        const n = Number(v);
        return Number.isFinite(n) ? String(n) : (v ?? '?');
      };
      return t('sale.insufficientStock', {
        item: cause.itemName ?? cause.itemId ?? '',
        have: qty(cause.currentQuantity),
        need: qty(cause.requestedQuantity),
        defaultValue: 'Not enough stock for "{{item}}" — {{have}} left, but this sale needs {{need}}. Lower the quantity or restock first (or allow negative stock in Settings → Inventory).',
      });
    }
    // The two ways a return can be refused. Both say what to do instead —
    // "unprocessable" at a counter with a customer waiting is not a message.
    if (cause?.code === 'pos.return_exceeds_sale') {
      return t('sale.returnExceedsSale', {
        defaultValue: 'This sale gives back more than it sells. Remove some returned items, or use Sales → Returns to refund the whole thing.',
      });
    }
    if (cause?.code === 'pos.refund_not_allowed') {
      return t('sale.refundNotAllowed', {
        defaultValue: 'Your account cannot take goods back. Ask whoever manages your users for the "pos.refund" permission.',
      });
    }
    // A register with no warehouse, rung up by someone who works in several
    // branches: the server will not guess which shelf the goods came off.
    if (cause?.code === 'terminal_warehouse_required') {
      return t('sale.terminalWarehouseRequired', {
        defaultValue: 'This register has no warehouse, and your account works in several branches — so the sale cannot tell which shelf the goods came off. Ask a manager to set the warehouse in POS → Terminals.',
      });
    }
    // Every line is price-only, so there is no sale to ring up — say what the
    // ticket IS (a price list) and where the shop keeps that.
    // Money in another currency the server will not book: too little, or far too much.
    if (cause?.code === 'cash_exchange_short') {
      return t('tender.exchangeShort', { defaultValue: 'Those notes don’t cover the sale at today’s rate — check the amount and the currency.' });
    }
    if (cause?.code === 'cash_exchange_over') {
      return t('tender.chargeOver', { defaultValue: 'That is far more than the sale at today’s rate — check the amount.' });
    }
    if (cause?.code === 'pos.nothing_to_charge') {
      return t('sale.nothingToCharge', {
        defaultValue: 'Nothing to charge — every line is set to 0. Give one a quantity, or send the customer a quotation instead.',
      });
    }
    if (p.detail && !p.detail.startsWith('errors.')) return p.detail;
    if (p.code) return tc(`errors:${p.code}`, { defaultValue: extractError(e) });
    return extractError(e);
  }, [t, tc]);
  const cashierName = useAuthStore((s) => s.user?.fullName ?? s.user?.email ?? '');
  const companyName = useAuthStore((s) => s.tenant?.name ?? '');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode ?? 'USD');
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  // Where the seam between the product grid and the cart sits, as the cart's
  // share of the row. Per user, not per browser: a shop's counter machine is
  // signed into by whoever is covering it, and inheriting someone else's layout
  // is the same annoyance as inheriting their filters.
  const splitRef = useRef<HTMLDivElement>(null);
  const splitKey = viewPrefsKey('pos-split', tenantId ?? null, userId);
  const [cartPct, setCartPctRaw] = useState<number>(
    () => clampCartPct(loadViewPrefs<{ cartPct?: number }>(splitKey, {}).cartPct),
  );
  const setCartPct = useCallback((pct: number) => {
    const next = clampCartPct(pct);
    setCartPctRaw(next);
    saveViewPrefs(splitKey, { cartPct: next });
  }, [splitKey]);

  // What the cart pane is ACTUALLY wide, so its rows react to the seam rather
  // than to the window. Viewport breakpoints stopped describing these panes the
  // moment their split became a thing the cashier drags.
  const cartW = useCartWidth(splitRef, cartPct);
  const cartTight = cartW !== null && cartW < CART_TIGHT_PX;
  const cartStacked = cartW !== null && cartW < CART_STACKED_PX;

  // Customer-facing second screen (toggleable module): when on, the register
  // mirrors the live cart to a display window and shows the "open display" button.
  const enabledModules = useEnabledModules(tenantId);
  const displayEnabled = moduleEnabled(enabledModules, 'customer_display');
  // Customer-group (price-list) pricing: when on, a line added for a customer
  // whose group quotes prices in the sale currency is priced from that list.
  const pricingOn = moduleEnabled(enabledModules, 'pricing');
  // A product can be filed under several categories. The API always sends the
  // extras; whether they COUNT is the module's call, exactly as on the item list
  // and the storefront.
  const multiCatOn = moduleEnabled(enabledModules, 'multi_category');
  // Tiles show each item's photo (first 'item' attachment); gated to users who
  // can read attachments (others just see the type-icon fallback — no 403s).
  const canFiles = usePermission('attachments.read');
  // Drawer-count "double check" on close: pre-fills counted cash with the
  // system's expected cash + shows a live variance. Gated to users who can read
  // the Z-report (cashiers without it just get a plain, editable cash field).
  // Taking goods back is money out of the drawer, so it is its own right — the
  // default cashier does not have it, and without it the quantity box behaves
  // exactly as it always did.
  const canRefund = usePermission('pos.refund');
  // The keeper's two rights: back-date a sale, and re-open a finished one to correct it.
  const canDate = usePermission('pos.sale.date');
  const canEditSale = usePermission('pos.sale.edit');
  const canAddProduct = usePermission('items.write');
  const [soundMuted, setSoundMutedState] = useState(() => isSoundMuted());
  // Live wall-clock for the shift bar (kiosk feel).
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000 * 30);
    return () => window.clearInterval(id);
  }, []);
  const clock = formatTime(now, { locale: i18n.language });
  const [terminals, setTerminals] = useState<PosTerminal[] | null>(null);
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>('');
  // Which warehouse this till sells from: the terminal's own, else the tenant
  // default. Shown in the header so the cashier can SEE it — the till itself
  // A till pinned to several warehouses lets the cashier choose per sale; one
  // pinned to a single warehouse shows the name and nothing to press.
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouseNames, setWarehouseNames] = useState<Map<string, string>>(new Map());
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string | null>(null);
  /** The cashier's choice for THIS shift; null = the till's own warehouse. */
  const [pickedWarehouseId, setPickedWarehouseId] = useState<string | null>(null);
  const [warehouseMenuOpen, setWarehouseMenuOpen] = useState(false);
  useEffect(() => {
    void api()
      .get<{ items: Array<{ id: string; name: string }>; defaultWarehouseId?: string | null }>('/warehouses')
      .then((r) => {
        setWarehouses(r.data.items);
        setWarehouseNames(new Map(r.data.items.map((w) => [w.id, w.name])));
        setDefaultWarehouseId(r.data.defaultWarehouseId ?? null);
      })
      .catch(() => { /* non-fatal — the chip simply doesn't render */ });
  }, []);
  const [session, setSession] = useState<PosSession | null>(null);
  const [openingCash, setOpeningCash] = useState('0');
  const [sale, setSale] = useState<SaleResponse | null>(null);
  // The day chosen before there is a cart to carry it (the cart is created by the
  // first item). '' = the default, the day the sale is finalized.
  const [pendingDay, setPendingDay] = useState('');
  const [items, setItems] = useState<Item[] | null>(null);
  const [itemQuery, setItemQuery] = useState('');
  const [scanText, setScanText] = useState('');
  const [couponCode, setCouponCode] = useState('');
  // Fixed amount by default — the shop discounts in money far more than in %.
  const [discountKind, setDiscountKind] = useState<'percent' | 'fixed'>('fixed');
  const [discountValue, setDiscountValue] = useState('');
  // The sale's note, as typed; saved on blur/Enter and before finalize.
  const [noteDraft, setNoteDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenderOpen, setTenderOpen] = useState(false);

  /**
   * The sale on the REAL paper — the shared A4/A5/80mm print page — before any
   * money moves. window.open keeps the opener, so the new tab inherits the
   * sessionStorage auth exactly like the post-finalize invoice button does.
   */
  const printBeforePay = (): void => {
    if (!sale) return;
    const w = window.open(appUrl(`/pos/sales/${sale.sale.id}/print`), '_blank');
    if (!w) setError(t('sale.popupBlocked', { defaultValue: 'Allow pop-ups to preview the invoice.' }));
  };
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Free-text line form (kept for the "custom item" escape hatch)
  const [customDesc, setCustomDesc] = useState('');
  const [customQty, setCustomQty] = useState('1');
  const [customPrice, setCustomPrice] = useState('');

  // Close-shift form. The drawer is counted PER CURRENCY: a shift can take money
  // in the till's own currency AND in a customer group's (a USD group on an IQD
  // register), those notes sit in separate compartments, and adding them together
  // would report a drawer that never existed. Keyed by currency code.
  const [showClose, setShowClose] = useState(false);
  /** The "customer pays their debt" dialog. */
  const [collectOpen, setCollectOpen] = useState(false);
  const [countedCash, setCountedCash] = useState<Record<string, string>>({});
  const [closeNote, setCloseNote] = useState('');
  // Live Z-report fetched when the close panel opens (expected cash + tenders).
  const [zreport, setZreport] = useState<PosZReport | null>(null);
  /** The customers' debt payments in the shift being closed — listed in the close window. */
  const [closeCollections, setCloseCollections] = useState<PosCollection[]>([]);
  /** Print the Z-report (close-out) once the count is in — kept per browser, on by default. */
  const [printZ, setPrintZ] = useState(() => {
    try { return localStorage.getItem('pos.printZ') !== '0'; } catch { return true; }
  });
  const zMoney = useShiftMoney();
  const [zLoading, setZLoading] = useState(false);
  // The shift's figures could not be loaded — the close is held, see the dialog.
  const [zFailed, setZFailed] = useState(false);
  // Shifts are PER CASHIER: a till can hold several open at once, one each. The
  // other cashiers' open shifts on the selected till (shown before you open your
  // own), and the one of theirs being closed from here — null while closing yours.
  const [otherShifts, setOtherShifts] = useState<PosSession[]>([]);
  const [closeFor, setCloseFor] = useState<PosSession | null>(null);
  const canCloseShift = usePermission('pos.shift.close');

  // Customers (for the order's customer picker) + category filter
  const [parties, setParties] = useState<Party[] | null>(null);
  const [custOpen, setCustOpen] = useState(false);
  const [custQuery, setCustQuery] = useState('');
  // One tap to a group: 'all' | a price-list id | '__none__' (ungrouped).
  const [custGroup, setCustGroup] = useState<string>('all');
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  // Attached customer's account (balance/debt + sales + payments).
  const [custStatement, setCustStatement] = useState<PortalStatement | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  // Price memory + recent sales for the attached customer (null = none/walk-in).
  const [insight, setInsight] = useState<PosCustomerInsight | null>(null);
  const [stmtRefresh, setStmtRefresh] = useState(0);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  // Browse model: 3 tabs (category / brand / featured). Category + brand show
  // image cards that drill into a paginated item list (with a back button).
  const [browseTab, setBrowseTab] = useState<'category' | 'brand' | 'featured'>('category');
  const [drillId, setDrillId] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Unit picker (shown when tapping an item that has packaging units)
  const [unitPickItem, setUnitPickItem] = useState<Item | null>(null);
  // Set when the picker is switching an existing cart line's unit (vs adding new).
  const [unitPickLine, setUnitPickLine] = useState<{ lineNo: number; quantity: string } | null>(null);
  // Product opened in the reusable detail modal (tap the ⓘ on a tile).
  const [detailItem, setDetailItem] = useState<Item | null>(null);

  // New-product dialog
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [npCode, setNpCode] = useState('');
  const [npName, setNpName] = useState('');
  const [npPrice, setNpPrice] = useState('');
  const [npType, setNpType] = useState<'product' | 'service'>('product');

  // This-shift sales (shown in a modal beside Close shift). Clicking a row
  // drills into that one sale (line items + payment breakdown) in the same
  // dialog — `viewSaleId` set = detail view, null = back to the list.
  const [salesOpen, setSalesOpen] = useState(false);
  const [sessionSales, setSessionSales] = useState<PosSale[] | null>(null);
  const [salesStatusFilter, setSalesStatusFilter] = useState('all');
  const [viewSaleId, setViewSaleId] = useState<string | null>(null);
  const { detail: viewSale, loading: viewSaleLoading } = usePosSaleDetail(viewSaleId);
  // Running shift tally shown live in the shift bar. A shift can hold sales in
  // more than one currency (a customer group prices in its own), and money in
  // different currencies is NEVER summed — hence a total PER currency.
  const [shiftTotals, setShiftTotals] = useState<
    { count: number; totals: Array<{ currencyCode: string; total: number }> } | null
  >(null);

  // Parked ("waiting") sales — hold the current cart and switch between several.
  const [parkedSales, setParkedSales] = useState<PosSale[]>([]);
  /** The closing shift's waiting sales: null = still checking; any = the close is held. */
  const [closeWaiting, setCloseWaiting] = useState<WaitingSale[] | null>(null);
  const [parkedOpen, setParkedOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdLabelInput, setHoldLabelInput] = useState('');
  const [clearOpen, setClearOpen] = useState(false);

  /**
   * Full screen for the till.
   *
   * The state is driven by the `fullscreenchange` EVENT rather than by the
   * click, because Esc and F11 leave full screen without going through this
   * button — tracking our own boolean would leave the icon lying about which
   * state the screen is in. `document.fullscreenElement` is the only truth.
   */
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const sync = (): void => setIsFullscreen(!!document.fullscreenElement);
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const toggleFullscreen = async (): Promise<void> => {
    try {
      // Rejects when the browser refuses (no user gesture, an iframe without
      // allowfullscreen, a kiosk policy). Swallow it: a till that throws a red
      // toast because a screen would not resize is worse than one that does not.
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { /* not available here — leave the screen as it is */ }
  };

  // Refs + UI bits for the cashier keyboard shortcuts.
  const searchRef = useRef<HTMLInputElement>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Briefly highlights the most-recently added cart line so it's easy to spot.
  const [flashLineNo, setFlashLineNo] = useState<number | null>(null);

  // ── the sale's currency follows the customer's group ────────────────────────
  // A customer group (price list) declares the currency ITS prices are quoted in,
  // and that currency — not the terminal's — is what such a customer is shown and
  // charged. Attaching one re-denominates the sale (the server then re-prices
  // every line from that group's list); a customer with no group sells in the
  // session currency. Nothing is EVER converted client-side.

  /** The currency this party's group quotes prices in; null ⇒ no group currency. */
  const groupCurrencyOf = (party: Party | null | undefined): string | null => {
    if (!pricingOn || !party?.priceListId || party.code === 'WALK-IN') return null;
    const pl = priceLists.find((x) => x.id === party.priceListId);
    // An inactive group carries no pricing basis (the server agrees) → session currency.
    if (pl) return pl.isActive ? pl.currencyCode : null;
    // Groups not loaded (or a brand-new customer) — trust the party's own field.
    return party.priceListCurrencyCode ?? null;
  };

  /** The currency a sale for this customer must run in. */
  const saleCurrencyFor = (party: Party | null | undefined): string =>
    groupCurrencyOf(party) ?? session?.currencyCode ?? baseCurrency;

  /** The live sale's currency (every amount on this screen is denominated in it). */
  const saleCurrency = sale?.sale.currencyCode ?? session?.currencyCode ?? baseCurrency;
  /** The customer on the cart (undefined ⇒ none, or customers not loaded yet). */
  const attachedParty = sale ? parties?.find((p) => p.id === sale.sale.partyId) : undefined;

  /**
   * The price this customer last paid for exactly this (item, unit) in the
   * sale's currency — the wholesale habit: a repeat customer's price is what
   * was agreed last time. Currency must match or the number is meaningless.
   */
  const lastPriceOf = (itemId: string | null | undefined, itemUnitId?: string | null): PosCustomerLastPrice | undefined => {
    if (!itemId || !insight) return undefined;
    return insight.lastPrices.find(
      (p) => p.itemId === itemId && (p.itemUnitId ?? null) === (itemUnitId ?? null) && p.currencyCode === saleCurrency,
    );
  };
  /** Their group's currency — what this sale should run in; null ⇒ session's. */
  const attachedGroupCurrency = groupCurrencyOf(attachedParty);

  // The attached customer's group prices for the products on screen, so the grid
  // shows what THIS customer pays. itemId → price in the sale currency, or null
  // when their group has no price for it. Empty ⇒ plain catalogue pricing.
  const [tilePrices, setTilePrices] = useState<Map<string, string | null>>(new Map());
  // Same, for the ONE product open in the unit picker / details modal (its base
  // unit plus every packaging unit).
  const [itemPrices, setItemPrices] = useState<ItemGroupPricing | null>(null);
  // Set when attaching a customer would re-denominate the cart but some line has
  // no price in their group's currency — the cashier decides what to do.
  const [switchBlocked, setSwitchBlocked] = useState<SwitchBlock | null>(null);

  // ---- bootstrap: load terminals, land on the till where YOUR shift is open ----
  // Shifts are per cashier. Picking simply the first till put every cashier onto
  // whatever shift happened to be open there — Duhok's cashiers all sold on the
  // owner's. So: the till of your own open shift, else the first till you may
  // open, else the first. The effect below then resumes the shift and its carts.
  useEffect(() => {
    (async () => {
      try {
        const r = await api().get<{ items: PosTerminal[] }>('/pos/terminals?activeOnly=true');
        setTerminals(r.data.items);
        if (r.data.items.length > 0) {
          const mine = userId
            ? await api()
              .get<{ items: PosSession[] }>('/pos/sessions', { params: { status: 'open', cashierUserId: userId, limit: 10 } })
              .then((x) => x.data.items)
              .catch(() => [] as PosSession[])
            : [];
          const ownTill = mine.find((x) => r.data.items.some((tt) => tt.id === x.terminalId))?.terminalId;
          const allowedTill = r.data.items.find((tt) => mayOpenTill(tt, userId))?.id;
          setSelectedTerminalId(ownTill ?? allowedTill ?? r.data.items[0]!.id);
        }
      } catch (e) {
        setError(friendlyError(e));
      }
    })();
  }, [friendlyError, userId]);

  // Whenever the selected terminal changes, look up its open session.
  useEffect(() => {
    if (!selectedTerminalId) return;
    (async () => {
      try {
        const cur = await api().get<PosSession | null>('/pos/sessions/current', {
          params: { terminalId: selectedTerminalId },
        });
        setSession(cur.data);
        // Resume the session's open (parked) sale so the cart lines + the
        // attached customer survive a page reload.
        if (cur.data) {
          // Load all parked sales up front so the "Waiting (N)" switcher is
          // populated immediately on reload, then resume the most recent one.
          // Only THIS person's (`mine`): the session belongs to the register, and
          // resuming its newest cart whoever started it put two cashiers on one
          // register into the same cart — each one's scans landing in the other's.
          // Two asks: the newest cart to resume INCLUDING an empty one (so a
          // reload lands where it was, never on an older held customer's cart),
          // and the waiting list WITHOUT empties (they are nothing to wait for).
          const [newest, parkedList] = await Promise.all([
            api().get<{ items: PosSale[] }>('/pos/sales', {
              params: { sessionId: cur.data.id, status: 'parked', mine: 1, limit: 1, includeEmpty: 1 },
            }),
            api().get<{ items: PosSale[] }>('/pos/sales', {
              params: { sessionId: cur.data.id, status: 'parked', mine: 1, limit: 50 },
            }),
          ]);
          setParkedSales(parkedList.data.items);
          const parked = newest.data.items[0];
          if (parked) {
            const full = await api().get<SaleResponse>(`/pos/sales/${parked.id}`);
            setSale(full.data);
          } else {
            setSale(null);
          }
        } else {
          setSale(null);
          setParkedSales([]);
        }
      } catch (e) {
        setError(friendlyError(e));
      }
    })();
  }, [selectedTerminalId, friendlyError]);

  // Once a shift is open, fetch sellable items once for the tile grid. We
  // pull a large page (the server caps at 500) instead of paginating; a
  // register screen with thousands of SKUs is better served by the search
  // box than by infinite scroll.
  useEffect(() => {
    if (!session) return;
    if (items !== null) return;
    (async () => {
      try {
        // The API caps one request at 500 rows — page through so the register
        // carries the WHOLE catalogue (the grid becomes usable after the first
        // page; the rest stream in).
        const PAGE = 500;
        const all: Item[] = [];
        for (let offset = 0; offset < 20_000; offset += PAGE) {
          // `stockPolicy=pos` = "apply the shop's hide-zero-stock policy to me".
          // Whether it actually filters is decided server-side from Settings →
          // Inventory (a cashier can't read settings, so the server owns it).
          // `stock: 1` attaches each item's on-hand (summed across warehouses)
          // so the unit picker can say how many are left without a second call.
          const r = await api().get<{ items: Item[]; total: number }>('/items', { params: { limit: PAGE, offset, stockPolicy: 'pos', stock: 1 } });
          all.push(...r.data.items);
          setItems(all.filter((i) => i.isActive && i.isSellable));
          if (r.data.items.length < PAGE || all.length >= (r.data.total ?? all.length)) break;
        }
      } catch (e) {
        setError(friendlyError(e));
      }
    })();
  }, [session, items, friendlyError]);

  // Brands power the register filters.
  useEffect(() => {
    if (!session) return;
    void api().get<{ items: Brand[] }>('/brands')
      .then((r) => setBrands(r.data.items.filter((b) => b.isActive)))
      .catch(() => undefined);
    // Customer groups (price lists) power the customer-picker grouping + currency.
    void listPriceLists().then(setPriceLists).catch(() => setPriceLists([]));
  }, [session]);

  // Categories power the browse cards — asked for in the cashier's language, so
  // an Arabic register doesn't label its departments in English.
  const dataLocale = i18n.language.split('-')[0] ?? 'en';
  useEffect(() => {
    if (!session) return;
    void api().get<{ items: Category[] }>('/categories', { params: { locale: dataLocale } })
      .then((r) => setCats(r.data.items))
      .catch(() => undefined);
  }, [session, dataLocale]);

  // Customers for the order picker (customers + "both"; vendors excluded).
  useEffect(() => {
    if (!session || parties !== null) return;
    (async () => {
      try {
        // Paged for the same reason as the catalogue — this tenant is already
        // at ~495 customers, one page away from silent truncation.
        const PAGE = 500;
        const all: Party[] = [];
        for (let offset = 0; offset < 20_000; offset += PAGE) {
          const r = await api().get<{ items: Party[]; total: number }>('/parties', { params: { limit: PAGE, offset } });
          all.push(...r.data.items);
          if (r.data.items.length < PAGE || all.length >= (r.data.total ?? all.length)) break;
        }
        setParties(all.filter((p) => p.partyType !== 'vendor'));
      } catch {
        setParties([]);
      }
    })();
  }, [session, parties]);

  // When a real (non-walk-in) customer is on the sale, load their account
  // statement so we can show the current balance/debt + sales + payments.
  const attachedPartyId = sale?.sale.partyId ?? null;
  useEffect(() => {
    const p = attachedPartyId ? parties?.find((x) => x.id === attachedPartyId) : undefined;
    if (!p || p.code === 'WALK-IN') { setCustStatement(null); return undefined; }
    let active = true;
    void api()
      .get<PortalStatement>(`/parties/${attachedPartyId}/statement`)
      .then((r) => { if (active) setCustStatement(r.data); })
      .catch(() => { if (active) setCustStatement(null); });
    return () => { active = false; };
  }, [attachedPartyId, parties, stmtRefresh]);

  // The customer's price memory + recent sales — one fetch per attach. Powers
  // the red "sold before" lines in the cart AND the account dialog's history.
  useEffect(() => {
    setInsight(null);
    const p = attachedPartyId ? parties?.find((x) => x.id === attachedPartyId) : undefined;
    if (!p || p.code === 'WALK-IN') return undefined;
    let active = true;
    void api()
      .get<PosCustomerInsight>(`/pos/customers/${attachedPartyId}/insight`)
      .then((r) => { if (active) setInsight(r.data); })
      .catch(() => { if (active) setInsight(null); });
    return () => { active = false; };
  }, [attachedPartyId, parties]);

  // ---- cashier keyboard shortcuts: F2 search · F3 scan · F9 charge · ? help ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (
        el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' ||
        el.tagName === 'SELECT' || el.isContentEditable
      );
      if (e.key === '?' && !typing) { e.preventDefault(); setShortcutsOpen((s) => !s); return; }
      if (e.key === 'F2' || (e.key === '/' && !typing)) {
        e.preventDefault(); searchRef.current?.focus(); searchRef.current?.select(); return;
      }
      if (e.key === 'F3') { e.preventDefault(); scanRef.current?.focus(); scanRef.current?.select(); return; }
      if (e.key === 'F9') {
        e.preventDefault();
        if (sale && sale.sale.status === 'parked' && sale.lines.length > 0) setTenderOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sale]);

  // ---- mirror the live cart to the customer display (second screen) ----
  useEffect(() => {
    if (!displayEnabled) return;
    const s = sale;
    const party = s ? parties?.find((p) => p.id === s.sale.partyId) : undefined;
    const realCustomer = party && party.code !== 'WALK-IN' ? party.displayName : null;
    const snapshot: DisplaySnapshot = {
      state: !s || s.lines.length === 0 ? 'idle' : s.sale.status === 'finalized' ? 'done' : 'sale',
      company: companyName,
      currency: s?.sale.currencyCode ?? baseCurrency,
      locale: i18n.language,
      customerName: realCustomer,
      lines: s
        ? s.lines.map((l) => ({
            id: String(l.id),
            name: l.description ?? '—',
            unitName: l.unitName,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            lineTotal: l.lineTotal,
            itemId: l.itemId,
          }))
        : [],
      subtotal: s?.sale.subtotal ?? '0',
      taxTotal: s?.sale.taxTotal ?? '0',
      discountTotal: s?.sale.discountTotal ?? '0',
      total: s?.sale.total ?? '0',
      changeDue: s?.sale.changeDue ?? undefined,
      saleNumber: s?.sale.saleNumber ?? null,
      at: Date.now(),
    };
    publishDisplay(snapshot);
  }, [sale, parties, displayEnabled, companyName, baseCurrency, i18n.language]);

  // ---- browse model: cards (category/brand) → drilled, paginated items ----
  const PAGE_SIZE = 12;
  const catNameById = useMemo(() => new Map(cats.map((c) => [c.id, c.name] as const)), [cats]);
  // Item lookup powers per-line unit switching (find an item's packaging units).
  const itemById = useMemo(() => new Map((items ?? []).map((i) => [i.id, i] as const)), [items]);
  const brandNameById = useMemo(() => new Map(brands.map((b) => [b.id, b.title] as const)), [brands]);

  // Cards: only categories / brands that actually have sellable items (+ counts).
  // A category card counts (and drills into) its WHOLE subtree — tallying the
  // exact category id gave a department with only subcategories no card at all,
  // so a cashier browsing from the top found nothing.
  const categoryCards = useMemo(() => {
    if (!items) return [] as Array<{ id: string; name: string; count: number }>;
    const own = new Map<string, number>();
    for (const it of items) {
      // Every shelf the product is on, so a cashier drilling into a category
      // finds what the office filed there — not only the products whose PRIMARY
      // category it is.
      for (const id of registerCategoryIds(it, multiCatOn)) own.set(id, (own.get(id) ?? 0) + 1);
    }
    const rollup = categorySubtreeCounts(cats, own);
    // /categories arrives in the shop's own running order (categories.sort) —
    // the fetched position IS the order, same rule as the brand cards below.
    return cats
      .map((c) => ({ id: c.id, name: c.name, count: rollup.get(c.id) ?? 0 }))
      .filter((c) => c.count > 0);
  }, [items, cats, multiCatOn]);
  // Brand cards follow the shop's own running order (`brands.sort`, set on the
  // brands page), not the alphabet. /brands already returns them in it, so the
  // fetched position IS the order — re-sorting by name here was throwing it
  // away, which is why the register disagreed with every other screen.
  const brandOrder = useMemo(() => new Map(brands.map((b, i) => [b.id, i] as const)), [brands]);
  const brandCards = useMemo(() => {
    if (!items) return [] as Array<{ id: string; name: string; count: number }>;
    const n = new Map<string, number>();
    for (const it of items) {
      if (it.brandId && brandNameById.has(it.brandId)) n.set(it.brandId, (n.get(it.brandId) ?? 0) + 1);
    }
    return [...n.entries()]
      .map(([id, count]) => ({ id, name: brandNameById.get(id)!, count }))
      .sort((a, b) => (brandOrder.get(a.id) ?? 0) - (brandOrder.get(b.id) ?? 0) || a.name.localeCompare(b.name));
  }, [items, brandNameById, brandOrder]);

  // When searching / on Featured / drilled into a card we show a flat (paginated)
  // item list; otherwise (`null`) we show the category|brand cards.
  const shownItems = useMemo(() => {
    if (!items) return null;
    const q = itemQuery.trim().toLowerCase();
    if (q) return items.filter((i) => i.code.toLowerCase().includes(q) || i.name.toLowerCase().includes(q));
    if (browseTab === 'featured') return items.filter((i) => i.isFeatured);
    if (drillId && browseTab === 'category') {
      const sub = categoryDescendantIds(cats, drillId);
      return items.filter((i) => registerCategoryIds(i, multiCatOn).some((id) => sub.has(id)));
    }
    if (drillId && browseTab === 'brand') return items.filter((i) => i.brandId === drillId);
    return null;
  }, [items, itemQuery, browseTab, drillId, cats, multiCatOn]);

  const pageCount = shownItems ? Math.max(1, Math.ceil(shownItems.length / PAGE_SIZE)) : 1;
  const curPage = Math.min(page, pageCount - 1);
  const pagedItems = shownItems ? shownItems.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE) : null;
  const drillTitle = drillId
    ? (browseTab === 'brand' ? brandNameById.get(drillId) : catNameById.get(drillId)) ?? ''
    : '';

  // Reset to the first page whenever the view changes.
  useEffect(() => { setPage(0); }, [browseTab, drillId, itemQuery]);

  // ---- group prices for the products on screen (one batched call) ----
  // Tiles must show what the ATTACHED customer pays, in the sale currency — not
  // the retail catalogue price. Only the visible page is priced (12 rows), and
  // the server decides everything: we never convert or compute a price here.
  const visibleKey = (pagedItems ?? []).map((i) => i.id).join(',');
  useEffect(() => {
    const ids = visibleKey ? visibleKey.split(',') : [];
    const party = attachedPartyId ? parties?.find((p) => p.id === attachedPartyId) : undefined;
    if (!pricingOn || ids.length === 0 || !party?.priceListId || party.code === 'WALK-IN') {
      setTilePrices(new Map());
      return undefined;
    }
    let active = true;
    void resolvePricesBulk({
      partyId: party.id,
      currency: saleCurrency,
      lines: ids.map((id) => ({ itemId: id })),
    })
      .then((r) => {
        if (!active) return;
        const m = new Map<string, string | null>();
        for (const l of r.items) m.set(l.itemId, groupPriceIn(l, saleCurrency));
        setTilePrices(m);
      })
      .catch(() => { if (active) setTilePrices(new Map()); });
    return () => { active = false; };
  }, [visibleKey, attachedPartyId, parties, saleCurrency, pricingOn]);

  // ---- group prices for ONE product (base + every packaging unit) ----
  // Loaded when the unit picker or the details modal opens, so both show the
  // customer's price rather than the catalogue one.
  const loadItemPrices = useCallback(async (item: Item): Promise<void> => {
    setItemPrices(null);
    const party = attachedPartyId ? parties?.find((p) => p.id === attachedPartyId) : undefined;
    if (!pricingOn || !party?.priceListId || party.code === 'WALK-IN') return;
    try {
      const r = await resolvePricesBulk({
        partyId: party.id,
        currency: saleCurrency,
        lines: [
          { itemId: item.id },
          ...item.units.map((u) => ({ itemId: item.id, itemUnitId: u.id })),
        ],
      });
      const units: Record<string, string | null> = {};
      let base: string | null = null;
      for (const l of r.items) {
        if (l.itemUnitId) units[l.itemUnitId] = groupPriceIn(l, saleCurrency);
        else base = groupPriceIn(l, saleCurrency);
      }
      setItemPrices({ itemId: item.id, base, units, groupName: r.basis.priceListName });
    } catch {
      /* non-fatal — the catalogue price stands */
    }
  }, [attachedPartyId, parties, saleCurrency, pricingOn]);

  const openShift = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api().post<OpenSessionResponse>('/pos/sessions', {
        terminalId: selectedTerminalId,
        openingCash,
      });
      setSession(r.data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Open the close panel and fetch the live Z-report, so the counted-cash field
  // defaults to the system's expected cash (still editable) and the cashier can
  // see what the drawer SHOULD hold beside what they counted.
  //
  // No longer gated on `pos.zreport`. A cashier closing their own shift was
  // shown a bare "counted cash" box with nothing to check it against — the
  // expected total, the opening + sales − returns breakdown and the variance
  // all come from this one call, and the permission every cashier lacks made
  // the close-out say nothing back to them. The server decides now: your own
  // shift is always readable, somebody else's still needs the permission.
  const openCloseShift = async (target?: PosSession): Promise<void> => {
    const closing = target ?? session;
    setCloseFor(target ?? null);
    setShowClose(true);
    setZreport(null);
    setZFailed(false);
    setCloseWaiting(null);
    if (!closing) return;
    // The shift's waiting sales: it does not close over one (the server refuses
    // too). The list leaves the register's empty carts out on its own.
    void api()
      .get<{ items: PosSale[] }>('/pos/sales', { params: { sessionId: closing.id, status: 'parked', limit: 50 } })
      .then((r) => setCloseWaiting(r.data.items.map((s) => ({
        saleNumber: s.saleNumber, partyName: s.partyName ?? null, total: s.total, currencyCode: s.currencyCode,
      }))))
      // Unknown is not "none": the server still refuses, and says which.
      .catch(() => setCloseWaiting([]));
    setCountedCash({ [closing.currencyCode]: '0' });
    setZLoading(true);
    setCloseCollections([]);
    void api()
      .get<{ items: PosCollection[] }>(`/pos/sessions/${closing.id}/collections`)
      .then((r) => setCloseCollections(r.data.items))
      .catch(() => setCloseCollections([]));
    try {
      const r = await api().get<PosZReport>(`/pos/sessions/${closing.id}/z-report`);
      setZreport(r.data);
      // Pre-fill every currency's count with what the system expects (still
      // editable). An older API without `byCurrency` yields the single flat row.
      const rows = r.data.byCurrency?.length
        ? r.data.byCurrency.map((c) => [c.currencyCode, c.expectedCash] as const)
        : [[closing.currencyCode, r.data.expectedCash] as const];
      setCountedCash(Object.fromEntries(rows));
    } catch {
      // Held, not guessed: without the figures the dialog could only offer the
      // shift currency's box, and every OTHER currency the shift took would close
      // as 0 counted — a whole day's dollars recorded as short.
      setZFailed(true);
    } finally {
      setZLoading(false);
    }
  };

  const cancelClose = (): void => {
    setShowClose(false);
    setZreport(null);
    setZFailed(false);
    setCloseFor(null);
    setCloseWaiting(null);
  };

  const closeShift = async () => {
    const closing = closeFor ?? session;
    if (!closing) return;
    setBusy(true); setError(null);
    try {
      const entries = Object.entries(countedCash);
      await api().post<PosSession>(`/pos/sessions/${closing.id}/close`, {
        // The session currency stays in `closingCash` (what every other client
        // sends); the rest ride along per currency and are never summed into it.
        closingCash: (countedCash[closing.currencyCode] ?? '').trim() || '0',
        ...(entries.length > 1
          ? {
              countedByCurrency: entries.map(([currencyCode, amount]) => ({
                currencyCode,
                amount: amount.trim() || '0',
              })),
            }
          : {}),
        note: closeNote || undefined,
      });
      // The Z-report on paper: sales by how they were paid, the customers' debt
      // payments, each drawer and its count. Best-effort — a printer problem
      // never undoes a close.
      if (printZ) {
        void printSessionCloseout(closing, {
          t,
          money: zMoney,
          storeName: useAuthStore.getState().tenant?.name ?? '',
          locale: i18n.language,
          rtl: i18n.dir() === 'rtl',
          cashier: closing.cashierName ?? (closeFor ? null : cashierName),
        }).catch(() => undefined);
      }
      if (closeFor) {
        // Somebody else's: yours (if any) carries on; the list loses that row.
        setCloseFor(null);
        setShowClose(false);
        setZreport(null);
        if (selectedTerminalId) void loadOtherShifts(selectedTerminalId);
        return;
      }
      setSession(null);
      setSale(null);
      setItems(null);
      setShowClose(false);
      setZreport(null);
    } catch (e) {
      // Refused over waiting sales the list had not caught (another tab parked
      // one): show them in the dialog, not as a bare error.
      const cause = (e as { response?: { data?: { cause?: { code?: string; sales?: WaitingSale[] } } } }).response?.data?.cause;
      if (cause?.code === 'parked_sales_open' && Array.isArray(cause.sales)) {
        setCloseWaiting(cause.sales);
        return;
      }
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const loadOtherShifts = useCallback(async (terminalId: string): Promise<void> => {
    try {
      const r = await api().get<{ items: PosSession[] }>('/pos/sessions', {
        params: { terminalId, status: 'open', limit: 50 },
      });
      setOtherShifts(r.data.items.filter((x) => x.cashierUserId !== userId));
    } catch {
      setOtherShifts([]);
    }
  }, [userId]);
  useEffect(() => {
    if (session || !selectedTerminalId) return;
    void loadOtherShifts(selectedTerminalId);
  }, [session, selectedTerminalId, loadOtherShifts]);

  // Load every sale rung up on the current shift (parked / finalized / voided).
  const openSessionSales = async (): Promise<void> => {
    if (!session) return;
    setSalesOpen(true);
    setSessionSales(null);
    setViewSaleId(null);
    setSalesStatusFilter('all');
    try {
      const r = await api().get<{ items: PosSale[] }>('/pos/sales', {
        params: { sessionId: session.id },
      });
      setSessionSales(r.data.items);
    } catch (e) {
      setError(friendlyError(e));
      setSessionSales([]);
    }
  };

  // Lightweight running tally for the shift bar: finalized count + gross PER
  // currency (a shift can hold a group customer's foreign-currency sale, and
  // those amounts must never be added together). Cheap enough to refresh after
  // every finalize; failures just leave the last value.
  const refreshShiftTotals = useCallback(async (): Promise<void> => {
    if (!session) return;
    try {
      const r = await api().get<{ items: PosSale[] }>('/pos/sales', {
        params: { sessionId: session.id, status: 'finalized' },
      });
      const by = new Map<string, number>();
      for (const s of r.data.items) {
        const code = s.currencyCode || session.currencyCode;
        by.set(code, (by.get(code) ?? 0) + Number(s.total));
      }
      // The shift's own currency first, then the rest alphabetically.
      const totals = [...by.entries()]
        .map(([currencyCode, total]) => ({ currencyCode, total }))
        .sort((a, b) => (a.currencyCode === session.currencyCode ? -1
          : b.currencyCode === session.currencyCode ? 1
          : a.currencyCode.localeCompare(b.currencyCode)));
      setShiftTotals({ count: r.data.items.length, totals });
    } catch {
      /* keep the previous tally on a transient error */
    }
  }, [session]);

  // Refresh the shift tally whenever the active shift changes.
  useEffect(() => {
    if (!session) { setShiftTotals(null); return; }
    void refreshShiftTotals();
  }, [session, refreshShiftTotals]);

  // ── the sale's date ─────────────────────────────────────────────────────────
  // Local calendar, not UTC: a keeper at 00:30 means the day on their wall.
  const dayStr = (offset: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const today = dayStr(0);
  /** What the date box shows: the cart's chosen day, else the one waiting for a cart, else today. */
  const saleDay = (sale && sale.sale.status === 'parked' ? sale.sale.docDate : pendingDay) || today;
  const backDated = saleDay !== today;
  const changeSaleDay = async (value: string): Promise<void> => {
    if (!value) return;
    // Choosing today is choosing nothing: the sale takes the day it is finalized.
    const chosen = value === today ? '' : value;
    if (!sale || sale.sale.status !== 'parked') { setPendingDay(chosen); return; }
    setBusy(true); setError(null);
    try {
      const r = await api().patch<SaleResponse>(`/pos/sales/${sale.sale.id}`, { docDate: chosen || null });
      setSale(r.data);
    } catch (e) {
      setError(friendlyError(e));
      playSound('error');
    } finally {
      setBusy(false);
    }
  };

  const ensureSaleRef = useRef<Promise<SaleResponse> | null>(null);
  const ensureSale = async (): Promise<SaleResponse> => {
    if (sale && sale.sale.status === 'parked') return sale;
    if (!session) throw new Error('no session');
    // De-dupe concurrent callers: two rapid add-item actions on an empty cart both
    // see `sale` still null (setSale hasn't flushed) and would each POST a NEW parked
    // sale, silently splitting the items across two orders. The first POST wins; later
    // callers reuse its in-flight promise.
    if (ensureSaleRef.current) return ensureSaleRef.current;
    const p = (async () => {
      const r = await api().post<SaleResponse>('/pos/sales', {
        sessionId: session.id,
        ...(canDate && pendingDay ? { docDate: pendingDay } : {}),
      });
      setSale(r.data);
      setPendingDay('');
      return r.data;
    })();
    ensureSaleRef.current = p;
    try {
      return await p;
    } finally {
      ensureSaleRef.current = null;
    }
  };

  // ── customer-group (price-list) pricing ─────────────────────────────────────
  // The sale runs in the attached customer's GROUP currency (or the session's
  // when they have no group), and each line is priced from that group's list.
  // Nothing is ever converted: a price quoted in another currency simply doesn't
  // apply, and the sale is re-denominated instead (see setSaleCurrency).

  // Resolve the customer's group price for an item, to pass as an override so
  // the group price is used (this also sidesteps the server's currency guard,
  // which would otherwise refuse an IQD-priced item on a USD sale). Returns
  // undefined — "let the server price it at base" — unless a real list price
  // applies AND it's denominated in the sale currency.
  const resolveGroupPrice = async (
    itemId: string,
    qty: string,
    partyId: string | null | undefined,
    itemUnitId?: string,
  ): Promise<string | undefined> => {
    if (!pricingOn || !partyId) return undefined;
    const party = parties?.find((p) => p.id === partyId);
    if (!party || party.code === 'WALK-IN' || !party.priceListId) return undefined;
    try {
      const r = await resolvePrice({ itemId, itemUnitId, partyId, qty: Number(qty) || 1, currency: saleCurrency });
      if (
        r.unitPrice != null &&
        (r.source === 'list-item' || r.source === 'list-percent') &&
        (!r.currencyCode || r.currencyCode === saleCurrency)
      ) {
        return trimMoney(r.unitPrice);
      }
    } catch {
      /* fall back to server base pricing */
    }
    return undefined;
  };

  /**
   * What ringing up (item, unit) will actually charge, and in which currency:
   * the attached customer's group price when one applies (already in the sale
   * currency), else the catalogue price in the ITEM's own currency. `blocked`
   * marks the combination the server would refuse — a catalogue price in a
   * currency the sale isn't running in, with no group price to stand in for it.
   */
  const priceForUnit = (
    item: Item,
    unitId: string | null,
  ): { amount: string; currency: string; blocked: boolean } => {
    const group = itemPrices && itemPrices.itemId === item.id
      ? (unitId ? itemPrices.units[unitId] ?? null : itemPrices.base)
      : undefined;
    if (group != null) return { amount: group, currency: saleCurrency, blocked: false };
    // A unit with no price of its own is the item's price for a FACTOR of them —
    // never the price of one. Charging the piece price for a carton takes the
    // whole carton off the shelf for the price of a single piece (the server
    // applies the same rule in cart.service).
    const unit = unitId ? item.units.find((u) => u.id === unitId) : undefined;
    const amount = (unit
      ? unit.salePrice ?? (item.salePrice != null ? new Decimal(item.salePrice).times(unit.factor).toFixed(4) : null)
      : item.salePrice) ?? '0';
    const itemCurrency = item.saleCurrencyCode ?? baseCurrency;
    return { amount, currency: itemCurrency, blocked: itemCurrency !== saleCurrency };
  };

  /**
   * Attach a customer to the (parked) sale — and move the sale into THEIR
   * group's currency when it differs. ONE PATCH does everything: the server
   * validates the currency (session's or the customer's group's, nothing else)
   * and re-prices every line for this customer, at whichever currency the sale
   * ends up in. It refuses a currency switch outright if any line has no price
   * there, so the cart can never end up mixing currencies; we then ask the
   * cashier what to do (`switchBlocked`).
   *
   * There is deliberately no line-by-line pass here any more. The browser could
   * only re-price lines the customer's list quotes explicitly (`resolveGroupPrice`
   * returns undefined for anything else), so an item ringing up at its shelf price
   * kept the PREVIOUS customer's automatic discount — a normal-customer deal
   * riding out on a ماركيت sale. Only the server knows which rules a customer type
   * is entitled to, so only the server re-prices.
   */
  const setCustomer = async (partyId: string) => {
    setBusy(true); setError(null);
    try {
      const party = parties?.find((p) => p.id === partyId) ?? null;
      const s = await ensureSale();
      const target = saleCurrencyFor(party);
      const switching = target !== s.sale.currencyCode;
      const r = await api().patch<SaleResponse>(`/pos/sales/${s.sale.id}`, {
        partyId,
        ...(switching ? { currencyCode: target } : {}),
      });
      setSale(r.data);
      setCustOpen(false);
    } catch (e) {
      const p = problemOf(e);
      const party = parties?.find((x) => x.id === partyId) ?? null;
      if (p.cause?.code === 'line_price_unavailable_in_currency') {
        // Refused: the cart holds items this group can't price in its currency.
        setCustOpen(false);
        setSwitchBlocked({
          partyId,
          partyName: party?.displayName ?? '',
          from: saleCurrency,
          to: p.cause.currency ?? saleCurrencyFor(party),
          items: (p.cause.lines ?? [])
            .map((l) => sale?.lines.find((x) => x.lineNo === l.lineNo)?.description ?? '')
            .filter((d): d is string => !!d),
        });
      } else if (p.cause?.code === 'currency_change_after_tender') {
        setError(t('sale.currencyAfterTender', {
          currency: saleCurrencyFor(party),
          defaultValue: 'Remove the payments taken on this sale before switching it to {{currency}}.',
        }));
      } else {
        setError(friendlyError(e));
      }
      playSound('error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Move the current cart to `currency` (the attached customer's group currency,
   * or back to the session's). Same server contract as attaching a customer.
   */
  const setSaleCurrency = async (currencyCode: string): Promise<void> => {
    if (!sale || sale.sale.currencyCode === currencyCode) return;
    setBusy(true); setError(null);
    try {
      const r = await api().patch<SaleResponse>(`/pos/sales/${sale.sale.id}`, { currencyCode });
      setSale(r.data);
    } catch (e) {
      const p = problemOf(e);
      if (p.cause?.code === 'line_price_unavailable_in_currency') {
        setSwitchBlocked({
          partyId: sale.sale.partyId ?? '',
          partyName: parties?.find((x) => x.id === sale.sale.partyId)?.displayName ?? '',
          from: sale.sale.currencyCode,
          to: currencyCode,
          items: (p.cause.lines ?? [])
            .map((l) => sale.lines.find((x) => x.lineNo === l.lineNo)?.description ?? '')
            .filter((d): d is string => !!d),
        });
      } else if (p.cause?.code === 'currency_change_after_tender') {
        setError(t('sale.currencyAfterTender', {
          currency: currencyCode,
          defaultValue: 'Remove the payments taken on this sale before switching it to {{currency}}.',
        }));
      } else {
        setError(friendlyError(e));
      }
      playSound('error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Resolution for a refused switch: drop the cart and open a fresh sale for that
   * customer, already denominated in their group's currency.
   */
  const startSaleForBlocked = async (): Promise<void> => {
    const blocked = switchBlocked;
    if (!blocked || !session) { setSwitchBlocked(null); return; }
    setBusy(true); setError(null);
    try {
      const docDate = canDate && sale?.sale.status === 'parked' ? sale.sale.docDate : null;
      if (sale && sale.sale.status === 'parked') {
        await api().delete(`/pos/sales/${sale.sale.id}`).catch(() => undefined);
      }
      const r = await api().post<SaleResponse>('/pos/sales', {
        sessionId: session.id,
        // No party ⇒ let the server attach walk-in (an empty id would 400).
        ...(blocked.partyId ? { partyId: blocked.partyId } : {}),
        currencyCode: blocked.to,
        ...(docDate ? { docDate } : {}),
      });
      setSale(r.data);
      setSwitchBlocked(null);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Create a customer inline and immediately attach it to the sale.
  // Create a real, persisted product without leaving the register.
  const createProduct = async () => {
    const code = npCode.trim();
    const name = npName.trim();
    if (!code || !name) return;
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = {
        code,
        name,
        itemType: npType,
        isSellable: true,
        isPurchasable: npType === 'product',
        // A product made at the counter is still stock: its sales must leave the shelf.
        isInventoryTracked: npType === 'product',
        unitOfMeasure: npType === 'service' ? 'svc' : 'ea',
        barcode: code,
      };
      if (npPrice) {
        body.salePrice = npPrice;
        // A catalogue price belongs to the SHOP, so it's denominated in the
        // shift's own currency — never a hardcoded USD, and never the currency
        // of whichever customer group happens to be on the cart right now (that
        // would quietly re-denominate the item for everyone else).
        body.saleCurrencyCode = session?.currencyCode ?? baseCurrency;
      }
      const r = await api().post<Item>('/items', body);
      setItems((prev) => [r.data, ...(prev ?? [])]);
      setNewProdOpen(false);
      setNpCode(''); setNpName(''); setNpPrice(''); setNpType('product');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const startNewSale = useCallback(async () => {
    if (!session) return;
    setBusy(true); setError(null);
    try {
      const r = await api().post<SaleResponse>('/pos/sales', { sessionId: session.id });
      setSale(r.data);
      setScanText('');
      setShowCustomForm(false);
      setCustomDesc(''); setCustomPrice(''); setCustomQty('1');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [session, friendlyError]);

  const addLine = async (input: {
    itemId?: string;
    itemUnitId?: string;
    description?: string;
    barcode?: string;
    quantity: string;
    unitPriceOverride?: string;
  }) => {
    setBusy(true); setError(null);
    try {
      const s = await ensureSale();
      const body: Record<string, unknown> = { quantity: input.quantity };
      if (input.itemId) body.itemId = input.itemId;
      if (input.itemUnitId) body.itemUnitId = input.itemUnitId;
      if (input.description) body.description = input.description;
      if (input.barcode) body.barcode = input.barcode;
      if (input.unitPriceOverride) body.unitPriceOverride = input.unitPriceOverride;
      // The shelf this line leaves from. Sent per LINE because that is what the
      // server books stock against — switching warehouse mid-sale therefore
      // affects what comes next, and never rewrites what is already in the cart.
      if (sellWarehouseId) body.warehouseId = sellWarehouseId;
      const r = await api().post<SaleResponse>(`/pos/sales/${s.sale.id}/lines`, body);
      setSale(r.data);
      playSound('add');
      // Flash the line that landed (highest lineNo) for a beat.
      const newest = r.data.lines.reduce((m, l) => Math.max(m, l.lineNo), 0);
      setFlashLineNo(newest);
      window.setTimeout(() => setFlashLineNo((cur) => (cur === newest ? null : cur)), 900);
    } catch (e) {
      // The server refuses an item priced in a currency other than the sale's.
      // On a group sale that means the customer's list has no price for it —
      // say so, because "start a {{currency}} shift" is not the fix there.
      const p = problemOf(e);
      if (p.cause?.code === 'price_currency_mismatch') {
        const inGroupCurrency = attachedGroupCurrency != null && attachedGroupCurrency === saleCurrency;
        setError(inGroupCurrency
          ? t('sale.noGroupPriceForItem', {
              group: attachedParty?.priceListName ?? '',
              currency: saleCurrency,
              defaultValue: 'This item has no {{currency}} price in this customer\'s group ({{group}}). Add one to the price list, or ring it up as a custom line.',
            })
          : t('errors.priceCurrencyMismatch', {
              currency: p.cause.itemCurrency ?? '',
              saleCurrency: p.cause.expected ?? saleCurrency,
              defaultValue: 'This item is priced in {{currency}}, not the sale currency {{saleCurrency}}. Set a custom price or ring it up in a {{currency}} shift.',
            }));
      } else {
        setError(friendlyError(e));
      }
      playSound('error');
    } finally {
      setBusy(false);
    }
  };

  // Manual sale-level (header) discount — percent or fixed amount.
  const applyDiscount = async () => {
    if (!sale || !discountValue.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await api().post<SaleResponse>(`/pos/sales/${sale.sale.id}/discount`, {
        discount: { kind: discountKind, value: discountValue.trim() },
      });
      setSale(r.data);
      setDiscountValue('');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const clearDiscount = async () => {
    if (!sale) return;
    setBusy(true); setError(null);
    try {
      const r = await api().post<SaleResponse>(`/pos/sales/${sale.sale.id}/discount`, { discount: null });
      setSale(r.data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyCoupon = async () => {
    if (!sale || !couponCode.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await api().post<SaleResponse>(`/pos/sales/${sale.sale.id}/coupon`, {
        code: couponCode.trim(),
      });
      setSale(r.data);
      setCouponCode('');
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'not_found') {
        setError(t('sale.couponNotFound', { defaultValue: 'No such coupon code.' }));
      } else if (code === 'unprocessable') {
        setError(t('sale.couponInvalid', { defaultValue: "Coupon can't be applied to this sale." }));
      } else {
        // A real server/network error — don't blame the coupon.
        setError(friendlyError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  // A different cart (new, resumed, switched) shows ITS note — keyed on the id
  // so a save's own response never clobbers what the cashier is typing.
  const saleIdForNote = sale?.sale.id;
  useEffect(() => {
    setNoteDraft(sale?.sale.notes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleIdForNote]);

  // A note on the sale — it rides onto the invoice when the sale finalizes.
  const saveNote = async (): Promise<void> => {
    if (!sale || sale.sale.status !== 'parked') return;
    const next = noteDraft.trim();
    if (next === (sale.sale.notes ?? '').trim()) return;
    try {
      const r = await api().patch<SaleResponse>(`/pos/sales/${sale.sale.id}`, { notes: next || null });
      // Blur fires as the cashier clicks ANOTHER cart: only take the response
      // if this sale is still the one on screen.
      setSale((cur) => (cur && cur.sale.id === r.data.sale.id ? r.data : cur));
    } catch (e) {
      setError(friendlyError(e));
    }
  };

  const removeCoupon = async () => {
    if (!sale) return;
    setBusy(true); setError(null);
    try {
      const r = await api().delete<SaleResponse>(`/pos/sales/${sale.sale.id}/coupon`);
      setSale(r.data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-read ONE item's on-hand and fold it into the loaded catalogue.
   *
   * The grid's numbers are as old as the catalogue load, and a register stays
   * open all day — so the moment a chooser is about to SHOW a quantity, that
   * quantity is re-read. Failures are silent on purpose: a stale number beside
   * the price is still useful, an error toast over the cashier's tap is not.
   */
  const refreshOnHand = (itemId: string): void => {
    void api()
      // `stockDetail` adds the per-warehouse split — the detail card answers
      // "where is it?", which a shop with a till per warehouse asks constantly.
      .get<{ items: Item[] }>('/items', { params: { ids: itemId, stock: 1, stockDetail: 1 } })
      .then((r) => {
        const fresh = r.data.items[0];
        if (!fresh || fresh.onHand === undefined) return;
        const patch = { onHand: fresh.onHand, onHandByWarehouse: fresh.onHandByWarehouse };
        setItems((prev) => (prev ?? []).map((i) => (i.id === fresh.id ? { ...i, ...patch } : i)));
        setUnitPickItem((cur) => (cur && cur.id === fresh.id ? { ...cur, ...patch } : cur));
        setDetailItem((cur) => (cur && cur.id === fresh.id ? { ...cur, ...patch } : cur));
      })
      .catch(() => undefined);
  };

  const addItem = async (item: Item) => {
    // Items with packaging units (pack/carton) open a chooser so the cashier
    // picks the unit + its price; plain items add the base unit straight away.
    // Either way the backend resolves price + tax from the canonical record.
    if (item.units.length > 0) {
      setUnitPickItem(item);
      refreshOnHand(item.id);
      void loadItemPrices(item);
      return;
    }
    // Base unit → the customer's own LAST price first (what was agreed the
    // previous time wins over the list), else their group price.
    const unitPriceOverride = lastPriceOf(item.id)?.unitPrice
      ?? await resolveGroupPrice(item.id, '1', sale?.sale.partyId);
    // BY ID. This used to send the item's CODE as if it were a scanned barcode
    // and let the server resolve it — which rang up a DIFFERENT product whenever
    // two items shared a code (they may: a code is not unique per tenant). The
    // tile already knows exactly which row it is.
    await addLine({ itemId: item.id, quantity: '1', unitPriceOverride });
  };

  // Scanning a barcode: match a loaded item so we can apply the customer's group
  // price. When it isn't one of those (a pack/carton barcode, or an item outside
  // the loaded page) and a customer group is pricing this sale, ask the server
  // to resolve it — otherwise the line would be added at the catalogue price,
  // which a group sale in another currency can't even accept. The add itself
  // still goes by barcode, so the server resolves the unit exactly as before.
  //
  // Matched on BARCODES only (own, extra, or a packaging unit's) — never the
  // item code, which is not unique and would price the wrong product.
  const addScanned = async (barcode: string): Promise<void> => {
    // WHICH PACK, not just which product. The local match happily recognised a
    // pack's barcode and then threw that fact away, so the group price below was
    // resolved for the BASE unit and sent as an override — which outranks the
    // server's correct price. A scanned سيت of six therefore rang up showing
    // "سيت ×6" at the price of one piece.
    const hit = items?.find(
      (i) =>
        i.barcode === barcode ||
        i.barcodes?.includes(barcode) ||
        i.units.some((u) => u.barcode === barcode || (u.barcodes ?? []).includes(barcode)),
    );
    let itemId = hit?.id;
    // Only when the code belongs to a PACK: the item's own barcode (or one of
    // its extra labels) means the base unit, and must not pick up a pack price.
    let itemUnitId: string | undefined = hit?.units.find(
      (u) => u.barcode === barcode || (u.barcodes ?? []).includes(barcode),
    )?.id;
    if (!itemId && pricingOn && attachedParty?.priceListId) {
      try {
        const { data } = await api().get<{ item: Item; itemUnitId: string | null }>(
          '/items/resolve', { params: { barcode } },
        );
        itemId = data.item.id;
        itemUnitId = data.itemUnitId ?? undefined;
      } catch {
        /* unknown code — let the add call produce the real error */
      }
    }
    const unitPriceOverride = itemId
      ? lastPriceOf(itemId, itemUnitId)?.unitPrice
        ?? await resolveGroupPrice(itemId, '1', sale?.sale.partyId, itemUnitId)
      : undefined;
    await addLine({ barcode, quantity: '1', unitPriceOverride });
  };

  // Add the chosen unit (or base) for the item in the unit-picker dialog. When
  // the picker was opened to switch an existing line, drop that line first and
  // re-add it with the chosen unit (the line-update API can't change the unit).
  const addItemUnit = async (item: Item, unitId: string | null) => {
    const switching = unitPickLine;
    setUnitPickItem(null);
    setUnitPickLine(null);
    const quantity = switching?.quantity ?? '1';
    if (switching) await removeLine(switching.lineNo);
    if (unitId) {
      // A packaging unit — the customer's own LAST price for this exact pack
      // first, else their per-unit group price, else the unit's base price.
      const unitPriceOverride = lastPriceOf(item.id, unitId)?.unitPrice
        ?? await resolveGroupPrice(item.id, quantity, sale?.sale.partyId, unitId);
      await addLine({ itemId: item.id, itemUnitId: unitId, quantity, unitPriceOverride });
    } else {
      const unitPriceOverride = lastPriceOf(item.id)?.unitPrice
        ?? await resolveGroupPrice(item.id, quantity, sale?.sale.partyId);
      await addLine({ itemId: item.id, quantity, unitPriceOverride });
    }
  };

  // Open the unit picker against an existing line (only for multi-unit items).
  const openUnitSwitch = (line: SaleResponse['lines'][number]) => {
    const item = line.itemId ? itemById.get(line.itemId) : undefined;
    if (!item || item.units.length === 0) return;
    setUnitPickLine({ lineNo: line.lineNo, quantity: line.quantity });
    setUnitPickItem(item);
    void loadItemPrices(item);
  };

  const addCustomLine = async () => {
    if (!customDesc.trim() || !customPrice) return;
    await addLine({
      description: customDesc.trim(),
      quantity: customQty || '1',
      unitPriceOverride: customPrice,
    });
    setCustomDesc(''); setCustomPrice(''); setCustomQty('1');
    setShowCustomForm(false);
  };

  const removeLine = async (lineNo: number) => {
    if (!sale) return;
    setBusy(true); setError(null);
    try {
      const r = await api().delete<SaleResponse>(`/pos/sales/${sale.sale.id}/lines/${lineNo}`);
      setSale(r.data);
      playSound('remove');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const changeLineQty = async (lineNo: number, currentQty: string, delta: number) => {
    if (!sale) return;
    const next = new Decimal(currentQty || '0').plus(delta);
    // The ± buttons never cross zero. Stepping a sale of 1 down removes the row,
    // as it always has — turning it into a RETURN is a deliberate act (type the
    // negative), not something you reach by leaning on the minus key. A line
    // that is already a return steps the same way, toward zero and out.
    if (next.eq(0) || next.isNegative() !== new Decimal(currentQty || '0').isNegative()) {
      await removeLine(lineNo);
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await api().patch<SaleResponse>(
        `/pos/sales/${sale.sale.id}/lines/${lineNo}`,
        { quantity: next.toString() },
      );
      setSale(r.data);
      playSound('tick');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Set an absolute quantity / unit-price on a line (from the inline inputs).
  const updateLineField = async (lineNo: number, body: { quantity?: string; unitPriceOverride?: string }) => {
    if (!sale) return;
    setBusy(true); setError(null);
    try {
      const r = await api().patch<SaleResponse>(`/pos/sales/${sale.sale.id}/lines/${lineNo}`, body);
      setSale(r.data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelCart = async () => {
    if (!sale) return;
    if (sale.sale.status !== 'parked') {
      setSale(null);
      return;
    }
    setBusy(true); setError(null);
    try {
      await api().delete(`/pos/sales/${sale.sale.id}`);
      setSale(null);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Clear all items from the cart but keep the SAME customer — drop the parked
  // sale and open a fresh one re-attached to the same party (one clean reset
  // regardless of how many lines, vs deleting them one by one).
  const clearCart = async () => {
    if (!sale || !session) { setClearOpen(false); return; }
    setBusy(true); setError(null);
    try {
      const partyId = sale.sale.partyId;
      // Keep the customer AND the currency their group prices in — a fresh cart
      // for the same customer must not silently fall back to the shift currency.
      const currencyCode = sale.sale.currencyCode;
      const docDate = canDate ? sale.sale.docDate : null;
      await api().delete(`/pos/sales/${sale.sale.id}`);
      const r = await api().post<SaleResponse>('/pos/sales', {
        sessionId: session.id, partyId, currencyCode, ...(docDate ? { docDate } : {}),
      });
      setSale(r.data);
      setClearOpen(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // ---- parked ("waiting") sales: hold the cart + switch between several ----
  const loadParked = useCallback(async () => {
    if (!session) { setParkedSales([]); return; }
    try {
      const r = await api().get<{ items: PosSale[] }>('/pos/sales', {
        params: { sessionId: session.id, status: 'parked', mine: 1, limit: 50 },
      });
      setParkedSales(r.data.items);
    } catch {
      /* non-fatal — the switcher just won't list holds */
    }
  }, [session]);

  // Keep the waiting list fresh whenever the active sale changes (hold, resume,
  // finalize, void all flip the id or status) — and on a slow tick, because this
  // tab is not the only writer: a second register tab or a long-lived session
  // can finalize a held sale, and a stale "Waiting" badge that keeps naming a
  // PAID sale is exactly the bug the shop reported.
  useEffect(() => {
    void loadParked();
    const timer = window.setInterval(() => { void loadParked(); }, 30_000);
    return () => window.clearInterval(timer);
  }, [loadParked, sale?.sale.id, sale?.sale.status]);

  // Park the current cart (optionally named) and open a fresh empty sale, so the
  // current one keeps waiting in the parked list.
  const holdSale = async (label: string) => {
    if (!sale || !session || sale.lines.length === 0) { setHoldOpen(false); return; }
    setBusy(true); setError(null);
    try {
      const trimmed = label.trim();
      if (trimmed) await api().patch(`/pos/sales/${sale.sale.id}`, { holdLabel: trimmed });
      // The current sale is already 'parked' server-side; just open a new one.
      // setSale(new id) flips the effect below, which refreshes the parked list.
      const r = await api().post<SaleResponse>('/pos/sales', { sessionId: session.id });
      setSale(r.data);
      setHoldOpen(false);
      setHoldLabelInput('');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Resume a parked sale into the cart. Drop the current cart first if it's an
  // unused empty so throwaway holds don't pile up.
  const resumeSale = async (id: string) => {
    if (sale?.sale.id === id) { setParkedOpen(false); return; }
    setBusy(true); setError(null);
    try {
      // Fetch the target FIRST — a failure here must never lose the current cart.
      const r = await api().get<SaleResponse>(`/pos/sales/${id}`);
      // A stale row: the sale was finalized or voided since this list was drawn
      // (another tab, or a badge that sat unrefreshed). Loading it as the active
      // cart would put a PAID sale on the register looking resumable — say what
      // happened, drop the dead row, and keep the cashier's current cart.
      if (r.data.sale.status !== 'parked') {
        setParkedSales((rs) => rs.filter((p) => p.id !== id));
        toast.info(r.data.sale.status === 'voided'
          ? t('sale.resumeVoided', { defaultValue: 'That held sale was voided — it has been removed from Waiting.' })
          : t('sale.resumePaid', { number: r.data.sale.saleNumber, defaultValue: 'Sale {{number}} is already paid — it has been removed from Waiting.' }));
        void loadParked();
        return;
      }
      // Only after the target is safely in hand, drop the current cart if it's an
      // unused empty so throwaway holds don't pile up.
      if (sale && sale.sale.status === 'parked' && sale.lines.length === 0) {
        await api().delete(`/pos/sales/${sale.sale.id}`).catch(() => undefined);
      }
      setSale(r.data);
      setParkedOpen(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Edit a FINISHED sale: the server re-opens it as a cart (undoing its invoice,
   * payments, cost and stock — the books keep both the original and the reversal)
   * and it lands here as the active cart, to change and charge again. Offered to
   * `pos.sale.edit` holders for sales of their own branch; the server decides
   * everything else and says why when it refuses.
   */
  const editFinishedSale = async (id: string, saleNumber: string): Promise<void> => {
    const go = await confirmDialog({
      title: t('sales.detail.editTitle', { sale: saleNumber, defaultValue: 'Edit sale {{sale}}?' }),
      message: t('sales.detail.editBody', {
        sale: saleNumber,
        defaultValue: 'Sale {{sale}} goes back to the cart: its invoice is cancelled, the payment is voided and the stock returns to the shelf. Change what is wrong and charge it again — the original and its reversal stay in the books as a record.',
      }),
      confirmLabel: t('sales.detail.edit', { defaultValue: 'Edit this sale' }),
    });
    if (!go) return;
    setBusy(true); setError(null);
    try {
      const r = await api().post<SaleResponse>(`/pos/sales/${id}/reopen`, {
        reason: t('sales.detail.editReason', { defaultValue: 'Edited at the register' }),
      });
      // An unused empty cart would only pile up behind the one being edited.
      if (sale && sale.sale.id !== id && sale.sale.status === 'parked' && sale.lines.length === 0) {
        await api().delete(`/pos/sales/${sale.sale.id}`).catch(() => undefined);
      }
      setSale(r.data);
      setPendingDay('');
      setSalesOpen(false);
      setViewSaleId(null);
      void loadParked();
      void refreshShiftTotals();
      toast.success(t('sales.detail.edited', {
        sale: saleNumber,
        defaultValue: 'Sale {{sale}} is back in the cart — change it, then charge it again.',
      }));
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      const cause = problemOf(e).cause;
      toast.error(
        cause?.code === 'session_closed'
          ? t('sales.detail.editShiftClosed', { defaultValue: 'That shift has been closed and counted — a sale in it can no longer be edited.' })
          : cause?.code === 'sale_has_linked_records'
            ? t('sales.detail.editLinked', {
              doc: cause.docNumber ?? '',
              defaultValue: 'This sale cannot be edited: {{doc}} (a return, credit note or instalment plan) hangs off it.',
            })
            : cause?.code === 'sale_has_later_payments'
              ? t('sales.detail.editLaterPayments', {
                defaultValue: 'This sale has been paid against since it was rung up, so it cannot be edited. Void the later payment first.',
              })
              : cause?.code === 'sale_not_finalized'
                ? t('sales.detail.editNotFinished', { defaultValue: 'That sale is no longer finished — refresh the list.' })
                : status === 404
                  ? t('sales.detail.editNotFound', { defaultValue: 'That sale is not in your branch.' })
                  : friendlyError(e),
      );
    } finally {
      setBusy(false);
    }
  };

  const discardParked = async (id: string) => {
    setBusy(true); setError(null);
    try {
      await api().delete(`/pos/sales/${id}`);
      if (sale?.sale.id === id) setSale(null);
      void loadParked();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Open the reusable product-detail modal (a tile's ⓘ or a cart line's name).
  const openDetail = (item: Item): void => {
    setDetailItem(item);
    refreshOnHand(item.id);
    void loadItemPrices(item);
  };

  const openLineDetail = async (line: SaleResponse['lines'][number]) => {
    if (!line.itemId) return;
    const cached = itemById.get(line.itemId);
    if (cached) { openDetail(cached); return; }
    try {
      const r = await api().get<Item>(`/items/${line.itemId}`);
      openDetail(r.data);
    } catch {
      /* item may be inactive/removed — ignore */
    }
  };

  // The shift the close dialog is counting: yours, or another cashier's being
  // closed from the open-shift screen. Built before the early returns so both
  // screens can show it.
  const closing = closeFor ?? session;
  const closeCcy = closing?.currencyCode ?? '';
  const drawerRows: Array<{
    currencyCode: string; expected: string; byTender: PosZReport['byTender'];
    collected?: string; collectedOther?: string; collections?: number;
    opening?: string; cashFromSales?: string; change?: string; refunds?: string;
    /** Takings that never reach the drawer: card / wallet / other / cashback tenders + on account. */
    nonCash?: string;
  }> =
    !zreport
      ? []
      : zreport.byCurrency?.length
        ? zreport.byCurrency.map((c) => ({
            currencyCode: c.currencyCode,
            expected: c.expectedCash,
            byTender: c.byTender,
            collected: c.collectedCash,
            collectedOther: c.collectedOther,
            collections: c.collectionsCount,
            opening: c.openingCash,
            // The notes received for SALES: everything in, less the debt payments
            // named on their own line (expected = opening + this + debt − change − refunds).
            cashFromSales: String(Number(c.cashReceived ?? c.expectedCash) - Number(c.collectedCash ?? 0) - (c.cashReceived === undefined ? Number(c.openingCash) : 0)),
            change: c.changeGiven,
            refunds: c.cashRefunds,
            nonCash: String(
              c.byTender.filter((b) => b.kind !== 'cash').reduce((acc, b) => acc + Number(b.total), 0)
              + Math.max(0, Number(c.netSales) - c.byTender.reduce((acc, b) => acc + Number(b.total), 0)),
            ),
          }))
        : [{ currencyCode: closeCcy, expected: zreport.expectedCash, byTender: zreport.byTender }];
  const closeDialog = (
  <Dialog open={showClose} onOpenChange={(o) => { if (!o) cancelClose(); }}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <LogOut className="h-4 w-4 text-fg-muted" aria-hidden />
          {t('shift.closeTitle')}
        </DialogTitle>
        {closeFor && (
          <p className="text-sm text-fg-muted">
            {t('shift.closingFor', {
              defaultValue: "Closing {{name}}'s shift — count the money that cashier took.",
              name: closeFor.cashierName ?? '—',
            })}
          </p>
        )}
      </DialogHeader>

      {/* Waiting sales hold the close (owner, 2026-11-02): closed over, they
          vanished from the register. Each is named so it can be found. */}
      {closeWaiting && closeWaiting.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm">
          <span className="font-semibold text-danger">
            {closeFor
              ? t('shift.waitingBlockOther', {
                  defaultValue: "This shift can't close: the cashier has {{n}} waiting sale(s). They must complete or delete them first.",
                  n: closeWaiting.length,
                })
              : t('shift.waitingBlock', {
                  defaultValue: "This shift can't close: {{n}} waiting sale(s) are still open. Complete or delete them from Waiting sales first.",
                  n: closeWaiting.length,
                })}
          </span>
          <ul className="flex flex-col gap-0.5 text-xs">
            {closeWaiting.map((w) => (
              <li key={w.saleNumber} className="flex items-center justify-between gap-2">
                <span><span className="font-mono">{w.saleNumber}</span>{w.partyName ? ` · ${w.partyName}` : ''}</span>
                <span className="font-mono tabular-nums" dir="ltr">{formatMoneyLib(w.total, { locale: i18n.language, currency: w.currencyCode })}</span>
              </li>
            ))}
          </ul>
          {!closeFor && (
            <ShButton variant="outline" size="sm" className="self-start" onClick={() => { cancelClose(); setParkedOpen(true); }}>
              {t('shift.showWaiting', { defaultValue: 'Show waiting sales' })}
            </ShButton>
          )}
        </div>
      )}

      {/* Drawer reconciliation, ONE BLOCK PER CURRENCY. The cashier counts each
          currency's notes separately, so each gets its own expected figure,
          count field and variance — never a single mixed total. */}
      {zLoading ? (
        <Skeleton className="mt-1 h-16 w-full" />
      ) : drawerRows.length > 0 ? (
        <div className="flex flex-col gap-2">
          {drawerRows.length > 1 && (
            <p className="text-xs text-fg-muted">
              {t('shift.countEachCurrency', {
                defaultValue: 'This shift took money in more than one currency. Count each one separately — they are never added together.',
              })}
            </p>
          )}
          {drawerRows.map((row) => {
            const counted = countedCash[row.currencyCode] ?? '';
            const variance = Number(counted || '0') - Number(row.expected);
            const balanced = Math.abs(variance) < 0.005;
            return (
              <div key={row.currencyCode} className="rounded-lg border border-border bg-bg-subtle/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-fg-muted">
                        {t('shift.expectedCash', { defaultValue: 'Expected cash in drawer' })}
                      </span>
                      {drawerRows.length > 1 && (
                        <span className="rounded-full border border-border bg-bg px-1.5 py-0.5 font-mono text-[0.65rem] font-semibold text-fg">
                          {row.currencyCode}
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xl font-bold tabular-nums text-fg">
                      {formatMoney(row.expected, row.currencyCode, i18n.language)}
                    </div>
                    {/* How the expected figure is made up — the customers' debt
                        payments named, so the money they handed over is visibly in it. */}
                    <dl className="mt-1.5 grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-[0.75rem]">
                      {Number(row.opening ?? 0) > 0 && (
                        <>
                          <dt className="text-fg-muted">{t('shift.zOpening', { defaultValue: 'Opening float' })}</dt>
                          <dd className="text-end font-mono tabular-nums">{formatMoney(row.opening ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                      <dt className="text-fg-muted">{t('shift.zCashSales', { defaultValue: '+ Cash from sales' })}</dt>
                      <dd className="text-end font-mono tabular-nums">{formatMoney(row.cashFromSales ?? '0', row.currencyCode, i18n.language)}</dd>
                      {Number(row.collected ?? 0) > 0 && (
                        <>
                          <dt className="font-semibold text-info">
                            {t('shift.zDebtPayments', { defaultValue: '+ Customers paying their debt ({{n}})', n: String(row.collections ?? 0) })}
                          </dt>
                          <dd className="text-end font-mono font-semibold tabular-nums text-info">{formatMoney(row.collected ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                      {Number(row.change ?? 0) > 0 && (
                        <>
                          <dt className="text-fg-muted">{t('shift.zChange', { defaultValue: '− Change given' })}</dt>
                          <dd className="text-end font-mono tabular-nums">{formatMoney(row.change ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                      {Number(row.refunds ?? 0) > 0 && (
                        <>
                          <dt className="text-fg-muted">{t('shift.zRefunds', { defaultValue: '− Cash refunds' })}</dt>
                          <dd className="text-end font-mono tabular-nums">{formatMoney(row.refunds ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                      {Number(row.nonCash ?? 0) > 0.004 && (
                        <>
                          <dt className="text-fg-subtle">{t('shift.notInDrawer', { defaultValue: 'Card / wallet / other / on account (not in the drawer)' })}</dt>
                          <dd className="text-end font-mono tabular-nums text-fg-subtle">{formatMoney(row.nonCash ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                      {Number(row.collectedOther ?? 0) > 0 && (
                        <>
                          <dt className="text-fg-subtle">{t('shift.zDebtOther', { defaultValue: 'Debt paid by card / other (not in the drawer)' })}</dt>
                          <dd className="text-end font-mono tabular-nums text-fg-subtle">{formatMoney(row.collectedOther ?? '0', row.currencyCode, i18n.language)}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                  {/* tender breakdown chips — in THIS currency */}
                  <div className="flex flex-wrap gap-1.5">
                    {/* Customers who paid off their debt in this shift — part of the expected cash. */}
                    {Number(row.collected ?? 0) + Number(row.collectedOther ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-info/40 bg-info/10 px-2 py-1 text-xs">
                        <Wallet className="h-3.5 w-3.5 text-info" aria-hidden />
                        <span className="text-fg-muted">
                          {t('collect.chip', { defaultValue: 'Debt payments ({{n}})', n: String(row.collections ?? 0) })}
                        </span>
                        <span className="font-mono font-semibold tabular-nums text-fg">
                          {formatMoney(String(Number(row.collected ?? 0) + Number(row.collectedOther ?? 0)), row.currencyCode, i18n.language)}
                        </span>
                      </span>
                    )}
                    {row.byTender.filter((b) => Number(b.total) !== 0).map((b) => (
                      <span
                        key={b.kind}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-bg px-2 py-1 text-xs"
                      >
                        <TenderIcon kind={b.kind} />
                        <span className="text-fg-muted">{t(`tender.${b.kind}`)}</span>
                        <span className="font-mono font-semibold tabular-nums text-fg">
                          {formatMoney(b.total, row.currencyCode, i18n.language)}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    id={`closeCash-${row.currencyCode}`}
                    label={
                      drawerRows.length > 1
                        ? `${t('shift.closingCash')} · ${row.currencyCode}`
                        : t('shift.closingCash')
                    }
                  >
                    <Input
                      id={`closeCash-${row.currencyCode}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={counted}
                      onChange={(e) =>
                        setCountedCash((prev) => ({ ...prev, [row.currencyCode]: e.target.value }))
                      }
                    />
                  </FormField>
                  {/* live variance (counted − expected), in THIS currency */}
                  <FormField
                    id={`closeVariance-${row.currencyCode}`}
                    label={t('shift.variance', { defaultValue: 'Variance' })}
                  >
                    <div
                      className={
                        'flex h-10 items-center rounded-md border px-3 font-mono text-sm font-semibold tabular-nums '
                        + (balanced
                          ? 'border-success/40 bg-success/10 text-success'
                          : 'border-warning/40 bg-warning/10 text-warning')
                      }
                    >
                      {balanced
                        ? t('shift.varianceBalanced', { defaultValue: 'Balanced ✓' })
                        : `${variance > 0 ? '+' : ''}${formatMoney(String(variance), row.currencyCode, i18n.language)}`}
                    </div>
                  </FormField>
                </div>
                {/* Over by exactly the non-cash takings: the money is real cash, so a
                    cash sale was almost certainly rung up as card. */}
                {variance > 0.004 && Number(row.nonCash ?? 0) > 0.004 && Math.abs(variance - Number(row.nonCash)) < 0.005 && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-danger/40 bg-danger/10 px-2.5 py-1.5 text-xs text-danger" role="alert">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    {t('shift.overIsNonCash', { defaultValue: 'The over equals the card / wallet / on-account sales. Were cash sales rung up as card? Find them in this shift\'s sales.' })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : zFailed ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-fg" role="alert">
          <span>
            {t('shift.figuresFailed', {
              defaultValue: "This shift's figures could not be loaded, so it can't be counted yet. Try again in a moment.",
            })}
          </span>
          <ShButton variant="outline" size="sm" onClick={() => void openCloseShift(closeFor ?? undefined)}>
            {t('shift.tryAgain', { defaultValue: 'Try again' })}
          </ShButton>
        </div>
      ) : (
        /* Before the figures have been asked for — never shown once they load or fail. */
        <FormField id="closeCash" label={t('shift.closingCash')}>
          <Input
            id="closeCash"
            type="number"
            step="0.01"
            min="0"
            value={countedCash[closeCcy] ?? ''}
            onChange={(e) => setCountedCash((prev) => ({ ...prev, [closeCcy]: e.target.value }))}
          />
        </FormField>
      )}
      {/* Every customer who paid off their debt in this shift — the money behind
          the line above, by name, so the count can be checked against it. */}
      {closeCollections.length > 0 && (
        <div className="rounded-lg border border-info/40 bg-info/5 p-3">
          <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold">
            <Wallet className="h-4 w-4 text-info" aria-hidden />
            {t('collect.title', { defaultValue: 'Customer pays their debt' })} · {closeCollections.length}
          </div>
          <ul className="flex flex-col divide-y divide-border text-sm">
            {closeCollections.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate">
                  {c.partyName ?? '—'}
                  <span className="ms-2 font-mono text-xs text-fg-muted">{c.paymentNumber}</span>
                  <span className="ms-2 text-xs text-fg-subtle">{t(`tender.${c.method}`, { defaultValue: c.method })}</span>
                </span>
                <span dir="ltr" className="shrink-0 font-mono font-semibold tabular-nums">
                  {formatMoney(c.notesAmount, c.notesCurrency, i18n.language)}
                  {c.notesCurrency !== c.currencyCode && (
                    <span className="ms-1 text-xs font-normal text-fg-muted">({formatMoney(c.amount, c.currencyCode, i18n.language)})</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <FormField id="closeNote" label={t('shift.note')}>
        <Input id="closeNote" value={closeNote} onChange={(e) => setCloseNote(e.target.value)} />
      </FormField>
      <label className="flex items-center gap-2 text-sm text-fg">
        <input
          type="checkbox"
          checked={printZ}
          onChange={(e) => {
            setPrintZ(e.target.checked);
            try { localStorage.setItem('pos.printZ', e.target.checked ? '1' : '0'); } catch { /* ignore */ }
          }}
          className="h-4 w-4 rounded border-border accent-brand"
        />
        {t('shift.printZ', { defaultValue: 'Print the close-out (Z-report) after closing' })}
      </label>
      <DialogFooter>
        <ShButton variant="ghost" onClick={cancelClose} disabled={busy}>
          {t('actions.cancel', { defaultValue: 'Cancel' })}
        </ShButton>
        <Button onClick={closeShift} loading={busy} disabled={zLoading || zFailed || closeWaiting === null || closeWaiting.length > 0}>{t('shift.closeShift')}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );

  if (terminals === null) {
    return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;
  }
  if (terminals.length === 0) {
    return (
      <EmptyState
        icon={<Monitor className="h-8 w-8" aria-hidden />}
        title={t('shift.noTerminals')}
        description={t('shift.noTerminalsDescription')}
        action={
          <Link to="/pos/terminals">
            <Button size="sm">{t('shift.setupTerminals')}</Button>
          </Link>
        }
      />
    );
  }

  // ---- pre-shift: terminal picker only ----
  if (!session) {
    return (
      <div className="flex min-h-[calc(var(--vh100)-12rem)] items-center justify-center">
        <Card className="w-full max-w-md p-8">
          <h1 className="text-xl font-semibold">{t('shift.openTitle')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('shift.openSubtitle')}</p>
          {error && (
            <div
              className="mt-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
              role="alert"
            >
              {error}
            </div>
          )}
          <div className="mt-6 flex flex-col gap-4">
            <FormField id="terminal" label={t('shift.terminal')}>
              <select
                id="terminal"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                value={selectedTerminalId}
                onChange={(e) => setSelectedTerminalId(e.target.value)}
                disabled={busy}
              >
                {terminals.map((tt) => (
                  <option key={tt.id} value={tt.id}>
                    {tt.code} — {tt.name}
                    {mayOpenTill(tt, userId) ? '' : ` · ${t('shift.notAssignedShort', { defaultValue: 'not assigned to you' })}`}
                  </option>
                ))}
              </select>
            </FormField>
            {/* What this till actually IS, before money is counted into it: its
                name and code, and the shelf its sales will leave from — a
                cashier opening the wrong till otherwise finds out at the first
                sale, when the stock has already moved. */}
            {(() => {
              const tt = terminals.find((x) => x.id === selectedTerminalId);
              if (!tt) return null;
              const whIds = tt.allWarehouses ? warehouses.map((w) => w.id) : tt.warehouseIds;
              const names = whIds.map((id) => warehouseNames.get(id) ?? id);
              const shown = tt.allWarehouses
                ? t('shift.allWarehouses', { defaultValue: 'Every warehouse' })
                : names.length > 0
                  ? names.join(' · ')
                  : warehouseNames.get(defaultWarehouseId ?? '') ?? t('shift.defaultWarehouse', { defaultValue: 'Default warehouse' });
              return (
                <dl className="rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm">
                  <div className="flex justify-between gap-3 py-0.5">
                    <dt className="text-fg-muted">{t('terminals.table.name', { defaultValue: 'Name' })}</dt>
                    <dd className="truncate font-medium">{tt.name}</dd>
                  </div>
                  <div className="flex justify-between gap-3 py-0.5">
                    <dt className="text-fg-muted">{t('terminals.table.code', { defaultValue: 'Code' })}</dt>
                    <dd className="font-mono">{tt.code}</dd>
                  </div>
                  {tt.location && (
                    <div className="flex justify-between gap-3 py-0.5">
                      <dt className="text-fg-muted">{t('terminals.table.location', { defaultValue: 'Location' })}</dt>
                      <dd className="truncate">{tt.location}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-3 py-0.5">
                    <dt className="text-fg-muted">{t('terminals.table.warehouse', { defaultValue: 'Warehouse' })}</dt>
                    <dd className="truncate text-end">{shown}</dd>
                  </div>
                  <div className="flex justify-between gap-3 py-0.5">
                    <dt className="text-fg-muted">{t('terminals.table.currency', { defaultValue: 'Currency' })}</dt>
                    <dd className="font-mono">{tt.defaultCurrencyCode}</dd>
                  </div>
                </dl>
              );
            })()}
            <FormField id="openCash" label={t('shift.openingCash')}>
              <Input
                id="openCash"
                type="number"
                step="0.01"
                min="0"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                disabled={busy}
              />
            </FormField>
            {(() => {
              const tt = terminals.find((x) => x.id === selectedTerminalId);
              return tt && !mayOpenTill(tt, userId) ? (
                <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg">
                  {t('shift.notAssigned', {
                    defaultValue: "You aren't assigned to this register. Pick one of yours, or ask a manager to add you to it on the Terminals page.",
                  })}
                </p>
              ) : null;
            })()}
            <Button
              onClick={openShift}
              loading={busy}
              disabled={!selectedTerminalId || !terminals.some((x) => x.id === selectedTerminalId && mayOpenTill(x, userId))}
            >
              {t('shift.openShift')}
            </Button>
          </div>
          {otherShifts.length > 0 && (
            <div className="mt-6 rounded-lg border border-border p-3">
              <div className="text-sm font-semibold">
                {t('shift.othersOpen', { defaultValue: 'Other shifts open on this register' })}
              </div>
              <p className="mt-0.5 text-xs text-fg-muted">
                {t('shift.othersOpenHint', {
                  defaultValue: 'Every cashier has their own shift. Close one here if its cashier has left without closing.',
                })}
              </p>
              <ul className="mt-2 flex flex-col gap-2">
                {otherShifts.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-3 rounded-md bg-bg-subtle px-3 py-2">
                    <div className="min-w-0 text-sm">
                      <div className="truncate font-medium">{o.cashierName ?? '—'}</div>
                      <div className="text-xs text-fg-muted">
                        {t('shift.othersOpenSince', {
                          defaultValue: 'Since {{date}} · {{n}} sales',
                          date: formatDateTime(o.openedAt, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'short' }),
                          n: o.salesCount ?? 0,
                        })}
                        {(o.takings ?? []).length > 0 && (
                          <> · {(o.takings ?? []).map((tk) => formatMoney(tk.total, tk.currencyCode, i18n.language)).join(' · ')}</>
                        )}
                      </div>
                    </div>
                    {canCloseShift && (
                      <ShButton variant="outline" size="sm" onClick={() => void openCloseShift(o)}>
                        <LogOut className="h-4 w-4" />
                        {t('shift.closeShift')}
                      </ShButton>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
        {closeDialog}
      </div>
    );
  }

  const total = sale?.sale.total ?? '0';
  const totalNumber = Number(total);
  // Every amount on this screen (totals, tender, change, receipt) is denominated
  // in the SALE's currency — which follows the customer's group, not the drawer.
  const currency = saleCurrency;
  const terminalCode = terminals.find((tt) => tt.id === session.terminalId)?.code ?? '—';
  // Where this till's sales leave from: terminal's pinned warehouse, else the
  // tenant default — the exact chain finalize runs server-side.
  const sellTerminal = terminals.find((tt) => tt.id === session.terminalId);
  /**
   * Which shelf this till is selling off right now.
   *
   * A till may be given several warehouses (or all of them), and the cashier
   * picks between them here — one counter serving two stores is the whole point.
   * The choice is per session and defaults to the till's own warehouse, so a
   * counter with one warehouse behaves exactly as it always has.
   */
  const allowedWarehouses = sellTerminal?.allWarehouses
    ? warehouses.map((w) => w.id)
    : sellTerminal?.warehouseIds ?? [];
  const sellWarehouseId = (pickedWarehouseId && allowedWarehouses.includes(pickedWarehouseId))
    ? pickedWarehouseId
    : sellTerminal?.warehouseId ?? defaultWarehouseId;
  const sellWarehouseName = sellWarehouseId ? warehouseNames.get(sellWarehouseId) ?? null : null;
  const canSwitchWarehouse = allowedWarehouses.length > 1;
  // Live shift duration (re-derives every clock tick via `now`).
  const shiftElapsed = formatDuration(now.getTime() - new Date(session.openedAt).getTime());
  // Close-shift drawer reconciliation — one row PER CURRENCY (expected vs counted
  // → variance). Money in different currencies is never added together, so each
  // currency gets its own expected figure, its own count field and its own
  // variance. A server without `byCurrency` (older API) collapses to the single
  // session-currency row, which is exactly how it used to render.
  const shiftCurrency = session.currencyCode;
  const walkInName = t('sale.walkIn', { defaultValue: 'Walk-in customer' });
  const customerName = (attachedParty?.displayName && plainText(attachedParty.displayName)) || walkInName;
  // Parked sales other than the one currently in the cart — the "waiting" list.
  const otherParked = parkedSales.filter((s) => s.id !== sale?.sale.id);
  // A real (non-walk-in) customer can carry a debt → sell on credit / on account.
  const canCredit = !!attachedParty && attachedParty.code !== 'WALK-IN';

  // The picker's world is ACTIVE lists only. المكاتب sits deactivated in this
  // shop; a chip for it would filter to customers whose group pricing the
  // server refuses to apply, and its section header would promise a currency
  // the sale will not run in. Customers of an inactive list are, for pricing
  // purposes, ungrouped — so that is the bucket they appear in.
  const activeLists = priceLists.filter((pl) => pl.isActive);
  const priceListById = new Map(activeLists.map((pl) => [pl.id, pl]));
  const groupKeyOf = (p: Party): string =>
    p.priceListId && priceListById.has(p.priceListId) ? p.priceListId : '__none__';
  /** The shop's anonymous counter customer — the server keeps it under code WALK-IN. */
  const walkInParty = (parties ?? []).find((p) => p.code === 'WALK-IN') ?? null;
  const customerList = (parties ?? []).filter((p) => {
    if (custGroup !== 'all' && groupKeyOf(p) !== custGroup) return false;
    const q = custQuery.trim().toLowerCase();
    return !q || p.displayName.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
  });
  // Customer groups = price lists; each carries the currency its prices are
  // quoted in — which is the currency a sale for that customer runs in. The
  // picker groups customers by their group and surfaces that currency.
  // Plain computations (not hooks) — they sit past the loading early-return and
  // the lists are small, so memoization isn't worth a rules-of-hooks hazard.
  const customerCurrency = (p: Party): string => groupCurrencyOf(p) ?? session.currencyCode;
  const hasGroups = activeLists.length > 0;
  type CustGroup = { name: string; currency: string; customers: Party[] };
  const customerGroups = ((): Array<[string, CustGroup]> => {
    const m = new Map<string, CustGroup>();
    for (const p of customerList) {
      const key = groupKeyOf(p);
      let g = m.get(key);
      if (!g) {
        const pl = p.priceListId ? priceListById.get(p.priceListId) : undefined;
        g = {
          name: pl?.name ?? t('sale.noGroup', { defaultValue: 'No group' }),
          currency: customerCurrency(p),
          customers: [],
        };
        m.set(key, g);
      }
      g.customers.push(p);
    }
    // Named groups first (A→Z), the ungrouped bucket last.
    return [...m.entries()].sort((a, b) => {
      if (a[0] === '__none__') return 1;
      if (b[0] === '__none__') return -1;
      return a[1].name.localeCompare(b[1].name);
    });
  })();
  const renderCustomerRow = (p: Party): JSX.Element => {
    const selected = sale?.sale.partyId === p.id;
    const name = plainText(p.displayName);
    return (
      <li key={p.id} className="border-b border-border/60 last:border-b-0">
        <button
          type="button"
          onClick={() => setCustomer(p.id)}
          disabled={busy}
          className={
            'flex w-full min-w-0 items-center justify-between gap-3 px-3 py-2 text-start text-sm transition ' +
            (selected ? 'bg-brand/10' : 'hover:bg-bg-subtle')
          }
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold uppercase text-brand" aria-hidden>
              {Array.from(name.trim())[0] ?? <User className="h-3.5 w-3.5" />}
            </span>
            <span className="min-w-0">
              {/* Wraps instead of clipping: an LTR company name inside an RTL row
                  loses its START to a truncating ellipsis, which is the part that
                  says who it is. */}
              <span className="line-clamp-2 break-words font-medium leading-snug text-fg">{name}</span>
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-fg-muted">
                <span className="truncate font-mono" dir="ltr">{p.code}</span>
                {p.phone && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="flex-none font-mono" dir="ltr">{p.phone}</span>
                  </>
                )}
              </span>
            </span>
          </span>
          <span className="flex flex-none items-center gap-2">
            <span className="rounded-md bg-bg-subtle px-1.5 py-0.5 text-[0.65rem] font-semibold tabular-nums text-fg-muted ring-1 ring-border/60">
              {customerCurrency(p)}
            </span>
            {selected && <Badge variant="success">{t('sale.current', { defaultValue: 'Current' })}</Badge>}
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="flex min-h-0 flex-col gap-3 overflow-hidden h-[calc(var(--vh100)-96px)]" lang={i18n.language}>
      {/* ---------- shift command bar: identity · live stats · actions ---------- */}
      <header className="flex flex-none flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-border bg-gradient-to-r from-brand/15 via-bg to-accent/10 px-4 py-2.5 shadow-sm">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2.5">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/15 text-brand shadow-inner ring-1 ring-brand/20">
              <Monitor className="h-5 w-5" aria-hidden />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight text-fg">{terminalCode}</div>
              <div className="flex items-center gap-1.5 text-xs text-fg-muted">
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                {t('shift.shiftNo', { number: session.sessionNumber, defaultValue: 'Shift #{{number}}' })}
                <span className="text-fg-subtle">·</span>
                <Timer className="h-3 w-3" aria-hidden />
                <span className="font-mono tabular-nums">{shiftElapsed}</span>
              </div>
            </div>
          </div>
          <span className="hidden h-9 w-px bg-border sm:block" aria-hidden />
          <div className="hidden leading-tight sm:block">
            <div className="text-[0.7rem] uppercase tracking-wide text-fg-muted">{t('shift.cashier', { defaultValue: 'Cashier' })}</div>
            <div className="max-w-[12rem] truncate text-sm font-medium text-fg">{cashierName}</div>
          </div>
          {/* live running shift tally */}
          <span className="hidden h-9 w-px bg-border md:block" aria-hidden />
          <button
            type="button"
            onClick={openSessionSales}
            className="hidden items-center gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-1 text-start transition hover:bg-success/15 md:flex"
            title={t('sales.sessionButton', { defaultValue: 'Sales' })}
          >
            <TrendingUp className="h-4 w-4 text-success" aria-hidden />
            <span className="leading-tight">
              {/* One figure PER currency — a shift can hold a group customer's
                  foreign-currency sale and those are never added together. */}
              <span className="block font-mono text-sm font-bold tabular-nums text-fg">
                {(shiftTotals?.totals.length
                  ? shiftTotals.totals
                  : [{ currencyCode: shiftCurrency, total: 0 }]
                ).map((x) => formatMoney(String(x.total), x.currencyCode, i18n.language)).join(' · ')}
              </span>
              <span className="block text-[0.7rem] text-fg-muted">
                {t('sales.sessionCount', { count: shiftTotals?.count ?? 0, defaultValue: '{{count}} sales' })}
              </span>
            </span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2.5 sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-fg-muted" aria-hidden />
              <span className="font-mono text-sm font-semibold tabular-nums text-fg">{clock}</span>
            </span>
            <span className="h-4 w-px bg-border" aria-hidden />
            {/* The CART's currency — highlighted when it isn't the drawer's,
                i.e. the sale follows the customer's group. */}
            <span
              className={
                'rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide ' +
                (currency === shiftCurrency
                  ? 'bg-bg-subtle text-fg-muted'
                  : 'bg-brand/15 text-brand ring-1 ring-brand/30')
              }
              title={
                currency === shiftCurrency
                  ? undefined
                  : t('sale.saleCurrencyHint', {
                      currency,
                      shiftCurrency,
                      defaultValue: 'This sale is priced in {{currency}} (the customer\'s group); the shift runs in {{shiftCurrency}}.',
                    })
              }
            >
              {currency}
            </span>
            {sellWarehouseName && (
              <>
                <span className="h-4 w-px bg-border" aria-hidden />
                {/* One warehouse: a label, as before. Several: the cashier picks,
                    because a counter serving two stores has to say which one this
                    sale leaves. The choice applies to lines added AFTER it. */}
                {canSwitchWarehouse ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setWarehouseMenuOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs font-semibold text-fg transition hover:border-brand/60"
                      title={t('sale.warehouseSwitchHint', { defaultValue: 'Choose which warehouse this till sells from.' })}
                    >
                      <WarehouseIcon className="h-4 w-4" aria-hidden />
                      <span className="max-w-[10rem] truncate">{sellWarehouseName}</span>
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    {warehouseMenuOpen && (
                      <div className="absolute z-30 mt-1 min-w-[12rem] rounded-lg border border-border bg-surface p-1 shadow-lg">
                        {allowedWarehouses.map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => { setPickedWarehouseId(id); setWarehouseMenuOpen(false); }}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition hover:bg-bg-subtle',
                              id === sellWarehouseId && 'font-semibold text-brand',
                            )}
                          >
                            <WarehouseIcon className="h-4 w-4 flex-none" aria-hidden />
                            <span className="truncate">{warehouseNames.get(id) ?? id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-fg-muted"
                    title={t('sale.warehouseHint', { defaultValue: 'Sales are deducted from this warehouse.' })}
                  >
                    <WarehouseIcon className="h-4 w-4" aria-hidden />
                    <span className="max-w-[10rem] truncate">{sellWarehouseName}</span>
                  </span>
                )}
              </>
            )}
          </div>
          {/* Full screen. A till spends all day on one page, and the browser
              chrome above it is a row of tabs nobody at a counter needs — it is
              also where a cashier accidentally navigates away mid-sale. Icon
              only: it sits beside a labelled button and the icon is universal. */}
          <ShButton
            variant="outline"
            size="sm"
            onClick={() => void toggleFullscreen()}
            title={isFullscreen
              ? t('sale.exitFullscreen', { defaultValue: 'Exit full screen' })
              : t('sale.fullscreen', { defaultValue: 'Full screen' })}
            aria-label={isFullscreen
              ? t('sale.exitFullscreen', { defaultValue: 'Exit full screen' })
              : t('sale.fullscreen', { defaultValue: 'Full screen' })}
          >
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </ShButton>
          {displayEnabled && (
            <ShButton
              variant="outline"
              size="sm"
              onClick={() => {
                // Prefer a separate window (for a 2nd monitor); if a popup blocker
                // nixes it, fall back to a plain new tab so "go there" always works.
                const w = window.open(appUrl('/pos/display'), 'erp-customer-display', 'width=1280,height=800');
                if (!w) window.open(appUrl('/pos/display'), '_blank');
              }}
              title={t('display.open', { defaultValue: 'Open customer display' })}
            >
              <Tv className="h-4 w-4" />
              {t('display.button', { defaultValue: 'Customer screen' })}
            </ShButton>
          )}
          <ShButton
            variant="outline"
            size="icon"
            className="hidden h-8 w-8 sm:inline-flex"
            onClick={() => setShortcutsOpen(true)}
            aria-label={t('shortcuts.title', { defaultValue: 'Keyboard shortcuts' })}
            title={t('shortcuts.hint', { defaultValue: 'Keyboard shortcuts (?)' })}
          >
            <Keyboard className="h-4 w-4" />
          </ShButton>
          <ShButton
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              const next = !soundMuted;
              setSoundMuted(next);
              setSoundMutedState(next);
              if (!next) playSound('tick');
            }}
            aria-label={t('shift.sound', { defaultValue: 'Sound' })}
            title={t('shift.sound', { defaultValue: 'Sound' })}
          >
            {soundMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </ShButton>
          <ShButton variant="outline" size="sm" onClick={openSessionSales}>
            <Receipt className="h-4 w-4" />
            {t('sales.sessionButton', { defaultValue: 'Sales' })}
          </ShButton>
          {/* A customer paying off their debt — taken HERE, in this shift, so it
              is in the drawer that gets counted. */}
          <ShButton variant="outline" size="sm" onClick={() => setCollectOpen(true)}>
            <Wallet className="h-4 w-4" />
            {t('collect.button', { defaultValue: 'Debt payment' })}
          </ShButton>
          <ShButton
            variant="outline"
            size="sm"
            onClick={() => (showClose ? cancelClose() : void openCloseShift())}
          >
            <LogOut className="h-4 w-4" />
            {t('shift.closeShift')}
          </ShButton>
        </div>
      </header>

      {error && (
        <div
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </div>
      )}

      {closeDialog}
      {session && (
        <CollectDebtDialog
          open={collectOpen}
          onOpenChange={setCollectOpen}
          sessionId={session.id}
          baseCurrency={baseCurrency}
          parties={parties}
          defaultPartyId={attachedParty && attachedParty.code !== 'WALK-IN' ? attachedParty.id : null}
          locale={i18n.language}
          onCollected={() => { playSound('success'); setStmtRefresh((n) => n + 1); void refreshShiftTotals(); }}
        />
      )}

      {/* ---------- two-pane register (products 25% / cart 75%) ---------- */}
      {/* Below lg the panes stack; the fixed-height parent is overflow-hidden, so the
          grid itself must scroll here or the cart + Charge button clip off-screen.
          On lg the panes sit side-by-side and scroll internally (overflow-visible).
          The cart takes three quarters by default: this till is driven by the
          scanner, so the screen that has to be readable across the counter is the
          sale, not the catalogue. The product pane stays for lookups by hand — and
          the seam between them drags, because a cashier who works the other way
          round had no way to say so. The middle `auto` track is that seam.

          The ratio rides on a custom property rather than an inline
          gridTemplateColumns, so `grid-cols-1` still governs the stacked layout
          below lg; an inline value would outrank it and produce a two-column
          grid on a phone. */}
      <div
        ref={splitRef}
        style={{ '--pos-split': `${100 - cartPct}fr auto ${cartPct}fr` } as React.CSSProperties}
        className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto lg:overflow-visible lg:grid-cols-[var(--pos-split)] lg:grid-rows-1"
      >
        {/* LEFT: items grid */}
        <Card className="flex min-h-0 flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[14rem]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
                aria-hidden
              />
              <Input
                ref={searchRef}
                value={itemQuery}
                onChange={(e) => setItemQuery(e.target.value)}
                placeholder={t('items.search')}
                aria-label={t('items.search')}
                className="ps-9"
              />
            </div>
            <div className="relative">
              <ScanLine
                className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
                aria-hidden
              />
              <Input
                ref={scanRef}
                value={scanText}
                onChange={(e) => setScanText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && scanText.trim()) {
                    void addScanned(scanText.trim());
                    setScanText('');
                  }
                }}
                placeholder={t('sale.scanPlaceholder')}
                aria-label={t('sale.scanPlaceholder')}
                className="w-64 ps-9 font-mono text-xs"
              />
            </div>
            {/* Quick-add posts to /items, which needs items.write. A cashier
                without it used to see the button and get a 403 on submit. */}
            {canAddProduct && (
              <ShButton variant="outline" size="sm" onClick={() => setNewProdOpen(true)}>
                <PackagePlus className="h-4 w-4" />
                {t('items.newProduct', { defaultValue: 'New product' })}
              </ShButton>
            )}
            <ShButton
              variant="outline"
              size="sm"
              onClick={() => setShowCustomForm((s) => !s)}
            >
              {t('sale.customAdd')}
            </ShButton>
          </div>

          {/* browse tabs: category / brand / featured */}
          <div className="-mx-1 flex flex-nowrap items-center gap-1.5 overflow-x-auto px-1 pb-0.5">
            {(['category', 'brand', 'featured'] as const).map((tab) => (
              <CategoryChip
                key={tab}
                active={browseTab === tab}
                onClick={() => { setBrowseTab(tab); setDrillId(null); }}
              >
                <span className="inline-flex items-center gap-1.5">
                  {tab === 'featured' ? (
                    <Star className={`h-3 w-3 ${browseTab === 'featured' ? 'fill-current' : ''}`} aria-hidden />
                  ) : tab === 'brand' ? (
                    <Tag className="h-3 w-3" aria-hidden />
                  ) : (
                    <FolderTree className="h-3 w-3" aria-hidden />
                  )}
                  {t(`browse.${tab}`, { defaultValue: tab })}
                </span>
              </CategoryChip>
            ))}
          </div>

          {showCustomForm && (
            <div className="rounded-md border border-border-subtle bg-bg-subtle/40 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
                <FormField id="customDesc" label={t('sale.addPlaceholder')}>
                  <Input
                    id="customDesc"
                    value={customDesc}
                    onChange={(e) => setCustomDesc(e.target.value)}
                  />
                </FormField>
                <FormField id="customQty" label={t('sale.addQty')}>
                  <Input
                    id="customQty"
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={customQty}
                    onChange={(e) => setCustomQty(e.target.value)}
                  />
                </FormField>
                <FormField id="customPrice" label={t('sale.addPrice')}>
                  <Input
                    id="customPrice"
                    inputMode="decimal"
                    value={customPrice}
                    onChange={(e) => {
                      // Cap to the sale currency's decimals (IQD→0).
                      const dp = getCurrencyDecimals(currency) ?? 2;
                      let v = e.target.value.replace(/[^0-9.]/g, '');
                      const i = v.indexOf('.');
                      if (i >= 0) v = dp === 0 ? v.slice(0, i) : v.slice(0, i + 1 + dp);
                      setCustomPrice(v);
                    }}
                  />
                </FormField>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    onClick={addCustomLine}
                    disabled={!customDesc.trim() || !customPrice || busy}
                  >
                    {t('sale.addButton')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {items === null ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-36" />
                ))}
              </div>
            ) : shownItems !== null ? (
              // flat item list: search results / featured / drilled category|brand
              <div className="flex min-h-full flex-col">
                {drillId && !itemQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => setDrillId(null)}
                    className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
                  >
                    <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                    {drillTitle}
                  </button>
                )}
                {pagedItems && pagedItems.length > 0 ? (
                  <>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
                      {pagedItems.map((it) => (
                        <ItemTile
                          key={it.id}
                          item={it}
                          locale={i18n.language}
                          enabled={canFiles}
                          baseCurrency={baseCurrency}
                          saleCurrency={currency}
                          groupPrice={tilePrices.has(it.id) ? tilePrices.get(it.id) ?? null : undefined}
                          onAdd={addItem}
                          onInfo={openDetail}
                        />
                      ))}
                    </div>
                    {pageCount > 1 && <Pager page={curPage} pageCount={pageCount} onPage={setPage} />}
                  </>
                ) : (
                  <EmptyState title={items.length === 0 ? t('items.noneSellable') : t('items.noResults')} />
                )}
              </div>
            ) : (browseTab === 'brand' ? brandCards : categoryCards).length === 0 ? (
              <EmptyState title={t('items.noResults')} />
            ) : (
              // category / brand image cards → tap to drill in
              <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
                {(browseTab === 'brand' ? brandCards : categoryCards).map((c) => (
                  <BrowseCard
                    key={c.id}
                    entityType={browseTab === 'brand' ? 'brand' : 'category'}
                    id={c.id}
                    name={c.name}
                    count={c.count}
                    enabled={canFiles}
                    onClick={() => setDrillId(c.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>

        <PaneDivider
          containerRef={splitRef}
          value={cartPct}
          onChange={setCartPct}
          onReset={() => setCartPct(CART_PCT_DEFAULT)}
          label={t('sale.resizePanes', { defaultValue: 'Drag to resize — double-click to reset' })}
        />

        {/* RIGHT: cart + checkout */}
        <Card className="flex h-full min-h-0 flex-col p-0">
          <div className="flex items-center justify-between border-b border-border bg-bg-subtle/30 px-4 py-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">
              <ShoppingCart className="h-4 w-4" aria-hidden />
              {t('sale.title')}
              {sale && sale.lines.length > 0 && (
                <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-brand px-1.5 text-[0.7rem] font-bold tabular-nums text-brand-contrast">
                  {sale.lines.length}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-0.5">
              {otherParked.length > 0 && (
                <ShButton
                  variant="ghost"
                  size="sm"
                  onClick={() => { void loadParked(); setParkedOpen(true); }}
                  className="gap-1 text-fg-muted hover:text-brand"
                  title={t('sale.parkedTitle', { defaultValue: 'Waiting sales' })}
                >
                  <Inbox className="h-4 w-4" />
                  <span className="grid h-4 min-w-[1rem] place-items-center rounded-full bg-brand px-1 text-[0.65rem] font-bold text-brand-contrast">
                    {otherParked.length}
                  </span>
                </ShButton>
              )}
              {sale && sale.sale.status === 'parked' && sale.lines.length > 0 && (
                <>
                  <ShButton
                    variant="ghost"
                    size="sm"
                    onClick={() => { setHoldLabelInput(''); setHoldOpen(true); }}
                    disabled={busy}
                    className="gap-1 text-fg-muted hover:text-brand"
                    title={t('sale.hold', { defaultValue: 'Hold sale' })}
                  >
                    <Pause className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('sale.hold', { defaultValue: 'Hold' })}</span>
                  </ShButton>
                  <ShButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setClearOpen(true)}
                    disabled={busy}
                    className="gap-1 text-fg-muted hover:text-danger"
                    title={t('sale.clearAll', { defaultValue: 'Clear all items' })}
                  >
                    <Eraser className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('sale.clear', { defaultValue: 'Clear' })}</span>
                  </ShButton>
                  <ShButton
                    variant="ghost"
                    size="sm"
                    onClick={cancelCart}
                    disabled={busy}
                    className="text-fg-muted hover:text-danger"
                    title={t('sale.voidCart')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </ShButton>
                </>
              )}
            </div>
          </div>

          {/* Customer, their pricing group and their account balance on ONE band.
              They are three facts about the same person, and stacking them cost
              three rows of a pane whose whole job is showing the sale. The
              informational pricing note becomes a chip; the MISMATCH case keeps
              its own full band below, because that one is a warning with an
              action attached and must not be shrunk into a badge. */}
          <div className="flex items-center gap-2 border-b border-border px-4 py-1.5">
            <button
              type="button"
              onClick={() => { setCustQuery(''); setCustGroup('all'); setCustOpen(true); }}
              disabled={sale?.sale.status === 'finalized'}
              className="flex min-w-0 flex-1 items-center gap-2.5 text-start outline-none transition hover:opacity-80 disabled:opacity-60"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
                <User className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-fg">{customerName}</span>
                <span className="block text-xs text-fg-muted">
                  {t('sale.changeCustomer', { defaultValue: 'Tap to change customer' })}
                </span>
              </span>
              <UserPlus className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
            </button>

            {/* Priced in the customer's own group currency — the normal case, so
                it states itself in a chip rather than a sentence across a row. */}
            {attachedGroupCurrency && attachedGroupCurrency === currency && (
              <span
                className="hidden max-w-[14rem] flex-none items-center gap-1 truncate rounded-md bg-brand/10 px-2 py-1 text-xs font-medium text-brand sm:inline-flex"
                title={t('sale.groupCurrencyActive', {
                  currency,
                  group: attachedParty?.priceListName ?? '',
                  defaultValue: 'Priced in {{currency}} from this customer\'s group ({{group}}).',
                })}
              >
                <Coins className="h-3.5 w-3.5 flex-none" aria-hidden />
                <span className="truncate">{currency}{attachedParty?.priceListName ? ` · ${attachedParty.priceListName}` : ''}</span>
              </span>
            )}

            {/* The day this sale is booked on. Today unless a keeper picks another
                (needs pos.sale.date); a back-dated one is called out so a stray
                old date can never ring up the next sale unnoticed. */}
            {canDate && (!sale || sale.sale.status === 'parked') && (
              <label
                className={cn(
                  'inline-flex flex-none items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition',
                  backDated ? 'border-warning bg-warning/10 text-warning' : 'border-border text-fg-muted hover:bg-bg-subtle',
                )}
                title={t('sale.date.hint', { defaultValue: 'The day this sale is booked on — its invoice, payment and cost all post on it.' })}
              >
                <CalendarDays className="h-3.5 w-3.5 flex-none" aria-hidden />
                <span className="sr-only">{t('sale.date.label', { defaultValue: 'Sale date' })}</span>
                <input
                  type="date"
                  dir="ltr"
                  value={saleDay}
                  max={dayStr(1)}
                  disabled={busy}
                  onChange={(e) => void changeSaleDay(e.target.value)}
                  className="w-[7.5rem] bg-transparent font-mono text-xs outline-none disabled:opacity-60"
                  aria-label={t('sale.date.label', { defaultValue: 'Sale date' })}
                />
              </label>
            )}

            {/* customer account: balance/debt + their profile & sales — always
                offered for a real customer, even while the statement loads */}
            {attachedParty && attachedParty.code !== 'WALK-IN' && (() => {
              const owing = custStatement?.summary.filter((s) => Number(s.balance) > 0.0001) ?? [];
              const head = owing[0];
              return (
                <button
                  type="button"
                  onClick={() => setAccountOpen(true)}
                  title={t('sale.account', { defaultValue: 'Account' })}
                  className="inline-flex flex-none items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs transition hover:bg-bg-subtle"
                >
                  <Wallet className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                  <span className={`font-mono font-semibold ${!custStatement ? 'text-fg-muted' : head ? 'text-warning' : 'text-success'}`}>
                    {!custStatement
                      ? t('sale.account', { defaultValue: 'Account' })
                      : head
                        ? `${t('sale.owes', { defaultValue: 'Owes' })} ${formatMoney(head.balance, head.currencyCode, i18n.language)}${owing.length > 1 ? '…' : ''}`
                        : t('sale.settled', { defaultValue: 'No balance' })}
                  </span>
                </button>
              );
            })()}
          </div>

          {/* The one case that keeps a row of its own: the cart is running in a
              different currency from the customer's group — a tender was taken
              first, or a re-price was refused — so the cashier is offered the
              switch rather than left to charge the wrong currency. */}
          {attachedGroupCurrency && attachedGroupCurrency !== currency && (
            (
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-warning/10 px-4 py-2 text-xs text-warning">
                <Info className="h-3.5 w-3.5 flex-none" aria-hidden />
                <span className="min-w-0 flex-1">
                  {t('sale.groupCurrencyMismatch', {
                    currency: attachedGroupCurrency,
                    saleCurrency: currency,
                    defaultValue: "This customer's prices are in {{currency}}, but the cart is in {{saleCurrency}}.",
                  })}
                </span>
                <ShButton
                  variant="outline"
                  size="sm"
                  className="h-7 flex-none"
                  disabled={busy || sale?.sale.status !== 'parked'}
                  onClick={() => void setSaleCurrency(attachedGroupCurrency)}
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  {t('sale.switchCurrency', { currency: attachedGroupCurrency, defaultValue: 'Switch to {{currency}}' })}
                </ShButton>
              </div>
            )
          )}

          {/* lines — min-h-0 lets this flex item shrink + scroll so the
              totals/charge footer below stays pinned and always visible */}
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
            {!sale || sale.lines.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <span className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-brand/15 to-accent/10 text-brand ring-1 ring-brand/15">
                  <ShoppingCart className="h-9 w-9" aria-hidden />
                </span>
                <p className="text-sm font-medium text-fg">{t('sale.emptyCart')}</p>
                <p className="max-w-[15rem] text-xs text-fg-muted">
                  {t('sale.emptyCartHint', { defaultValue: 'Scan a barcode or tap a product to start a sale.' })}
                </p>
              </div>
            ) : (
              <>
              {/* Column header. The rows below are a table in everything but
                  markup: every cell is a direct flex child with a FIXED width,
                  so the unit, quantity and price line up down the cart instead
                  of drifting with the length of each product name. */}
              {!cartStacked && (
              <div className="mb-1 flex items-center gap-2 px-3 text-[0.65rem] font-semibold uppercase tracking-wide text-fg-muted">
                <span className="min-w-0 flex-1">{t('sale.colProduct', { defaultValue: 'Product' })}</span>
                {!cartTight && <span className="w-28">{t('sale.colBarcode', { defaultValue: 'Barcode' })}</span>}
                <span className="w-24">{t('sale.colUnit', { defaultValue: 'Unit' })}</span>
                <span className="w-[104px] text-center">{t('sale.colQty', { defaultValue: 'Qty' })}</span>
                <span className="w-[132px]">{t('sale.colPrice', { defaultValue: 'Price' })}</span>
                <span className="w-[116px] text-end">{t('sale.colTotal', { defaultValue: 'Total' })}</span>
                <span className="w-8" aria-hidden />
              </div>
              )}
              <ul className="flex flex-col gap-1.5">
                {/* Newest line on top — most-recently added item shows first. */}
                {[...sale.lines].reverse().map((l) => {
                  const lineItem = l.itemId ? itemById.get(l.itemId) : undefined;
                  // THE CODE ON THE BOX THAT WAS SOLD. A carton carries its own
                  // label, so showing the item's primary on a carton line reads
                  // back a different package at a different price to a customer
                  // querying it — and it is the one number a cashier uses to
                  // check the line against what is in front of them. Falls back
                  // to the item's own when a pack has no label of its own.
                  const lineUnit = l.itemUnitId
                    ? lineItem?.units.find((u) => u.id === l.itemUnitId)
                    : undefined;
                  const lineBarcode = lineUnit?.barcode ?? lineItem?.barcode ?? null;
                  // Sold to THIS customer before — the row goes red and carries
                  // their last price, so the cashier sees the history at a glance.
                  const lastSale = lastPriceOf(l.itemId, l.itemUnitId);
                  return (
                  <li
                    key={l.id}
                    className={
                      // ONE row per line. The cart owns three quarters of the
                      // screen now, so the identity and the controls fit side by
                      // side and the vertical space buys visible lines instead.
                      // flex-wrap keeps it honest on a narrow window: the control
                      // group drops to a second row rather than overflowing.
                      'flex flex-wrap items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm transition-colors duration-300 ' +
                      (flashLineNo === l.lineNo
                        ? 'border-brand bg-brand/15'
                        : lastSale
                          ? 'border-danger/50 bg-danger/10 hover:border-danger'
                          : 'border-border bg-bg-subtle/50 hover:border-border-strong')
                    }
                  >
                    {l.itemId ? (
                      <button
                        type="button"
                        onClick={() => void openLineDetail(l)}
                        className="group flex min-w-0 flex-1 items-center gap-2 text-start outline-none"
                        title={t('sale.viewDetails', { defaultValue: 'View product details' })}
                      >
                        <ItemThumb itemId={l.itemId} seed={l.itemId ?? l.description ?? 'x'} enabled={canFiles} className="h-8 w-8 flex-none" />
                        <span className="truncate text-sm font-semibold text-fg transition-colors group-hover:text-brand group-hover:underline">
                          {l.description ?? '—'}
                        </span>
                      </button>
                    ) : (
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <ItemThumb itemId={l.itemId} seed={l.itemId ?? l.description ?? 'x'} enabled={canFiles} className="h-8 w-8 flex-none" />
                        <span className="truncate text-sm font-semibold text-fg">{l.description ?? '—'}</span>
                      </div>
                    )}
                    {/* Barcode. Its own column rather than trailing the name: it
                        is what the cashier reads back to a customer querying a
                        line, and a name long enough to truncate would eat it.
                        Hidden below md, where the width simply is not there. */}
                    {!cartTight && (
                      <span className="w-28 truncate font-mono text-[0.7rem] tabular-nums text-fg-muted" title={lineBarcode ?? undefined}>
                        {lineBarcode ?? '—'}
                      </span>
                    )}
                    {/* The controls, as ONE block. Individually they are flex
                        children with fixed widths, so a wrapping row broke them
                        apart a cell at a time and left the row a ragged stack.
                        Grouped, they either sit inline or take a line of their
                        own — the two states worth having. */}
                    <div className={'flex items-center gap-x-2 ' + (
                      // Wrapping among themselves too: the cells are fixed-width
                      // and flex-none, so a full-width row narrower than their
                      // sum (~510px) overflowed the card rather than reflowing.
                      cartStacked ? 'w-full flex-wrap justify-between gap-y-1.5' : 'flex-none')}>
                    {sale.sale.status === 'parked' ? (
                      <>
                        {/* unit selector + pack size (pieces per pack), beside the qty */}
                        {(() => {
                          const item = lineItem;
                          const multi = !!item && item.units.length > 0;
                          const factor = Number(l.unitFactor || '1');
                          const unitLabel = l.unitName ?? t('sale.baseUnit', { defaultValue: 'Single' });
                          if (!multi && !l.unitName) return <span className="w-24 flex-none" aria-hidden />;
                          return (
                            // Sized by its content (at least the old 6rem, so short rows still line
                            // up). It used to be a fixed 6rem that clipped: «باكيت ▾» filled it and
                            // the pack size beside it was cut to a stray «1×» — a cashier could not
                            // see that a packet is 10 pieces.
                            <div className="flex min-w-24 flex-none items-center gap-1">
                              {multi ? (
                                <button
                                  type="button"
                                  onClick={() => openUnitSwitch(l)}
                                  disabled={busy}
                                  className="inline-flex h-8 items-center gap-1 rounded-md border border-brand/40 bg-brand/10 px-2 text-xs font-semibold text-brand transition hover:bg-brand/20 disabled:opacity-50"
                                  title={t('sale.changeUnit', { defaultValue: 'Change unit' })}
                                >
                                  <Package className="h-3 w-3" aria-hidden />
                                  {unitLabel}
                                  <ChevronDown className="h-3 w-3" aria-hidden />
                                </button>
                              ) : (
                                <span className="inline-flex h-8 items-center rounded-md bg-brand/10 px-2 text-xs font-semibold text-brand">
                                  {l.unitName}
                                </span>
                              )}
                              {factor > 1 && (
                                // What the pack holds, in the product's own base unit: «10 قطعة».
                                <span
                                  className="inline-flex h-8 items-center whitespace-nowrap rounded-md bg-bg-subtle px-1.5 text-xs font-semibold tabular-nums text-fg"
                                  title={t('sale.packSize', { defaultValue: 'Pieces per pack' })}
                                >
                                  {trimNum(String(factor))} {item?.unitOfMeasure || t('sale.baseUnit', { defaultValue: 'Single' })}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {/* quantity: − [input] + */}
                        <div className="inline-flex flex-none items-center overflow-hidden rounded-md border border-border-strong bg-bg">
                          <button
                            type="button"
                            onClick={() => changeLineQty(l.lineNo, l.quantity, -1)}
                            disabled={busy}
                            aria-label={t('sale.remove')}
                            className="grid h-8 w-8 place-items-center text-fg-muted transition hover:bg-brand/10 hover:text-brand disabled:opacity-50"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <LineNumberInput
                            value={l.quantity}
                            allowNegative={canRefund}
                            allowZero
                            onCommit={(v) => updateLineField(l.lineNo, { quantity: v })}
                            disabled={busy}
                            ariaLabel={t('sale.addQty')}
                            className="h-8 w-11 border-x border-border-strong bg-transparent text-center text-sm font-semibold tabular-nums text-fg outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => changeLineQty(l.lineNo, l.quantity, 1)}
                            disabled={busy}
                            aria-label={t('sale.addButton')}
                            className="grid h-8 w-8 place-items-center text-fg-muted transition hover:bg-brand/10 hover:text-brand disabled:opacity-50"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {/* His last price, tap to apply — shown only for a repeat item. */}
                        {lastSale && (
                          <button
                            type="button"
                            onClick={() => {
                              if (trimNum(l.unitPrice) !== trimNum(lastSale.unitPrice)) {
                                void updateLineField(l.lineNo, { unitPriceOverride: lastSale.unitPrice });
                              }
                            }}
                            disabled={busy}
                            title={t('sale.lastSoldHint', {
                              price: formatMoney(lastSale.unitPrice, lastSale.currencyCode, i18n.language),
                              date: formatDateTime(lastSale.soldAt, { locale: i18n.language }, { dateStyle: 'medium' }),
                              doc: lastSale.docNumber,
                              defaultValue: 'Last sold to this customer for {{price}} on {{date}} ({{doc}}) — tap to apply',
                            })}
                            className="inline-flex h-8 flex-none items-center gap-1 rounded-md border border-danger/40 bg-danger/10 px-1.5 text-[0.65rem] font-bold tabular-nums text-danger transition hover:bg-danger/20 disabled:opacity-50"
                          >
                            <History className="h-3 w-3" aria-hidden />
                            {trimNum(lastSale.unitPrice)}
                          </button>
                        )}
                        {/* editable unit price — red when it carries the customer's history */}
                        <div className={'inline-flex w-[132px] flex-none items-center rounded-md border ' + (
                          lastSale ? 'border-danger/60 bg-danger/10' : 'border-border-strong bg-bg')}>
                          <LineNumberInput
                            value={l.unitPrice}
                            onCommit={(v) => updateLineField(l.lineNo, { unitPriceOverride: v })}
                            disabled={busy}
                            // A price of zero is a real choice at a counter — a gift, a
                            // sample, a replacement — and is honoured: the goods leave
                            // the shelf and the customer gets an invoice for nothing.
                            allowZero
                            ariaLabel={t('sale.addPrice')}
                            className={'h-8 w-full min-w-0 bg-transparent px-2 text-sm tabular-nums outline-none ' + (
                              lastSale ? 'font-semibold text-danger' : 'text-fg')}
                          />
                          <span className="pe-2 text-[0.65rem] font-medium text-fg-muted">{currency}</span>
                        </div>
                      </>
                    ) : (
                      <span className="w-[calc(6rem+104px+132px+1rem)] flex-none truncate text-xs text-fg-muted">
                        {l.unitName ? `${l.unitName}${Number(l.unitFactor || '1') > 1 ? ` ×${trimNum(l.unitFactor || '1')}` : ''} · ` : ''}
                        {t('sale.lineSummary', { quantity: trimNum(l.quantity), unitPrice: trimNum(l.unitPrice) })}
                      </span>
                    )}
                    {/* The line's money sits at the end, next to the only
                        destructive control — the two things a cashier looks for
                        when a customer changes their mind. */}
                    {/* A return reads as one at a glance. The money is already
                        negative and formatMoney shows the sign, but a cashier
                        scanning a busy cart reads COLOUR before digits, and
                        mistaking a return for a sale is a drawer that does not
                        balance at close. */}
                    <span
                      className={'w-[116px] flex-none text-end font-mono text-sm font-bold tabular-nums '
                        + (Number(l.quantity) < 0 ? 'text-danger'
                          : Number(l.quantity) === 0 ? 'text-fg-muted' : 'text-fg')}
                      title={Number(l.quantity) < 0
                        ? t('sale.returnedLine', { defaultValue: 'Returned — comes off this sale and goes back to stock' })
                        : Number(l.quantity) === 0
                          ? t('sale.priceOnlyLine', { defaultValue: 'Price only — shown to the customer, nothing charged and nothing leaves the shelf' })
                          : undefined}
                    >
                      {/* A price-only line shows the PRICE, which is the whole
                          point of it — a column of 0.00 would hide the one
                          number the customer is standing there to see. */}
                      {Number(l.quantity) === 0
                        ? formatMoney(l.unitPrice, currency, i18n.language)
                        : formatMoney(l.lineTotal, currency, i18n.language)}
                    </span>
                    {sale.sale.status === 'parked' && (
                      <button
                        type="button"
                        onClick={() => removeLine(l.lineNo)}
                        disabled={busy}
                        aria-label={t('sale.remove')}
                        className="grid h-8 w-8 flex-none place-items-center rounded-md border border-danger/40 bg-danger/5 text-danger transition hover:bg-danger/15 disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {sale.sale.status !== 'parked' && <span className="w-8 flex-none" aria-hidden />}
                    </div>
                  </li>
                  );
                })}
              </ul>
              </>
            )}
          </div>

          {/* totals + CTA. Squeezed vertically on purpose: this block sits under
              the cart, so every pixel it takes is a cart line the cashier cannot
              see. The pane is wide now, so coupon and discount sit SIDE BY SIDE
              rather than stacked, and the running totals share a row with the
              grand total instead of a stack plus a separator. */}
          <div className="border-t border-border bg-bg-subtle/40 px-4 py-2">
            {/* One physical row. flex-1 alone let each block's intrinsic content
                width decide the basis, and the two of them overflowed the row and
                wrapped into a stack — basis-0 + min-w-0 makes them genuinely
                split it, whatever their inputs want. */}
            {/* EVERYTHING on one row: coupon and discount grow from a 12rem
                basis (so only a genuinely narrow window wraps them), and the
                two money columns sit flex-none at the row's end — subtotal
                first, then the grand total. Four things, one line. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {/* coupon */}
            {sale && sale.sale.status === 'parked' && (
              <div className="min-w-0 grow basis-[12rem]">
                {sale.sale.couponCode ? (
                  <div
                    className={
                      'flex items-center justify-between rounded-md border px-2 py-1.5 text-sm ' +
                      (Number(sale.sale.discountTotal) > 0
                        ? 'border-success/40 bg-success/10'
                        : 'border-border bg-bg-subtle')
                    }
                  >
                    <span className="flex min-w-0 items-center gap-1.5 font-medium">
                      <Ticket className="h-4 w-4 flex-none" aria-hidden />
                      <span className="truncate">{sale.sale.couponCode}</span>
                      {Number(sale.sale.discountTotal) === 0 && (
                        <span className="text-xs font-normal text-fg-muted">
                          · {t('sale.couponNoDiscount', { defaultValue: 'no discount' })}
                        </span>
                      )}
                    </span>
                    <ShButton
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={removeCoupon}
                      disabled={busy}
                      aria-label={t('sale.removeCoupon', { defaultValue: 'Remove coupon' })}
                    >
                      <X className="h-3.5 w-3.5" />
                    </ShButton>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); void applyCoupon(); }
                      }}
                      placeholder={t('sale.couponPlaceholder', { defaultValue: 'Coupon code' })}
                      aria-label={t('sale.couponPlaceholder', { defaultValue: 'Coupon code' })}
                      className="h-8 text-sm"
                    />
                    <ShButton
                      variant="outline"
                      size="sm"
                      onClick={applyCoupon}
                      disabled={!couponCode.trim() || busy}
                    >
                      <Ticket className="h-4 w-4" />
                      {t('sale.applyCoupon', { defaultValue: 'Apply' })}
                    </ShButton>
                  </div>
                )}
              </div>
            )}
            {/* manual discount — hidden while a coupon drives the discount slot */}
            {sale && sale.sale.status === 'parked' && !sale.sale.couponCode && (
              <div className="min-w-0 grow basis-[12rem]">
                {Number(sale.sale.discountTotal) > 0 ? (
                  <div className="flex items-center justify-between rounded-md border border-success/40 bg-success/10 px-2 py-1.5 text-sm">
                    <span className="flex min-w-0 items-center gap-1.5 font-medium">
                      <Percent className="h-4 w-4 flex-none" aria-hidden />
                      {t('sale.discount')}
                      <span className="font-mono tabular-nums">
                        −{formatMoney(sale.sale.discountTotal, currency, i18n.language)}
                      </span>
                    </span>
                    <ShButton
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={clearDiscount}
                      disabled={busy}
                      aria-label={t('sale.removeDiscount', { defaultValue: 'Remove discount' })}
                    >
                      <X className="h-3.5 w-3.5" />
                    </ShButton>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <select
                      value={discountKind}
                      onChange={(e) => setDiscountKind(e.target.value as 'percent' | 'fixed')}
                      aria-label={t('sale.discount')}
                      className="h-8 shrink-0 rounded-md border border-border bg-bg px-2 text-sm text-fg"
                    >
                      <option value="fixed">{currency}</option>
                      <option value="percent">%</option>
                    </select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void applyDiscount(); } }}
                      placeholder={t('sale.discountValue', { defaultValue: 'Discount' })}
                      aria-label={t('sale.discountValue', { defaultValue: 'Discount' })}
                      className="h-8 text-sm"
                    />
                    <ShButton
                      variant="outline"
                      size="sm"
                      onClick={applyDiscount}
                      disabled={!discountValue.trim() || busy}
                    >
                      <Percent className="h-4 w-4" />
                      {t('sale.applyDiscount', { defaultValue: 'Discount' })}
                    </ShButton>
                  </div>
                )}
              </div>
            )}
            {/* note on the sale — printed on its invoice */}
            {sale && sale.sale.status === 'parked' && (
              <div className="relative min-w-0 grow basis-[12rem]">
                <StickyNote className="pointer-events-none absolute start-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden />
                <Input
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onBlur={() => void saveNote()}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveNote(); } }}
                  maxLength={2000}
                  placeholder={t('sale.notesPlaceholder', { defaultValue: 'Note on this sale' })}
                  aria-label={t('sale.notesPlaceholder', { defaultValue: 'Note on this sale' })}
                  className="h-8 ps-8 text-sm"
                />
              </div>
            )}

              <span className="ms-auto flex flex-none items-baseline gap-x-3 text-xs text-fg-muted">
                <span>
                  {t('sale.subtotal')}{' '}
                  <span className="font-mono tabular-nums text-fg">
                    {formatMoney(sale?.sale.subtotal ?? '0', currency, i18n.language)}
                  </span>
                </span>
                {Number(sale?.sale.taxTotal ?? '0') > 0 && (
                  <span>
                    {t('sale.tax')}{' '}
                    <span className="font-mono tabular-nums text-fg">
                      {formatMoney(sale?.sale.taxTotal ?? '0', currency, i18n.language)}
                    </span>
                  </span>
                )}
                {Number(sale?.sale.discountTotal ?? '0') > 0 && (
                  <span className="text-success">
                    {t('sale.discount')}{' '}
                    <span className="font-mono tabular-nums">
                      −{formatMoney(sale?.sale.discountTotal ?? '0', currency, i18n.language)}
                    </span>
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              {/* The number and the button that takes it, side by side — the
                  cashier's eye lands once. */}
              <span className="flex flex-none items-baseline gap-2 pe-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('sale.total')}</span>
                <span
                  key={total}
                  className="font-mono text-[1.6rem] font-extrabold leading-none tabular-nums text-fg duration-200 animate-in fade-in zoom-in-95"
                >
                  {formatMoney(total, currency, i18n.language)}
                </span>
              </span>
              {/* The paper BEFORE the money: opens the sale's PDF in a tab so the
                  cashier sees it and prints from the viewer. The endpoint has no
                  status gate, so a parked sale renders like any other — which is
                  the point: the customer reads the invoice, then pays. Fetched as
                  a blob because a bare window.open cannot carry the bearer token. */}
              <Button
                variant="secondary"
                className="h-12 rounded-xl px-4"
                disabled={!sale || sale.lines.length === 0}
                onClick={printBeforePay}
                title={t('sale.printBeforeHint', { defaultValue: 'Show and print the invoice before payment' })}
              >
                <Printer className="h-5 w-5" aria-hidden />
                {t('sale.printBefore', { defaultValue: 'Print' })}
              </Button>
              <Button
                className="h-12 flex-1 rounded-xl bg-gradient-to-r from-brand via-brand to-accent text-base font-bold shadow-md transition-transform hover:opacity-95 active:scale-[0.99] disabled:from-fg-muted disabled:to-fg-muted disabled:opacity-40"
                disabled={!sale || sale.sale.status !== 'parked' || sale.lines.length === 0}
                onClick={() => setTenderOpen(true)}
              >
                <CreditCard className="h-5 w-5" aria-hidden />
                {totalNumber > 0
                  ? t('sale.charge', { amount: formatMoney(total, currency, i18n.language) })
                  : t('sale.chargeZero')}
              </Button>
            </div>
            {sale && sale.sale.status === 'finalized' && (
              <ReceiptStrip
                sale={sale.sale}
                onNewSale={startNewSale}
                customerPhone={parties?.find((p) => p.id === sale.sale.partyId)?.phone ?? null}
                waVars={{
                  company: companyName,
                  customer: customerName,
                  docNumber: sale.sale.saleNumber,
                  total: formatMoney(sale.sale.total, currency, i18n.language),
                  currency,
                }}
              />
            )}
          </div>
        </Card>
      </div>

      {/* ---------- tender dialog ---------- */}
      {sale && (
        <TenderDialog
          note={noteDraft}
          onNoteChange={setNoteDraft}
          open={tenderOpen}
          onOpenChange={(open) => {
            setTenderOpen(open);
            // A fresh attempt starts clean — yesterday's refusal must not sit
            // in the dialog looking like it refers to THIS payment.
            if (open) setError(null);
            // Dismissing the receipt after a finalized sale → wipe the cart and
            // open a fresh empty sale, ready for the next customer.
            if (!open && sale.sale.status === 'finalized') {
              void startNewSale();
            }
          }}
          sale={sale}
          locale={i18n.language}
          baseCurrency={baseCurrency}
          party={attachedParty ?? null}
          statement={custStatement}
          onPay={async (p) => {
            setBusy(true); setError(null);
            let addedNo: number | null = null;
            try {
              await saveNote();
              if (p.tender) {
                const body: Record<string, unknown> = { kind: p.tender.kind, amount: p.tender.amount };
                if (p.tender.reference) body.reference = p.tender.reference;
                const r = await api().post<SaleResponse>(`/pos/sales/${sale.sale.id}/tenders`, body);
                setSale(r.data);
                addedNo = Math.max(...(r.data.tenders ?? []).map((tn) => tn.tenderNo));
              }
              if (p.finalize) {
                const r = await api().post<{ sale: SaleResponse; warnings: unknown[] }>(
                  `/pos/sales/${sale.sale.id}/finalize`,
                  { onCredit: p.onCredit ?? false, ...(p.cashExchange ? { cashExchange: p.cashExchange } : {}) },
                );
                setSale(r.data.sale);
                // Paid = out of Waiting, NOW — the row must not linger even for one slow round trip.
                setParkedSales((rs) => rs.filter((x) => x.id !== r.data.sale.sale.id));
                playSound('success');
                void refreshShiftTotals();
              }
            } catch (e) {
              // The tender this attempt added comes off again, so a retry starts
              // from the same cart — and cash in other notes isn't left as a
              // tender with nothing to say what notes it was.
              if (addedNo !== null && Number.isFinite(addedNo)) {
                try {
                  const r = await api().delete<SaleResponse>(`/pos/sales/${sale.sale.id}/tenders/${addedNo}`);
                  setSale(r.data);
                } catch { /* the error below is the one to show */ }
              }
              const prob = problemOf(e) as { code?: string; cause?: { code?: string } };
              const cause = prob.cause?.code;
              setError(prob.code === 'currency.rate_not_found'
                ? t('sale.rateNotFound', {
                    currency,
                    baseCurrency,
                    defaultValue: 'No {{currency}} → {{baseCurrency}} exchange rate for today, so this sale can\'t be posted. Ask a manager to add it in Settings → Currencies, then finalize again.',
                  })
                : cause === 'cash_exchange_short'
                  ? t('tender.exchangeShort', { defaultValue: 'Those notes don\'t cover the sale at today\'s rate — check the amount and the currency.' })
                  : friendlyError(e));
              playSound('error');
            } finally {
              setBusy(false);
            }
          }}
          onRemoveTender={async (tenderNo) => {
            setBusy(true); setError(null);
            try {
              const r = await api().delete<SaleResponse>(
                `/pos/sales/${sale.sale.id}/tenders/${tenderNo}`,
              );
              setSale(r.data);
            } catch (e) {
              setError(friendlyError(e));
            } finally {
              setBusy(false);
            }
          }}
          onNewSale={async () => {
            setTenderOpen(false);
            await startNewSale();
          }}
          canCredit={canCredit}
          busy={busy}
          error={error}
        />
      )}

      {/* ---------- hold (park) dialog ---------- */}
      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="h-5 w-5 text-brand" aria-hidden />
              {t('sale.holdTitle', { defaultValue: 'Hold this sale' })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-muted">
              {t('sale.holdHint', { defaultValue: 'Park the current cart so you can start another sale, then resume it any time from Waiting.' })}
            </p>
            <FormField id="hold-label" label={t('sale.holdLabel', { defaultValue: 'Label (optional)' })}>
              <Input
                id="hold-label"
                value={holdLabelInput}
                onChange={(e) => setHoldLabelInput(e.target.value)}
                placeholder={t('sale.holdLabelPlaceholder', { defaultValue: 'e.g. customer name' })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void holdSale(holdLabelInput); } }}
                autoFocus
              />
            </FormField>
            <div className="flex justify-end gap-2">
              <ShButton variant="ghost" onClick={() => setHoldOpen(false)} disabled={busy}>
                {t('sale.cancel', { defaultValue: 'Cancel' })}
              </ShButton>
              <Button onClick={() => void holdSale(holdLabelInput)} loading={busy}>
                <Pause className="h-4 w-4" aria-hidden />
                {t('sale.holdConfirm', { defaultValue: 'Hold sale' })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- clear-cart confirm ---------- */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="h-5 w-5 text-danger" aria-hidden />
              {t('sale.clearTitle', { defaultValue: 'Clear all items?' })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fg-muted">
            {t('sale.clearHint', { defaultValue: 'This removes every item from the current cart. The customer stays attached.' })}
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <ShButton variant="ghost" onClick={() => setClearOpen(false)} disabled={busy}>
              {t('sale.cancel', { defaultValue: 'Cancel' })}
            </ShButton>
            <Button variant="danger" onClick={() => void clearCart()} loading={busy}>
              <Eraser className="h-4 w-4" aria-hidden />
              {t('sale.clearConfirm', { defaultValue: 'Clear all' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- refused currency switch (customer can't price this cart) ---------- */}
      <Dialog open={switchBlocked !== null} onOpenChange={(o) => { if (!o) setSwitchBlocked(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" aria-hidden />
              {t('sale.switchBlockedTitle', { defaultValue: "Can't price this cart for that customer" })}
            </DialogTitle>
          </DialogHeader>
          {switchBlocked && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-fg-muted">
                {t('sale.switchBlockedHint', {
                  customer: switchBlocked.partyName,
                  from: switchBlocked.from,
                  to: switchBlocked.to,
                  defaultValue: 'Selling to {{customer}} means pricing this cart in {{to}}, and these items have no {{to}} price. The cart is still in {{from}} — nothing was changed.',
                })}
              </p>
              {switchBlocked.items.length > 0 && (
                <ul className="max-h-40 list-disc overflow-y-auto ps-5 text-sm text-fg">
                  {/* Two lines can hold the same product — index-keyed on purpose. */}
                  {switchBlocked.items.map((d, i) => <li key={`${i}-${d}`} className="truncate">{d}</li>)}
                </ul>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <ShButton variant="ghost" onClick={() => setSwitchBlocked(null)} disabled={busy}>
                  {t('sale.switchBlockedKeep', { defaultValue: 'Keep this cart' })}
                </ShButton>
                <Button onClick={() => void startSaleForBlocked()} loading={busy}>
                  <Eraser className="h-4 w-4" aria-hidden />
                  {t('sale.switchBlockedFresh', {
                    currency: switchBlocked.to,
                    defaultValue: 'Clear cart & start a {{currency}} sale',
                  })}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- parked (waiting) sales switcher ---------- */}
      <Dialog open={parkedOpen} onOpenChange={setParkedOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-brand" aria-hidden />
              {t('sale.parkedTitle', { defaultValue: 'Waiting sales' })}
            </DialogTitle>
          </DialogHeader>
          {otherParked.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t('sale.parkedEmpty', { defaultValue: 'No sales are waiting.' })}
            </p>
          ) : (
            <ul className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
              {otherParked.map((s) => {
                const party = parties?.find((p) => p.id === s.partyId);
                const name = (party && party.code !== 'WALK-IN' ? party.displayName : '') || walkInName;
                const label = s.holdLabel?.trim();
                const title = label || name;
                const time = formatTime(s.createdAt, { locale: i18n.language });
                return (
                  <li key={s.id} className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle/40 px-3 py-2 transition-colors hover:border-brand/40">
                    <button
                      type="button"
                      onClick={() => void resumeSale(s.id)}
                      disabled={busy}
                      className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start outline-none disabled:opacity-50"
                    >
                      <span className="flex w-full items-center gap-2">
                        <span className="truncate text-sm font-semibold text-fg">{title}</span>
                        <span className="ms-auto shrink-0 font-mono text-sm font-bold tabular-nums text-fg">
                          {formatMoney(s.total, s.currencyCode, i18n.language)}
                        </span>
                      </span>
                      <span className="truncate text-xs text-fg-muted">
                        {s.saleNumber}{label && name !== walkInName ? ` · ${name}` : ''} · {time}
                      </span>
                    </button>
                    <ShButton
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-fg-muted hover:text-danger"
                      onClick={() => void discardParked(s.id)}
                      disabled={busy}
                      aria-label={t('sale.discard', { defaultValue: 'Discard' })}
                      title={t('sale.discard', { defaultValue: 'Discard' })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ShButton>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- customer picker ---------- */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        {/* A grid item will not shrink below its content unless it is told it
            may (min-w-0): without that the chips and long names pushed the
            column wider than the dialog and the whole thing scrolled sideways. */}
        <DialogContent className="max-w-lg overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-brand/10 text-brand">
                <User className="h-4 w-4" aria-hidden />
              </span>
              {t('sale.selectCustomer', { defaultValue: 'Select customer' })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden />
              <Input
                value={custQuery}
                onChange={(e) => setCustQuery(e.target.value)}
                placeholder={t('sale.searchCustomers', { defaultValue: 'Search customers…' })}
                aria-label={t('sale.searchCustomers', { defaultValue: 'Search customers' })}
                className="h-11 ps-9"
                autoFocus
              />
            </div>

            {/* One tap straight to ماركيت or قرطاسية: the groups exist and the
                list already sections by them, but reaching a group meant
                scrolling past every other one. Chips filter; search still works
                inside the chosen group. Reset on open, not sticky — the next
                customer is usually not from the last customer's group. */}
            {activeLists.length > 0 && (
              <div className="flex min-w-0 flex-wrap gap-1.5">
                {[{ id: 'all', name: t('sale.groupAll', { defaultValue: 'All' }) },
                  ...activeLists.map((pl) => ({ id: pl.id, name: pl.name })),
                  { id: '__none__', name: t('sale.noGroup', { defaultValue: 'No group' }) }].map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setCustGroup(g.id)}
                    aria-pressed={custGroup === g.id}
                    className={
                      'rounded-full border px-3 py-1 text-xs font-semibold transition ' +
                      (custGroup === g.id
                        ? 'border-brand bg-brand/15 text-brand'
                        : 'border-border bg-bg-subtle text-fg-muted hover:text-fg')
                    }
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            )}

            {/* WALK-IN, IN ONE TAP. Most sales at a counter are anonymous, and
                going back to anonymous after serving a named customer meant
                finding "WALK-IN" among hundreds of rows — so cashiers left the
                previous customer attached, which prices the sale off that
                customer's group and posts the sale to their account. The till
                (.exe) has always fallen back to walk-in by itself; this is the
                same idea where the web register needs a deliberate tap. */}
            {walkInParty && (
              <button
                type="button"
                onClick={() => setCustomer(walkInParty.id)}
                disabled={busy || sale?.sale.partyId === walkInParty.id}
                className={
                  'flex w-full min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-start transition ' +
                  (sale?.sale.partyId === walkInParty.id
                    ? 'border-brand bg-brand/10 text-brand'
                    : 'border-border bg-bg-subtle hover:border-brand hover:bg-brand/5')
                }
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/15 text-brand">
                  <User className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{walkInName}</span>
                  <span className="block truncate text-xs text-fg-muted">
                    {t('sale.walkInHint', { defaultValue: 'No account — the usual counter sale' })}
                  </span>
                </span>
                {sale?.sale.partyId === walkInParty.id && (
                  <Check className="h-4 w-4 flex-none" aria-hidden />
                )}
              </button>
            )}

            {/* full customer form (identical to the customers page) */}
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => { setCustOpen(false); setNewCustomerOpen(true); }}
            >
              <UserPlus className="h-4 w-4" />
              {t('sale.newCustomer', { defaultValue: 'New customer' })}
            </Button>

            <ul className="max-h-[min(22rem,42vh)] min-w-0 overflow-y-auto overflow-x-hidden rounded-lg border border-border">
              {customerList.length === 0 ? (
                <li className="py-6 text-center text-sm text-fg-muted">
                  {t('sale.noCustomers', { defaultValue: 'No customers found' })}
                </li>
              ) : hasGroups ? (
                customerGroups.map(([key, g]) => (
                  <li key={key}>
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-bg-subtle px-3 py-1.5">
                      <span className="truncate text-xs font-bold uppercase tracking-wide text-fg-muted">{g.name}</span>
                      <Badge variant="secondary">{g.currency}</Badge>
                    </div>
                    <ul>{g.customers.map((p) => renderCustomerRow(p))}</ul>
                  </li>
                ))
              ) : (
                customerList.map((p) => renderCustomerRow(p))
              )}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- new customer (full form, identical to the customers page) ---------- */}
      <Dialog open={newCustomerOpen} onOpenChange={setNewCustomerOpen}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="sr-only">{t('sale.newCustomer', { defaultValue: 'New customer' })}</DialogTitle>
          <PartyForm
            mode="create"
            side="customer"
            hideType
            onCancel={() => setNewCustomerOpen(false)}
            onSaved={async (party) => {
              setNewCustomerOpen(false);
              if (party) {
                setParties((prev) => [party, ...(prev ?? [])]);
                await setCustomer(party.id);
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ---------- customer account (balance/debt + sales + payments) ---------- */}
      <Dialog open={accountOpen} onOpenChange={setAccountOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('sale.customerAccount', { defaultValue: 'Customer account' })}
              {attachedParty ? ` — ${attachedParty.displayName}` : ''}
            </DialogTitle>
          </DialogHeader>
          {attachedParty && (
            <>
              {/* Who they are — the card a cashier reads before quoting anything. */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-md bg-bg-subtle px-2 py-1 font-mono text-fg-muted">{attachedParty.code}</span>
                {attachedParty.phone && (
                  <a href={`tel:${attachedParty.phone}`} dir="ltr" className="inline-flex items-center gap-1 rounded-md bg-bg-subtle px-2 py-1 font-mono text-brand underline-offset-4 hover:underline">
                    {attachedParty.phone}
                  </a>
                )}
                {attachedParty.priceListName && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-brand/10 px-2 py-1 font-medium text-brand">
                    <Coins className="h-3 w-3" aria-hidden />{attachedParty.priceListName}
                  </span>
                )}
                {insight && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-bg-subtle px-2 py-1 text-fg-muted">
                    <History className="h-3 w-3" aria-hidden />
                    {t('sale.priceMemoryCount', { n: insight.lastPrices.length, defaultValue: '{{n}} items with a remembered price' })}
                  </span>
                )}
              </div>

              {/* Their latest purchases, newest first — tickets and invoices together. */}
              {insight && insight.recentSales.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border">
                  <div className="border-b border-border bg-bg-subtle px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    {t('sale.recentSales', { defaultValue: 'Recent sales' })}
                  </div>
                  <ul className="flex max-h-56 flex-col divide-y divide-border-subtle overflow-y-auto">
                    {insight.recentSales.map((s) => (
                      <li key={`${s.source}:${s.docNumber}`} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="w-24 flex-none font-mono text-xs text-fg-muted">{s.docNumber}</span>
                        <span className="flex-none text-xs text-fg-muted">
                          {formatDateTime(s.docDate, { locale: i18n.language }, { dateStyle: 'medium' })}
                        </span>
                        <span className={'inline-flex flex-none items-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold ' + (
                          s.source === 'pos' ? 'bg-brand/10 text-brand' : 'bg-info/10 text-info')}>
                          {s.source === 'pos'
                            ? t('sale.sourcePos', { defaultValue: 'POS' })
                            : t('sale.sourceInvoice', { defaultValue: 'Invoice' })}
                        </span>
                        <span className="min-w-0 flex-1" />
                        <span className="flex-none font-mono text-sm font-semibold tabular-nums">
                          {formatMoney(s.total, s.currencyCode, i18n.language)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Its "Record payment" is the shift's debt payment — the money goes in
                  this drawer and the close-out counts it. */}
              <CustomerStatementCard
                party={attachedParty}
                onChanged={() => setStmtRefresh((n) => n + 1)}
                onRecordPayment={() => { setAccountOpen(false); setCollectOpen(true); }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- new product ---------- */}
      <Dialog open={newProdOpen} onOpenChange={setNewProdOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('items.newProduct', { defaultValue: 'New product' })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <FormField id="npName" label={t('items.name', { defaultValue: 'Name' })} required>
              <Input id="npName" value={npName} onChange={(e) => setNpName(e.target.value)} autoFocus />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField id="npCode" label={t('items.code', { defaultValue: 'Code / SKU' })} required>
                <Input id="npCode" value={npCode} onChange={(e) => setNpCode(e.target.value)} className="font-mono" />
              </FormField>
              <FormField id="npPrice" label={t('items.salePrice', { defaultValue: 'Sale price' })}>
                <Input id="npPrice" type="number" min="0" step="0.01" value={npPrice} onChange={(e) => setNpPrice(e.target.value)} />
              </FormField>
            </div>
            <Tabs value={npType} onValueChange={(v) => setNpType(v as 'product' | 'service')}>
              <TabsList className="h-9 w-full">
                <TabsTrigger value="product" className="flex-1">{t('items.product', { defaultValue: 'Product' })}</TabsTrigger>
                <TabsTrigger value="service" className="flex-1">{t('items.service', { defaultValue: 'Service' })}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex justify-end gap-2 pt-1">
              <ShButton variant="ghost" onClick={() => setNewProdOpen(false)}>
                {t('actions.cancel', { defaultValue: 'Cancel' })}
              </ShButton>
              <Button onClick={createProduct} loading={busy} disabled={!npCode.trim() || !npName.trim()}>
                {t('items.createProduct', { defaultValue: 'Create product' })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- unit picker (item has pack/carton units) ---------- */}
      <Dialog
        open={unitPickItem !== null}
        onOpenChange={(o) => { if (!o) { setUnitPickItem(null); setUnitPickLine(null); } }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {unitPickLine
                ? t('sale.changeUnit', { defaultValue: 'Change unit' })
                : t('sale.chooseUnit', { defaultValue: 'Choose a unit' })}
            </DialogTitle>
          </DialogHeader>
          {unitPickItem && (() => {
            // What the cart will actually charge per unit: the customer's group
            // price when one applies, else the catalogue price in the ITEM's own
            // currency (never the sale's — that would mislabel the number).
            const base = priceForUnit(unitPickItem, null);
            // On hand, in BASE units — and what that is worth in each packaging
            // unit, because "301 pieces" and "25 cartons" are the same shelf and
            // the cashier is about to choose between them. Only for a tracked
            // item, and only once the number has actually loaded: a bare 0 while
            // it is in flight would read as "none left".
            // Same rule as the detail card: a tracked item always shows its
            // number (0 included), an untracked one only when it actually holds
            // something — this shop has thousands of untracked products that
            // carry a real quantity, and gating on the flag hid every one.
            const rawOnHand = unitPickItem.onHand;
            const onHand =
              rawOnHand !== undefined && (unitPickItem.isInventoryTracked || Number(rawOnHand) !== 0)
                ? Number(rawOnHand)
                : null;
            const perUnit = (factor: string): string => {
              const f = Number(factor) || 1;
              return formatNumber(Math.floor((onHand ?? 0) / f), { locale: i18n.language });
            };
            return (
              <div className="flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 text-sm font-medium">{unitPickItem.name}</p>
                  {/* The same product card the tile's ⓘ opens — photo, prices, stock —
                      so a cashier can check what they are about to ring up without
                      leaving the choice. It opens ON TOP of this dialog. */}
                  <button
                    type="button"
                    onClick={() => openDetail(unitPickItem)}
                    className="inline-flex flex-none items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-muted transition hover:border-brand hover:text-brand focus-visible:ring-2 focus-visible:ring-brand/40"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden />
                    {t('sale.viewDetails', { defaultValue: 'View details' })}
                  </button>
                </div>
                {onHand !== null && (
                  <p className={'text-xs font-medium ' + (onHand > 0 ? 'text-fg-muted' : 'text-danger')}>
                    {t('sale.onHand', { defaultValue: 'In stock' })}:{' '}
                    <span className="font-mono tabular-nums">{formatNumber(onHand, { locale: i18n.language })}</span>{' '}
                    {unitPickItem.unitOfMeasure}
                  </p>
                )}
                <UnitChoice
                  label={`${t('sale.baseUnit', { defaultValue: 'Single' })} · ${unitPickItem.unitOfMeasure}`}
                  hint={onHand !== null ? t('sale.availableN', { n: formatNumber(onHand, { locale: i18n.language }), defaultValue: `${formatNumber(onHand, { locale: i18n.language })} available` }) : undefined}
                  price={formatMoney(base.amount, base.currency, i18n.language)}
                  warn={base.blocked}
                  disabled={busy}
                  onClick={() => void addItemUnit(unitPickItem, null)}
                />
                {unitPickItem.units.map((u) => {
                  const p = priceForUnit(unitPickItem, u.id);
                  const factorHint = t('sale.unitFactorHint', { factor: u.factor, defaultValue: `${u.factor} × base` });
                  return (
                    <UnitChoice
                      key={u.id}
                      label={u.name}
                      hint={onHand !== null
                        ? `${factorHint} · ${t('sale.availableN', { n: perUnit(u.factor), defaultValue: `${perUnit(u.factor)} available` })}`
                        : factorHint}
                      price={formatMoney(p.amount, p.currency, i18n.language)}
                      warn={p.blocked}
                      disabled={busy}
                      onClick={() => void addItemUnit(unitPickItem, u.id)}
                    />
                  );
                })}
                {(base.blocked || unitPickItem.units.some((u) => priceForUnit(unitPickItem, u.id).blocked)) && (
                  <p className="text-xs text-warning">
                    {t('sale.noGroupPriceHint', {
                      currency,
                      defaultValue: 'Amounts marked in orange have no {{currency}} price for this customer and can\'t be rung up as they are.',
                    })}
                  </p>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ---------- product details (reusable modal) ---------- */}
      <ItemDetailDialog
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(o) => { if (!o) setDetailItem(null); }}
        locale={i18n.language}
        // Items with no own currency are priced in the TENANT BASE currency —
        // never the sale's (a USD cart must not relabel an IQD price "$").
        currencyFallback={baseCurrency}
        canViewImages={canFiles}
        categoryName={detailItem?.categoryId ? catNameById.get(detailItem.categoryId) : null}
        brandName={detailItem?.brandId ? brandNameById.get(detailItem.brandId) : null}
        busy={busy}
        groupPrices={
          detailItem && itemPrices && itemPrices.itemId === detailItem.id
            ? {
                currencyCode: currency,
                groupName: itemPrices.groupName,
                base: itemPrices.base,
                units: itemPrices.units,
              }
            : null
        }
        onAddToCart={async (it, unitId) => {
          setDetailItem(null);
          // Both paths carry the customer's group price so the line is charged
          // at it (and so an item priced in another currency is accepted).
          const unitPriceOverride = await resolveGroupPrice(it.id, '1', sale?.sale.partyId, unitId ?? undefined);
          await addLine({
            itemId: it.id,
            ...(unitId ? { itemUnitId: unitId } : {}),
            quantity: '1',
            unitPriceOverride,
          });
        }}
      />

      {/* ---------- this-shift sales ---------- */}
      <Dialog open={salesOpen} onOpenChange={setSalesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-fg-muted" aria-hidden />
              {t('sales.sessionTitle', { defaultValue: 'Sales this shift' })}
            </DialogTitle>
          </DialogHeader>
          {viewSaleId ? (
            // ---- drilled into one sale: line items + payment breakdown ----
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setViewSaleId(null)}
                className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-fg-muted transition-colors hover:text-fg"
              >
                <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                {tc('actions.back', { defaultValue: 'Back' })}
              </button>
              {viewSaleLoading || !viewSale ? (
                <PosSaleDetailSkeleton />
              ) : (
                <PosSaleDetailBody
                  detail={viewSale}
                  partyName={parties?.find((p) => p.id === viewSale.sale.partyId)?.displayName}
                  cashierName={cashierName}
                  terminalName={terminals?.find((tm) => tm.id === viewSale.sale.terminalId)?.name}
                  onOpenInvoice={
                    viewSale.sale.invoiceId
                      ? () => window.open(appUrl(`/sales/invoices/${viewSale.sale.invoiceId}/print`), '_blank')
                      : undefined
                  }
                  onEdit={canEditSale ? () => void editFinishedSale(viewSale.sale.id, viewSale.sale.saleNumber) : undefined}
                  editing={busy}
                />
              )}
            </div>
          ) : sessionSales === null ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : sessionSales.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-muted">
              {t('sales.sessionEmpty', { defaultValue: 'No sales in this shift yet.' })}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between rounded-md bg-bg-subtle/60 px-3 py-2 text-sm text-fg-muted">
                {t('sales.sessionSummary', {
                  count: sessionSales.filter((s) => s.status === 'finalized').length,
                  // Totalled PER currency — a shift can mix the drawer currency
                  // with a customer group's, and those are never added up.
                  total: sumByCurrency(
                    sessionSales.filter((s) => s.status === 'finalized'),
                    shiftCurrency,
                  )
                    .map((x) => formatMoney(x.total, x.currencyCode, i18n.language))
                    .join(' · '),
                  defaultValue: '{{count}} finalized · {{total}}',
                })}
              </div>
              {new Set(sessionSales.map((s) => s.status)).size > 1 && (
                <label className="flex items-center gap-2 self-start text-xs">
                  <span className="font-medium text-fg-muted">{t('sales.table.status')}</span>
                  <select
                    value={salesStatusFilter}
                    onChange={(e) => setSalesStatusFilter(e.target.value)}
                    className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
                  >
                    <option value="all">{tc('filters.allStatuses', { defaultValue: 'All statuses' })}</option>
                    {Array.from(new Set(sessionSales.map((s) => s.status))).map((st) => (
                      <option key={st} value={st}>{t(`sales.status.${st}`, { defaultValue: st })}</option>
                    ))}
                  </select>
                </label>
              )}
              <DataTable
                columns={
                  [
                    {
                      id: 'number',
                      header: t('sales.table.number'),
                      sortValue: (s: PosSale) => s.saleNumber,
                      searchValue: (s: PosSale) => s.saleNumber,
                      cellClassName: 'font-mono text-xs',
                      cell: (s: PosSale) => s.saleNumber,
                    },
                    {
                      id: 'date',
                      header: t('sales.table.date'),
                      sortValue: (s: PosSale) => s.saleDate,
                      cellClassName: 'text-xs text-fg-muted',
                      // The BOOKED time: a back-dated sale lists on its own day.
                      cell: (s: PosSale) => formatDateTime(s.saleDate, { locale: i18n.language }, { dateStyle: 'short', timeStyle: 'short' }),
                    },
                    {
                      id: 'customer',
                      header: t('sales.table.customer', { defaultValue: 'Customer' }),
                      sortValue: (s: PosSale) => parties?.find((p) => p.id === s.partyId)?.displayName ?? '',
                      searchValue: (s: PosSale) => parties?.find((p) => p.id === s.partyId)?.displayName ?? '',
                      cellClassName: 'text-sm',
                      cell: (s: PosSale) => parties?.find((p) => p.id === s.partyId)?.displayName ?? '—',
                    },
                    {
                      id: 'total',
                      header: t('sales.table.total'),
                      align: 'end' as const,
                      sortValue: (s: PosSale) => Number(s.total),
                      cell: (s: PosSale) => formatMoney(s.total, s.currencyCode, i18n.language),
                    },
                    {
                      id: 'status',
                      header: t('sales.table.status'),
                      sortValue: (s: PosSale) => t(`sales.status.${s.status}`, { defaultValue: s.status }),
                      searchValue: (s: PosSale) => t(`sales.status.${s.status}`, { defaultValue: s.status }),
                      cell: (s: PosSale) => <Badge variant={s.status === 'finalized' ? 'success' : s.status === 'parked' ? 'warning' : 'secondary'}>{t(`sales.status.${s.status}`, { defaultValue: s.status })}</Badge>,
                    },
                  ] satisfies DataTableColumn<PosSale>[]
                }
                data={salesStatusFilter === 'all' ? sessionSales : sessionSales.filter((s) => s.status === salesStatusFilter)}
                rowKey={(s) => s.id}
                searchPlaceholder={t('sales.table.number')}
                pageSize={8}
                onRowClick={(s) => setViewSaleId(s.id)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- keyboard shortcuts cheatsheet ---------- */}
      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="h-4 w-4 text-fg-muted" aria-hidden />
              {t('shortcuts.title', { defaultValue: 'Keyboard shortcuts' })}
            </DialogTitle>
          </DialogHeader>
          <ul className="flex flex-col gap-1.5">
            {[
              { keys: ['F2'], label: t('shortcuts.search', { defaultValue: 'Focus product search' }) },
              { keys: ['F3'], label: t('shortcuts.scan', { defaultValue: 'Focus barcode scan' }) },
              { keys: ['F9'], label: t('shortcuts.charge', { defaultValue: 'Charge / open payment' }) },
              { keys: ['?'], label: t('shortcuts.help', { defaultValue: 'Toggle this help' }) },
            ].map((row) => (
              <li
                key={row.keys.join('+')}
                className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2 text-sm"
              >
                <span className="text-fg">{row.label}</span>
                <span className="flex items-center gap-1">
                  {row.keys.map((k) => (
                    <kbd
                      key={k}
                      className="rounded-md border border-border bg-bg-subtle px-2 py-0.5 font-mono text-xs font-semibold text-fg shadow-sm"
                    >
                      {k}
                    </kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface UnitChoiceProps {
  label: string;
  hint?: string;
  price: string;
  /** The price isn't in the sale currency (no group price) — flag it, don't hide it. */
  warn?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function UnitChoice({ label, hint, price, warn, disabled, onClick }: UnitChoiceProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5 text-start transition hover:border-brand hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-brand/40 disabled:opacity-60"
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-fg">{label}</span>
        {hint && <span className="block text-xs text-fg-muted">{hint}</span>}
      </span>
      <span className={'font-mono text-sm font-semibold tabular-nums ' + (warn ? 'text-warning' : '')}>
        {price}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Category helpers + chip

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition ' +
        (active
          ? 'border-transparent bg-brand text-brand-contrast'
          : 'border-border text-fg-muted hover:bg-bg-subtle hover:text-fg')
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tile

/** Trim trailing zeros for display (e.g. "1.0000" → "1", "3.7500" → "3.75"). */
function trimNum(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

/** Inline numeric input for a cart line — commits the qty / price on blur or Enter. */
function LineNumberInput({
  value,
  onCommit,
  disabled,
  ariaLabel,
  className,
  allowNegative = false,
  allowZero = false,
}: {
  value: string;
  /** May return the save's promise — the box then re-reads the server's value when it settles. */
  onCommit: (v: string) => void | Promise<unknown>;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
  /**
   * Quantity only, and only for a cashier who may take goods back: a negative
   * quantity is a return. Off by default because this same input edits the
   * PRICE, where a negative is always a mistake.
   */
  allowNegative?: boolean;
  /**
   * Zero is accepted. On a QUANTITY it is a PRICE-ONLY line (the item stays on
   * the ticket with its price and nothing is charged); on a PRICE it is goods
   * given away. Off by default so a box that has not been told which it is keeps
   * refusing the number.
   */
  allowZero?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState(() => trimNum(value));
  const focused = useRef(false);
  // The server's value as of the latest render — read after a save settles.
  const latest = useRef(value);
  latest.current = value;
  // Sync from the server value, but never clobber what the cashier is typing.
  useEffect(() => {
    if (!focused.current) setDraft(trimNum(value));
  }, [value]);
  const commit = (): void => {
    // At most 4 places — what a line stores and the API accepts. A price typed as
    // 2000 ÷ 1670 = 1.1976047 was REFUSED whole, the cart kept its old price, and
    // this box went on showing the refused number beside a total that did not
    // match it.
    const v = capRaw(draft.trim(), 4);
    const n = Number(v);
    // Each rule is its own switch, so the PRICE box keeps refusing both a zero
    // and a minus while the QUANTITY box accepts whichever the cashier is
    // allowed to mean: a return (negative) or a price shown but not charged (0).
    const valid = v !== ''
      && Number.isFinite(n)
      && (allowZero || n !== 0)
      && (allowNegative || n >= 0);
    if (valid && n !== Number(value)) {
      setDraft(trimNum(v));
      // A refused save leaves the server's value as it was, so nothing would
      // re-sync the box: put it back to the price actually charged.
      void Promise.resolve(onCommit(v)).finally(() => {
        if (!focused.current) setDraft(trimNum(latest.current));
      });
    } else setDraft(trimNum(value));
  };
  return (
    /*
      The shared NumberInput, unstyled — the border and height belong to the
      wrapper this sits inside, between its ± buttons. Reusing it rather than
      re-implementing keeps the caret handling in ONE tested place; the same
      logic living in two files is exactly what drifts.

      It was type="number", which reports an empty value the moment a separator
      appears. The min="0" that came with it was already redundant (commit()
      rejects anything not greater than zero) and `step` drove a spinner the
      ± buttons already replace, so nothing is lost with them.
    */
    <NumberInput
      value={draft}
      onValueChange={setDraft}
      disabled={disabled}
      aria-label={ariaLabel}
      unstyled
      className={className}
      onFocus={(e) => { focused.current = true; e.currentTarget.select(); }}
      onBlur={() => { focused.current = false; commit(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

function Pager({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (p: number) => void }): JSX.Element {
  const { t } = useTranslation('pos');
  return (
    <div className="mt-3 flex items-center justify-center gap-2">
      <ShButton
        size="icon"
        variant="outline"
        className="h-8 w-8"
        disabled={page <= 0}
        onClick={() => onPage(page - 1)}
        aria-label={t('browse.prev', { defaultValue: 'Previous' })}
      >
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </ShButton>
      <span className="min-w-[4rem] text-center text-xs font-medium tabular-nums text-fg-muted">
        {t('browse.page', { page: page + 1, count: pageCount, defaultValue: `${page + 1} / ${pageCount}` })}
      </span>
      <ShButton
        size="icon"
        variant="outline"
        className="h-8 w-8"
        disabled={page >= pageCount - 1}
        onClick={() => onPage(page + 1)}
        aria-label={t('browse.next', { defaultValue: 'Next' })}
      >
        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </ShButton>
    </div>
  );
}

interface BrowseCardProps {
  entityType: 'category' | 'brand';
  id: string;
  name: string;
  count: number;
  enabled: boolean;
  onClick: () => void;
}

/** A category / brand card (image + name + item count) that drills into items. */
function BrowseCard({ entityType, id, name, count, enabled, onClick }: BrowseCardProps): JSX.Element {
  const { t } = useTranslation('pos');
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let made: string | null = null;
    void (async () => {
      try {
        const list = await listAttachments(entityType, id);
        const img = list
          .filter((a) => isImageMime(a.mimeType))
          .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img) { if (active) setUrl(null); return; }
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u);
      } catch { if (active) setUrl(null); }
    })();
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [entityType, id, enabled]);
  const Fallback = entityType === 'brand' ? Tag : FolderTree;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-36 flex-col gap-1 rounded-lg border border-border bg-bg p-2 text-start outline-none transition-all duration-150 hover:-translate-y-0.5 hover:border-brand hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand/40"
      aria-label={name}
    >
      {/* The photo takes every pixel the card can spare (flex-1) and stays
          object-contain, so the whole image shows and is never cropped. */}
      <span
        className={`relative grid min-h-0 w-full flex-1 place-items-center overflow-hidden rounded-md ${url ? 'ring-1 ring-border/60' : 'text-white/90'}`}
        style={url ? undefined : { backgroundImage: gradientFor(id + name) }}
      >
        {url ? (
          <FitImage src={url} />
        ) : (
          <Fallback className="h-6 w-6 drop-shadow" aria-hidden />
        )}
      </span>
      <span className="line-clamp-2 text-xs font-semibold leading-tight">{name}</span>
      <span className="mt-auto inline-flex w-fit items-center rounded-full bg-bg-subtle px-1.5 py-0.5 text-[0.7rem] font-medium text-fg-muted">
        {t('browse.itemsCount', { count, defaultValue: `${count} items` })}
      </span>
    </button>
  );
}

interface ItemTileProps {
  item: Item;
  locale: string;
  enabled: boolean;
  /** Tenant base currency — what an item with no own sale currency is priced in. */
  baseCurrency: string;
  /** The currency the cart charges in (the customer's group currency, or the shift's). */
  saleCurrency: string;
  /**
   * The attached customer's group price for this item, in `saleCurrency`:
   * a string = what they pay, `null` = their group has no price for it,
   * `undefined` = no group pricing in play (plain catalogue price).
   */
  groupPrice?: string | null;
  onAdd: (item: Item) => void;
  onInfo: (item: Item) => void;
}

function ItemTile({
  item, locale, enabled, baseCurrency, saleCurrency, groupPrice, onAdd, onInfo,
}: ItemTileProps): JSX.Element {
  const { t } = useTranslation('pos');
  // What this customer pays beats the catalogue price. An item with no own
  // currency is priced in the tenant BASE currency (what the server assumes) —
  // never a hardcoded USD, which rendered every such item as "$2,500".
  const itemCurrency = item.saleCurrencyCode ?? baseCurrency;
  const price = groupPrice ?? item.salePrice ?? '0';
  const currency = groupPrice != null ? saleCurrency : itemCurrency;
  // The catalogue price is in another currency and the customer's group has no
  // price to replace it — the server will refuse this line as it stands.
  const blocked = groupPrice == null && itemCurrency !== saleCurrency;
  // Product photo (shared hook): first image attachment, gated by `enabled`.
  const url = useItemImage(item.id, enabled);
  const Icon = item.itemType === 'service' ? Wrench : Package;
  // h-44, not h-36. The photo is object-contain (never cropped), but in a 144px
  // tile the name and price row left it barely 68px of height, so a fitted
  // product rendered postage-stamp small and read as broken. The extra 32px goes
  // entirely to the image, which is the part a cashier recognises a product by.
  return (
    <div className="group relative flex h-48 flex-col rounded-lg border border-border bg-bg text-start transition-all duration-150 hover:-translate-y-0.5 hover:border-brand hover:shadow-md focus-within:ring-2 focus-within:ring-brand/40">
      {/* main tap target = add to cart */}
      <button
        type="button"
        onClick={() => onAdd(item)}
        className="flex h-full w-full flex-col gap-1.5 p-2 text-start outline-none active:translate-y-0"
        aria-label={`${t('sale.addButton')} — ${item.code} — ${item.name}`}
      >
        {/* Photo fills the tile's spare height (flex-1), object-contain so the
            whole product is visible rather than cropped to a fixed strip. */}
        <span
          className={`relative grid min-h-0 w-full flex-1 place-items-center overflow-hidden rounded-md ${url ? 'ring-1 ring-border/60' : 'text-white/90'}`}
          style={url ? undefined : { backgroundImage: gradientFor(item.id) }}
        >
          {url ? (
            // The whole photo, edge to edge of the box: fitted, never zoomed or
            // cropped, with white or black bars (whichever its edge is) where the
            // proportions differ.
            <FitImage src={url} />
          ) : (
            <Icon className="h-6 w-6 drop-shadow" aria-hidden />
          )}
          {/* hover "add" affordance — signals the tile adds to the cart */}
          <span className="absolute inset-0 grid place-items-center bg-brand/0 opacity-0 transition group-hover:bg-brand/30 group-hover:opacity-100">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-bg/90 text-brand shadow-md">
              <Plus className="h-4 w-4" aria-hidden />
            </span>
          </span>
          {item.isFeatured && (
            <span className="absolute end-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-bg/90 text-warning shadow-sm ring-1 ring-warning/30">
              <Star className="h-3 w-3 fill-current" aria-hidden />
            </span>
          )}
        </span>
        <span className="line-clamp-3 text-xs font-medium leading-tight" title={item.name}>{item.name}</span>
        <div className="mt-auto flex items-end justify-between gap-1">
          <span className="font-mono text-[0.65rem] text-fg-muted">{item.code}</span>
          <span
            className={
              'rounded-md px-1.5 py-0.5 font-mono text-xs font-bold tabular-nums ' +
              (blocked ? 'bg-warning/15 text-warning' : 'bg-brand/10 text-brand')
            }
            title={blocked
              ? t('sale.noGroupPriceTile', {
                  currency: saleCurrency,
                  defaultValue: 'No {{currency}} price for this customer — needs a group price or a custom line.',
                })
              : undefined}
          >
            {formatMoney(price, currency, locale)}
          </span>
        </div>
      </button>
      {/* info button → product details modal (sibling, not nested in the add button) */}
      <button
        type="button"
        onClick={() => onInfo(item)}
        className="absolute start-1 top-1 z-10 grid h-6 w-6 place-items-center rounded-full bg-bg/80 text-fg-muted opacity-0 shadow-sm ring-1 ring-border backdrop-blur transition hover:text-brand focus-visible:opacity-100 focus-visible:ring-brand/40 group-hover:opacity-100"
        aria-label={t('sale.viewDetails', { defaultValue: 'View details' })}
        title={t('sale.viewDetails', { defaultValue: 'View details' })}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Receipt strip (in-pane, post-finalize)

interface ReceiptStripProps {
  sale: PosSale;
  onNewSale: () => void;
  customerPhone?: string | null;
  waVars?: Record<string, string | undefined>;
}

function ReceiptStrip({ sale, onNewSale, customerPhone, waVars }: ReceiptStripProps): JSX.Element {
  const { t } = useTranslation('pos');
  return (
    <div className="mt-3 rounded-md border border-success/40 bg-success/10 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Badge variant="success">{t('receipt.title')}</Badge>
          <p className="mt-1 text-xs text-fg-muted">
            {t('receipt.saleNumber', { number: sale.saleNumber })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {customerPhone && waVars && (
            <WhatsAppButton docType="posReceipt" phone={customerPhone} vars={waVars} size="sm" />
          )}
          <Button size="sm" onClick={onNewSale}>{t('receipt.newSale')}</Button>
        </div>
      </div>
      {sale.invoiceId && (
        // window.open keeps the opener so the new tab inherits the sessionStorage
        // auth; an <a target="_blank"> gets implicit noopener and would bounce to
        // login. Keep it a new tab so the register isn't lost.
        <button
          type="button"
          onClick={() => window.open(appUrl(`/sales/invoices/${sale.invoiceId}/print`), '_blank')}
          className="mt-2 inline-block text-xs text-brand underline-offset-4 hover:underline"
        >
          {t('receipt.openPrint')}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tender dialog

/** What the cashier chose to pay with — one step: add the tender (if any) and, when it covers, finalize. */
interface PayRequest {
  tender?: { kind: PosTenderKind; amount: string; reference?: string | null };
  /** Money in ANOTHER currency than the sale's — notes, or a card / wallet / other charged in it. */
  cashExchange?: {
    tendered: { currencyCode: string; amount: string };
    change: { currencyCode: string; amount: string };
    tenderKind?: 'cash' | 'card' | 'wallet' | 'other';
  };
  finalize: boolean;
  onCredit?: boolean;
}

interface TenderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: SaleResponse;
  locale: string;
  /** Tenant base currency — cashback is a base-currency wallet, and change for other notes is given in it. */
  baseCurrency: string;
  /** The attached customer (null / WALK-IN ⇒ no account card). */
  party: Party | null;
  /** The customer's open-item statement (current debt by currency). */
  statement: PortalStatement | null;
  onPay: (p: PayRequest) => Promise<void>;
  onRemoveTender: (tenderNo: number) => Promise<void>;
  onNewSale: () => Promise<void>;
  /** The sale's note — the same draft as the cart's note box, saved before finalize. */
  note: string;
  onNoteChange: (note: string) => void;
  canCredit: boolean;
  busy: boolean;
  /**
   * The page's error, surfaced INSIDE the dialog — a refused finalize must not
   * look like a button that did nothing.
   */
  error: string | null;
}

/**
 * The smallest note a currency is paid in, where coarser than its decimals —
 * Iraq has nothing under 250 dinars. Dinars that come out of a conversion are
 * rounded to it IN THE SHOP'S FAVOUR, the till's rule (pos-desktop lib/fx.ts):
 * what is asked for goes up, change goes down.
 */
const CASH_STEP: Record<string, number> = { IQD: 250 };
const ceilStep = (a: Decimal, step: number): Decimal => a.dividedBy(step).toDecimalPlaces(0, Decimal.ROUND_CEIL).times(step);
const floorStep = (a: Decimal, step: number): Decimal => a.dividedBy(step).toDecimalPlaces(0, Decimal.ROUND_FLOOR).times(step);

/**
 * Take the money — laid out like the till's payment screen, and as compact.
 *
 * How it is paid (cash / card / wallet / other / cashback), and in WHICH currency:
 * the sale's own, or another — a dollar sale paid in dinars (cash asked for rounded
 * UP to 250, change rounded DOWN), a dinar sale paid in dollars (change in dinars).
 * A card, wallet or other payment can be charged in the other currency too — the
 * exact amount, nothing back. The sale's payment stays in its own currency; what
 * really changed hands rides along as `cashExchange`, and the server books the
 * exchange on that tender's account (the drawer for notes) and counts the drawer
 * by notes only at close. Then one button finishes it.
 */
function TenderDialog({
  open,
  onOpenChange,
  sale,
  locale,
  baseCurrency,
  party,
  statement,
  onPay,
  onRemoveTender,
  onNewSale,
  note,
  onNoteChange,
  canCredit,
  busy,
  error,
}: TenderDialogProps): JSX.Element {
  const { t } = useTranslation('pos');
  const [kind, setKind] = useState<PosTenderKind>('cash');
  const [payCcy, setPayCcy] = useState('');
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  /** A card / wallet / other amount typed over the suggested one ('' = the suggestion). */
  const [charge, setCharge] = useState('');
  const [cbBalance, setCbBalance] = useState<string | null>(null);
  const [cbCfg, setCbCfg] = useState<CashbackConfig | null>(null);
  /** Multipliers taking ONE unit of the sale's currency to each other currency (today's rates). */
  const [rates, setRates] = useState<Record<string, string>>({});

  const currency = sale.sale.currencyCode ?? baseCurrency;
  const dp = getCurrencyDecimals(currency) ?? 2;
  const due = Decimal.max(0, new Decimal(sale.sale.total || '0').minus(sale.sale.tenderTotal || '0'));
  const isFinalized = sale.sale.status === 'finalized';
  const partyId = sale.sale.partyId;

  // A fresh open starts clean: cash, in the sale's own notes, nothing typed.
  useEffect(() => {
    if (!open) return;
    setKind('cash');
    setPayCcy(currency);
    setReceived('');
    setReference('');
    setCharge('');
  }, [open, currency]);

  // Today's rates from the sale's currency — what cash in other notes is worth.
  useEffect(() => {
    if (!open) return;
    let live = true;
    void api().get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base: currency } })
      .then((r) => { if (live) setRates(Object.fromEntries(r.data.items.map((x) => [x.code, x.rate]))); })
      .catch(() => { if (live) setRates({}); });
    return () => { live = false; };
  }, [open, currency]);

  // The cashback wallet + its redeem policy (offered only when the sale is in the base currency).
  useEffect(() => {
    if (!open || !partyId) { setCbBalance(null); return; }
    let active = true;
    void getCashbackBalance(partyId).then((b) => { if (active) setCbBalance(b); });
    void getCashbackConfig().then((c) => { if (active) setCbCfg(c); }).catch(() => { /* server still enforces */ });
    return () => { active = false; };
  }, [open, partyId, sale.sale.tenderTotal]);

  const cbAvail = new Decimal(cbBalance ?? '0');
  const cbTendered = (sale.tenders ?? []).filter((tn) => tn.kind === 'cashback')
    .reduce((s, tn) => s.plus(new Decimal(tn.amount || '0')), new Decimal(0));
  const cbPolicyLeft = cbCfg
    ? Decimal.max(0, new Decimal(sale.sale.total || '0').times(cbCfg.maxRedeemPct).div(100).minus(cbTendered))
    : null;
  const cbMax = Decimal.min(cbAvail, due, ...(cbPolicyLeft ? [cbPolicyLeft] : []));
  const cashbackOffered = currency === baseCurrency && cbAvail.gt(0) && (!cbCfg || cbAvail.gte(cbCfg.minRedeem || '0'));
  const kinds: PosTenderKind[] = cashbackOffered ? [...TENDER_KINDS, 'cashback'] : TENDER_KINDS;

  // Which notes cash may come in: the sale's own first; change for any other is
  // given in the base currency, so one side of every pairing must BE the base.
  const payOptions = [currency, ...(currency === baseCurrency
    ? Object.keys(rates).filter((c) => c !== currency).sort()
    : (rates[baseCurrency] ? [baseCurrency] : []))];
  const isCash = kind === 'cash';
  // Every way of paying but cashback (a base-currency wallet) can be in another currency.
  const multiCcy = kind !== 'cashback';
  const payIn = multiCcy && payOptions.includes(payCcy) ? payCcy : currency;
  const cross = payIn !== currency;
  const payDp = getCurrencyDecimals(payIn) ?? 2;
  const baseDp = getCurrencyDecimals(baseCurrency) ?? 2;
  const receivedDec = safeDecimal(received);
  const typed = received.trim() !== '' && receivedDec.gt(0);
  const rate = cross ? new Decimal(rates[payIn] ?? '0') : new Decimal(1);
  // Notes only: a card machine or a wallet charges the exact amount, to the unit.
  const step = cross && isCash ? (CASH_STEP[baseCurrency] ?? null) : null;

  /** What is asked for, in the customer's notes; and the change (in the base currency for other notes). */
  let needed = due;
  let change = new Decimal(0);
  if (!cross) {
    change = (typed ? receivedDec : due).minus(due);
  } else if (payIn === baseCurrency) {
    // e.g. a dollar sale paid in dinars: the bill in dinars, UP to the next note.
    const exact = due.times(rate);
    needed = step ? ceilStep(exact, step) : exact.toDecimalPlaces(baseDp, Decimal.ROUND_UP);
    change = receivedDec.minus(needed);
    if (step && change.gt(0)) change = floorStep(change, step);
  } else {
    // e.g. a dinar sale paid in dollars: the least dollars that cover it, change in dinars DOWN to a note.
    needed = due.times(rate).toDecimalPlaces(payDp, Decimal.ROUND_UP);
    const over = rate.gt(0) ? receivedDec.dividedBy(rate).minus(due) : new Decimal(-1);
    change = step ? floorStep(over, step) : over.toDecimalPlaces(baseDp, Decimal.ROUND_DOWN);
  }
  const changeCcy = cross ? baseCurrency : currency;
  const short = isCash && (cross ? !typed || receivedDec.lt(needed) : change.lt(0));
  const rateText = cross && rate.gt(0)
    ? (rate.gte(1)
      ? `1 ${currency} = ${formatMoney(rate.toString(), payIn, locale)}`
      : `1 ${payIn} = ${formatMoney(new Decimal(1).dividedBy(rate).toDecimalPlaces(2).toString(), currency, locale)}`)
    : '';
  /**
   * A card, wallet or other payment: the amount actually put through, which the
   * cashier may type over the suggestion — the machine may charge at its own rate.
   * In the other currency it is the WHOLE payment (the server books the rate gap
   * to exchange differences), held between 98% and 110% of the sale at today's
   * rate so a slipped digit is caught here. In the sale's own currency it may be
   * LESS than due: the rest is then paid by another method.
   */
  const suggested = cross ? needed : due;
  const chargeTyped = charge.trim() !== '' && safeDecimal(charge).gt(0);
  const chargeDec = chargeTyped ? safeDecimal(charge) : suggested;
  const exactIn = due.times(rate);
  const chargeProblem: 'short' | 'over' | 'overDue' | null = isCash || kind === 'cashback' ? null
    : cross
      ? (chargeDec.lt(exactIn.times('0.98')) ? 'short' : chargeDec.gt(exactIn.times('1.10')) ? 'over' : null)
      : (chargeDec.gt(due) ? 'overDue' : null);
  const partial = !isCash && kind !== 'cashback' && !cross && chargeDec.lt(due);
  const steps = payDp === 0 ? [5000, 10000, 25000, 50000] : [5, 10, 20, 50, 100];
  const neededNum = needed.toNumber();
  const suggestions = [...new Set(steps.map((s) => Math.ceil(neededNum / s) * s))].filter((v) => v > neededNum).slice(0, 3);

  const canPay = !busy && !isFinalized && (due.gt(0) || (sale.tenders ?? []).length > 0) && (
    kind === 'cash' ? !short
      : kind === 'cashback' ? cbMax.gt(0)
        : chargeDec.gt(0) && chargeProblem === null);

  // A new remaining amount (a part was just paid) or another currency: the suggestion again.
  const dueKey = due.toFixed(dp);
  useEffect(() => { setCharge(''); }, [dueKey, payIn, kind]);

  const pay = (): void => {
    if (!canPay) return;
    // Already covered by earlier tenders (a wallet part): just finish it.
    if (due.lte(0)) { void onPay({ finalize: true }); return; }
    if (kind === 'cashback') {
      const amt = cbMax.toFixed(dp);
      void onPay({ tender: { kind, amount: amt }, finalize: cbMax.gte(due) });
      return;
    }
    if (kind === 'cash' && cross) {
      void onPay({
        tender: { kind: 'cash', amount: due.toFixed(dp) },
        cashExchange: {
          tendered: { currencyCode: payIn, amount: receivedDec.toFixed(payDp) },
          change: { currencyCode: changeCcy, amount: Decimal.max(0, change).toFixed(baseDp) },
        },
        finalize: true,
      });
      return;
    }
    if (kind === 'cash') {
      // The notes handed over — the server works out the change from them.
      void onPay({ tender: { kind: 'cash', amount: (typed ? receivedDec : due).toFixed(dp) }, finalize: true });
      return;
    }
    if (cross && (kind === 'card' || kind === 'wallet' || kind === 'other')) {
      // Charged in the other currency: what was put through, nothing back.
      void onPay({
        tender: { kind, amount: due.toFixed(dp), reference: reference.trim() || null },
        cashExchange: {
          tenderKind: kind,
          tendered: { currencyCode: payIn, amount: chargeDec.toFixed(payDp) },
          change: { currencyCode: currency, amount: new Decimal(0).toFixed(dp) },
        },
        finalize: true,
      });
      return;
    }
    // In the sale's own currency — all of it, or a part with the rest to follow.
    void onPay({
      tender: { kind, amount: chargeDec.toFixed(dp), reference: reference.trim() || null },
      finalize: !partial,
    });
  };

  /** Which currency the money comes in — the same switch for notes, a card, a wallet. */
  const ccySwitch = payOptions.length > 1 ? (
    <div className="grid gap-1 rounded-lg border border-border bg-bg p-1" style={{ gridTemplateColumns: `repeat(${payOptions.length}, 1fr)` }}>
      {payOptions.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => { setPayCcy(c); setReceived(''); }}
          aria-pressed={payIn === c}
          className={`rounded-md py-1.5 text-sm font-bold transition ${payIn === c ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}
        >
          {c}
        </button>
      ))}
    </div>
  ) : null;

  const owing = statement?.summary.filter((s) => Number(s.balance) > 0.0001) ?? [];
  const realCustomer = !!party && party.code !== 'WALK-IN';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-3 p-4">
        <DialogHeader>
          <DialogTitle>{isFinalized ? t('receipt.title') : t('tender.title')}</DialogTitle>
        </DialogHeader>

        {isFinalized ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-success/15 text-success ring-4 ring-success/20">
              <CheckCircle2 className="h-8 w-8" aria-hidden />
            </span>
            <div className="text-center">
              <div className="text-3xl font-bold tabular-nums">{formatMoney(sale.sale.total, currency, locale)}</div>
              {sale.sale.cashIn ? (
                <div className="mt-1 text-sm text-fg-muted">
                  {t('tender.received', { defaultValue: 'Received' })}{' '}
                  <span className="font-semibold text-fg">{formatMoney(sale.sale.cashIn.amount, sale.sale.cashIn.currencyCode, locale)}</span>
                  {sale.sale.cashBack && Number(sale.sale.cashBack.amount) > 0 && (
                    <> · {t('sale.changeDue')} <span className="font-semibold text-success">{formatMoney(sale.sale.cashBack.amount, sale.sale.cashBack.currencyCode, locale)}</span></>
                  )}
                </div>
              ) : Number(sale.sale.changeDue) > 0 && (
                <div className="mt-1 text-sm">
                  <span className="text-fg-muted">{t('sale.changeDue')}</span>{' '}
                  <span className="font-semibold text-success">{formatMoney(sale.sale.changeDue, currency, locale)}</span>
                </div>
              )}
              <p className="mt-2 text-xs text-fg-muted">{t('receipt.saleNumber', { number: sale.sale.saleNumber })}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {sale.sale.invoiceId && (
                // window.open keeps the opener so the new tab inherits the sessionStorage auth.
                <ShButton variant="outline" onClick={() => window.open(appUrl(`/sales/invoices/${sale.sale.invoiceId}/print`), '_blank')}>
                  {t('receipt.openPrint')}
                </ShButton>
              )}
              <Button onClick={onNewSale}>{t('receipt.newSale')}</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1.1fr_1fr]">
              {/* who + how much + how it is paid + the note */}
              <div className="flex flex-col gap-2.5">
                <div className="rounded-xl border border-border bg-bg-subtle/50 px-3 py-2.5 text-center">
                  <div className="truncate text-xs text-fg-muted">
                    {realCustomer ? party!.displayName : t('sale.walkIn', { defaultValue: 'Walk-in customer' })}
                  </div>
                  <div className="font-mono text-2xl font-extrabold tabular-nums text-fg">{formatMoney(due.toString(), currency, locale)}</div>
                  {realCustomer && (
                    <div className="mt-0.5 text-[0.7rem] text-fg-muted">
                      {owing.length > 0
                        ? <>{t('sale.owes', { defaultValue: 'Owes' })} <span className="font-semibold text-warning">{owing.map((s) => formatMoney(s.balance, s.currencyCode, locale)).join(' · ')}</span></>
                        : <span className="text-success">{t('sale.settled', { defaultValue: 'No balance' })}</span>}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {kinds.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      aria-pressed={kind === k}
                      className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-sm font-semibold transition ${kind === k ? 'border-brand bg-brand-subtle text-fg' : 'border-border bg-bg text-fg-muted hover:bg-bg-subtle'}`}
                    >
                      <TenderIcon kind={k} />
                      {t(`tender.${k}`)}
                    </button>
                  ))}
                </div>
                {/* Card / wallet / other money never reaches the drawer. Said here,
                    while it can still be changed: a cash sale rung up as card shows
                    at close as money "over" that nobody can explain. */}
                {kind !== 'cash' && (
                  <div className="flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs text-warning" role="note">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    {t('tender.notInDrawer', {
                      tender: t(`tender.${kind}`),
                      defaultValue: '{{tender}}: this money does NOT go into the drawer. If the customer paid in notes, choose Cash.',
                    })}
                  </div>
                )}
                <label className="flex flex-col gap-1 text-[0.7rem] font-medium text-fg-muted">
                  {t('tender.noteLabel', { defaultValue: 'Note on this sale (printed and kept on the invoice)' })}
                  <textarea
                    rows={2}
                    value={note}
                    onChange={(e) => onNoteChange(e.target.value)}
                    maxLength={2000}
                    placeholder={t('tender.notePlaceholder', { defaultValue: 'e.g. free sample, delivered to the shop…' })}
                    className="w-full resize-none rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm text-fg placeholder:text-fg-subtle focus:border-brand focus:outline-none"
                  />
                </label>
              </div>

              {/* the money itself */}
              <div className="flex flex-col gap-2 rounded-xl border border-border bg-bg-subtle/40 p-3">
                {kind === 'cash' ? (
                  <>
                    {ccySwitch}
                    <label className="text-xs font-medium text-fg-muted" htmlFor="tender-received">
                      {t('tender.receivedIn', { ccy: payIn, defaultValue: 'Received in {{ccy}}' })}
                    </label>
                    <Input
                      id="tender-received"
                      value={received}
                      onChange={(e) => setReceived(normalizeAmountTyping(e.target.value))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pay(); } }}
                      inputMode="decimal"
                      dir="ltr"
                      placeholder={cross ? needed.toFixed(payDp) : due.toFixed(dp)}
                      className="text-end font-mono text-xl"
                      autoFocus
                    />
                    {cross && (
                      <div className="flex justify-between text-[0.7rem] text-fg-muted">
                        <span dir="ltr">{rateText}</span>
                        <span>{t('tender.needed', { defaultValue: 'Due' })}: <strong className="text-fg">{formatMoney(needed.toString(), payIn, locale)}</strong></span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-1.5">
                      <ShButton type="button" variant="outline" size="sm" onClick={() => setReceived(needed.toFixed(payDp))}>
                        {t('tender.exact')}
                      </ShButton>
                      {suggestions.map((v) => (
                        <ShButton key={v} type="button" variant="outline" size="sm" onClick={() => setReceived(String(v))}>
                          {formatMoney(String(v), payIn, locale)}
                        </ShButton>
                      ))}
                    </div>
                    <div className={`mt-auto flex items-center justify-between rounded-lg px-3 py-2 text-sm font-bold ${short ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'}`}>
                      <span>{short ? t('tender.short', { defaultValue: 'Still short' }) : t('sale.changeDue')}</span>
                      <span className="font-mono tabular-nums" dir="ltr">
                        {short
                          ? formatMoney((cross ? needed.minus(receivedDec) : change.abs()).toString(), cross ? payIn : currency, locale)
                          : formatMoney(Decimal.max(0, change).toString(), changeCcy, locale)}
                      </span>
                    </div>
                  </>
                ) : kind === 'cashback' ? (
                  <div className="flex flex-col gap-1 text-sm">
                    <div>{t('tender.cashbackAvailable', { defaultValue: 'Available cashback' })}: <strong>{formatMoney(cbAvail.toFixed(dp), baseCurrency, locale)}</strong></div>
                    <div className="text-fg-muted">{t('tender.cashbackUse', { amount: formatMoney(cbMax.toFixed(dp), currency, locale), defaultValue: 'Pays {{amount}} of this sale; the rest by another method.' })}</div>
                  </div>
                ) : (
                  <>
                    {ccySwitch}
                    <label className="text-xs font-medium text-fg-muted" htmlFor="tender-charge">
                      {t('tender.chargedIn', { ccy: payIn, defaultValue: 'Amount charged in {{ccy}}' })}
                    </label>
                    <Input
                      id="tender-charge"
                      value={charge}
                      onChange={(e) => setCharge(normalizeAmountTyping(e.target.value))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pay(); } }}
                      inputMode="decimal"
                      dir="ltr"
                      placeholder={suggested.toFixed(cross ? payDp : dp)}
                      className="text-end font-mono text-xl"
                      autoFocus
                    />
                    <div className="flex items-center justify-between gap-2 text-[0.7rem] text-fg-muted">
                      {cross ? <span dir="ltr">{rateText}</span> : <span />}
                      {chargeTyped && (
                        <button type="button" className="underline hover:text-fg" onClick={() => setCharge('')}>
                          {cross
                            ? t('tender.atRate', { amount: formatMoney(suggested.toString(), payIn, locale), defaultValue: 'At today’s rate: {{amount}}' })
                            : t('tender.allDue', { amount: formatMoney(due.toString(), currency, locale), defaultValue: 'All of it: {{amount}}' })}
                        </button>
                      )}
                    </div>
                    {chargeProblem && (
                      <div className="rounded-lg bg-danger/10 px-3 py-1.5 text-xs font-semibold text-danger">
                        {chargeProblem === 'short'
                          ? t('tender.exchangeShort', { defaultValue: 'Those notes don’t cover the sale at today’s rate — check the amount and the currency.' })
                          : chargeProblem === 'over'
                            ? t('tender.chargeOver', { defaultValue: 'That is far more than the sale at today’s rate — check the amount.' })
                            : t('tender.chargeOverDue', { defaultValue: 'More than is due — a card or wallet gives no change.' })}
                      </div>
                    )}
                    <label className="text-xs font-medium text-fg-muted" htmlFor="tender-ref">{t('tender.reference', { defaultValue: 'Reference' })}</label>
                    <Input id="tender-ref" value={reference} onChange={(e) => setReference(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pay(); } }} />
                    <div className="mt-auto flex items-center justify-between rounded-lg bg-bg px-3 py-2 text-sm">
                      <span className="text-fg-muted">
                        {partial
                          ? t('tender.restOther', { defaultValue: 'The rest, by another method' })
                          : t('tender.settles', { defaultValue: 'Pays for the sale' })}
                      </span>
                      <span className="font-mono font-bold tabular-nums" dir="ltr">
                        {formatMoney((partial ? due.minus(chargeDec) : due).toString(), currency, locale)}
                      </span>
                    </div>
                  </>
                )}
                {(sale.tenders ?? []).length > 0 && (
                  <ul className="flex flex-col gap-1 border-t border-border pt-2 text-xs">
                    {sale.tenders.map((tn) => (
                      <li key={tn.tenderNo} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1"><TenderIcon kind={tn.kind} />{t(`tender.${tn.kind}`)}</span>
                        <span className="flex items-center gap-1 font-mono tabular-nums">
                          {formatMoney(tn.amount, tn.currencyCode, locale)}
                          <button type="button" onClick={() => void onRemoveTender(tn.tenderNo)} disabled={busy} className="text-fg-muted hover:text-danger" aria-label={t('tender.remove', { defaultValue: 'Remove' })}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* what is being paid for */}
              <div className="hidden min-h-0 flex-col rounded-xl border border-border md:flex">
                <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-xs font-semibold">
                  <span>{t('tender.items', { defaultValue: 'Items in this sale' })}</span>
                  <span className="text-fg-muted">{sale.lines.length}</span>
                </div>
                <ul className="max-h-64 flex-1 overflow-y-auto">
                  {sale.lines.map((l) => (
                    <li key={l.id} className="flex items-start justify-between gap-2 border-b border-border/60 px-3 py-1.5 text-xs last:border-0">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-fg">{l.description}</span>
                        <span className="text-fg-muted" dir="ltr">{trimNum(l.quantity)} × {formatMoney(l.unitPrice, currency, locale)}</span>
                      </span>
                      <span className="flex-none font-mono font-semibold tabular-nums">{formatMoney(l.lineTotal, currency, locale)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">{error}</div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={pay}
                disabled={!canPay}
                loading={busy}
                className="h-11 flex-1 rounded-xl bg-gradient-to-r from-success to-success text-base font-bold"
              >
                <CheckCircle2 className="h-5 w-5" aria-hidden />
                {/* Names a non-cash tender, so the press that books it is a conscious one. */}
                {kind === 'cash' ? t('tender.finalize') : `${t('tender.finalize')} · ${t(`tender.${kind}`)}`}
              </Button>
              {canCredit && realCustomer && due.gt(0) && (
                <ShButton variant="outline" onClick={() => void onPay({ finalize: true, onCredit: true })} disabled={busy} className="h-11 sm:w-auto">
                  {t('tender.credit', { defaultValue: 'Charge to account' })} · {formatMoney(due.toString(), currency, locale)}
                </ShButton>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TenderIcon({ kind }: { kind: PosTenderKind }): JSX.Element {
  switch (kind) {
    case 'cash':
      return <Banknote className="h-4 w-4 text-success" aria-hidden />;
    case 'card':
      return <CreditCard className="h-4 w-4 text-info" aria-hidden />;
    case 'wallet':
      return <Wallet className="h-4 w-4 text-accent" aria-hidden />;
    case 'cashback':
      return <Gift className="h-4 w-4 text-warning" aria-hidden />;
    default:
      return <CreditCard className="h-4 w-4 text-fg-muted" aria-hidden />;
  }
}


// ---------------------------------------------------------------------------
// helpers

/** Compact h:mm / m:ss style elapsed label for the live shift duration. */
function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  const s = totalSec % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

// Decorative tile gradient — shared with the cart thumbnails + detail modal so a
// given item gets the same fallback colour everywhere (see itemImage.ts).
const gradientFor = itemGradient;

// Thin positional wrapper over the shared lib formatMoney so the POS honors the
// backend per-currency decimals (IQD → 0, registered from currenciesStore on
// boot) instead of a hardcoded 2dp. Signature kept positional so every call
// site here is unchanged.
function formatMoney(value: string, currency: string, locale: string): string {
  return formatMoneyLib(value || '0', { locale, currency });
}

/**
 * A bulk-resolved line's GROUP price when it applies in `currency`, else null.
 * A group price quoted in another currency simply doesn't apply — the POS never
 * converts, it re-denominates the whole sale instead.
 */
function groupPriceIn(line: BulkPriceResponse['items'][number], currency: string): string | null {
  if (line.unitPrice == null) return null;
  if (line.source !== 'list-item' && line.source !== 'list-percent') return null;
  if (line.currencyCode && line.currencyCode !== currency) return null;
  return trimMoney(line.unitPrice);
}

/**
 * Total a set of sales PER currency (never across them), the sale's own
 * currency first. Display-only rounding to that currency's decimals.
 */
function sumByCurrency(
  sales: PosSale[],
  firstCurrency: string,
): Array<{ currencyCode: string; total: string }> {
  const by = new Map<string, number>();
  for (const s of sales) {
    const code = s.currencyCode || firstCurrency;
    by.set(code, (by.get(code) ?? 0) + Number(s.total));
  }
  if (by.size === 0) by.set(firstCurrency, 0);
  return [...by.entries()]
    .map(([currencyCode, total]) => ({
      currencyCode,
      total: total.toFixed(getCurrencyDecimals(currencyCode) ?? 2),
    }))
    .sort((a, b) => (a.currencyCode === firstCurrency ? -1
      : b.currencyCode === firstCurrency ? 1
      : a.currencyCode.localeCompare(b.currencyCode)));
}

function extractError(e: unknown): string {
  const r = (e as { response?: { data?: { code?: string; detail?: string } } }).response?.data;
  if (r?.detail) return r.detail;
  if (r?.code) return r.code;
  return String((e as Error).message ?? e);
}
